import store, { persistStore } from '../store'
import { eventBus } from './eventBus'
import RecordRTC from 'recordrtc'
import { saveAs } from 'file-saver'
import i18n from './i18n'
import io from 'socket.io-client'
import * as Signicat from './signicat'
import debounce from 'lodash/debounce'
import VideoZoom from './videoZoom'
import PinchZoomHandler from './pinchZoomHandler'

// Only the threshold for switching to chunked sending; the Licode path is unaffected.
const PRESENTATION_CHUNK_LEN = 45000
// Frame adds 5 bytes plus a ~150 byte header, keeping it under the ~15KB the SFU relays.
const PRESENTATION_BIN_CHUNK = 13000
// Serial on purpose: the SFU relays ~0.45 MB/s of data packets and silently drops the excess.
// Widening this loses frames.
const PRESENTATION_MAX_IN_FLIGHT = 1
import fixWebmDuration from 'fix-webm-duration'
import { buildAgentLastMeetingInfo } from './meetingHelpers'
import { clientContext } from '../diagnostics/statsMapper'
import {
  SMARTINTAKE_SINGLE_FILE_MAX_BYTES,
  SMARTINTAKE_MAX_FILE_BYTES
} from './TranscriptionServices/factories/SmartIntakeAI'
const rnnoiseWasmPath = '/js/rnnoise.wasm'
const rnnoiseWasmSimdPath = '/js/rnnoise_simd.wasm'
const rnnoiseWorkletPath = '/js/workletProcessor.js'
const EventBus = eventBus

// Opus bitrate for transcription audio. Wideband speech is intelligible well below this,
// and it decides how long a recording can run before the upload service rejects it.
const TRANSCRIPTION_AUDIO_BITS_PER_SECOND = 32000
// Upload ceilings per destination, in bytes. null means "no known cap": a local download
// is limited only by the disk, so it must never show a countdown.
// smartintake limit is resolved at recording start from Flagmint smartintake_chunked_upload.
const RECORDING_SIZE_LIMITS = {
  local: null,
  smartintake: SMARTINTAKE_SINGLE_FILE_MAX_BYTES,
  osaw: 200 * 1024 * 1024, // weseedo-api tus-server MAX_UPLOAD_SIZE
  scriptix: null,
  bumicom: null,
  unknown: null
}
// Stop this far below the cap: the projection is an average, and the container plus the
// final chunk still have to fit under the limit.
const RECORDING_SIZE_SAFETY_MARGIN = 1.5 * 1024 * 1024
// Minutes remaining at which the agent is warned.
const RECORDING_SIZE_WARNING_MINUTES = [5, 4, 3, 2, 1]

// Meeting-only libraries, loaded on first use instead of at import time. main.js constructs
// Webrtc at boot, so a static import here would pull them onto every page (incl. login).
// Each loader assigns the same identifier the code already uses, so call sites are unchanged.
let tsvb = null
const ensureEffectsSdk = async () => {
  if (!tsvb) {
    tsvb = (await import('effects-sdk')).tsvb
  }
}

let LivekitAdapter = null
const ensureLivekitAdapter = async () => {
  if (!LivekitAdapter) {
    LivekitAdapter = (await import('./livekitAdapter')).default
  }
}

let RnnoiseWorkletNode = null
let loadRnnoise = null
const ensureRnnoise = async () => {
  if (!RnnoiseWorkletNode) {
    const mod = await import('@sapphi-red/web-noise-suppressor')
    RnnoiseWorkletNode = mod.RnnoiseWorkletNode
    loadRnnoise = mod.loadRnnoise
  }
}

const Webrtc = class Webrtc {
  EventBus = EventBus
  connection = null
  status = 'offline'
  forcedLogout = false
  localStream = null
  localStreamScreen = null
  roomId = null
  pageUnloaded = false
  // Status to re-announce after a socket reconnect (the server resets it to offline).
  statusBeforeDisconnect = null
  authenticatedTimeout = null
  iceDisconnected = false
  soundMeterContext = null
  soundMeterAnalyser = null
  soundMeterSource = null
  soundMeterRaf = null
  // TODO: (MEDIA_SERVER) `p2p` and the `mediaEngine` guards exist only to run Licode
  // and LiveKit side by side. When Licode is removed: drop `p2p`/setP2P, delete every
  // Erizo branch, and the `if (this.mediaEngine === 'livekit')` guards become the only
  // path (un-guard them). Grep this file for MEDIA_SERVER for the spots.
  p2p = true
  mediaEngine = 'licode'
  livekitAdapter = null
  fileChunks = []
  showDeviceSettingsPane = false
  uploadBumicomRecording = false
  isScriptixRecording = false
  recordingNotifiedIdentities = new Set()
  knownRemoteIdentities = new Set()
  recordingNoticeForgetTimers = {}
  // Tracks the in-flight save of a stopped recording so the meeting-end flow can wait for it.
  recordingSavePromise = null
  recordingSaveInProgress = false
  axiosInstance = null
  noiseSuppressionContext = null
  effectsSDK = null
  replaceStreamWaitForPCInterval = null
  isVideoEffectRunning = false
  backgroundImage = null
  muteStatusMap = {} // this is use to memoize and prevent sending irrelavent data to websocket.
  productModeName = 'WeSeeDo'
  // True from hangup/leave until the next join. Blocks stale leave / room-disconnected
  // from a previous Licode room ending a later session (esp. Direct re-invite).
  isEndingCall = false
  // True when the call ended because the room never connected, so the teardown can tell
  // a failed start apart from a call that actually took place. Cleared on the next join.
  roomConnectFailed = false
  // True only for the hangup that starts Signicat identification. MeetingRoom.onEndCall
  // reads this so that handoff does not show the feedback form, then clears it. A later
  // real end of the same meeting still asks for feedback.
  skipFeedbackForIdentification = false
  localStreamStopInterval = null
  // Every camera/microphone track this client has opened. Leaving the room stops the tracks
  // that were published, but noise suppression and resolution changes swap tracks out of
  // localStream.stream first, and an unpublished track is one nothing else still holds.
  // Without this the device behind it stays open after the call ends.
  acquiredTracks = new Set()

  // Video zoom properties
  videoZoom = null
  pinchZoomHandler = null
  isZoomEnabled = false
  currentZoomLevel = 1.0
  maxZoomLevel = 3.0
  minZoomLevel = 1.0
  zoomTimeout = null
  // True once zoom has been initialised this call; gates camera-switch zoom handling
  // (a real switch tears zoom down, so we can't rely on `videoZoom` being set). Reset on endCall.
  zoomFeatureActive = false
  streamRetryTimeouts = new Set()

  /** @type {boolean} Flagmint smartintake_chunked_upload — false keeps the 32 MB single-file path. */
  smartIntakeChunkedUpload = false

  constructor () {
    // effects-sdk is no longer initialized here — it loads on first effect use, so the
    // login page doesn't pay for it. Entry points below await initializeEffectsSDK().
    window.addEventListener('wsd-licode-ice-disconnected', (connectionStateEvent) => {
      if (connectionStateEvent.detail && connectionStateEvent.detail.state === 'disconnected') {
        this.iceDisconnected = true
        // this.EventBus.$emit('onRemoteAgentDisconnect', userId)
      } else if (connectionStateEvent.detail && connectionStateEvent.detail.state === 'connected') {
        this.iceDisconnected = false
        // this.EventBus.$emit('onRemoteAgentDisconnect', userId)
      }
    })

    window.addEventListener('beforeunload', (event) => {
      this.pageUnloaded = true
      this.stampAgentMeetingInterruptedOnUnload()
      this.disconnect(false)
    })
    window.addEventListener('pagehide', (event) => {
      this.pageUnloaded = true
      this.stampAgentMeetingInterruptedOnUnload()
      this.disconnect(false)
    })

    setInterval(async () => {
      if (this.localStream !== null && this.activeVideoDevice !== null) {
        let deviceFound = false
        let devices = await navigator.mediaDevices.enumerateDevices()
        this.videoDevices = []
        this.audioDevices = []
        this.outputDevices = []
        devices.forEach((device) => {
          if (device.kind === 'videoinput') {
            this.videoDevices.push({
              'label': device.label,
              'deviceId': device.deviceId
            })
          } else if (device.kind === 'audioinput') {
            this.audioDevices.push({
              'label': device.label,
              'deviceId': device.deviceId
            })
          } else if (device.kind === 'audiooutput') {
            this.outputDevices.push({
              'label': device.label,
              'deviceId': device.deviceId
            })
          }
          if (device.deviceId === this.activeVideoDevice) {
            deviceFound = true
          }
        })
        let deviceFeedback = {
          'video_input': this.videoDevices,
          'audio_input': this.audioDevices,
          'audio_output': this.outputDevices
        }
        if (!deviceFound) {
          console.log('The video device appears to be unplugged / no longer available, ending call.')
          this.stopLocalStreams()
          this.EventBus.$emit('onStreamNoLongerAvailable', {
            'devices': deviceFeedback
          })
          window.LoggerAPI.addLog('client', new Date(), 'log', 'STREAM_NO_LONGER_AVAILABLE', 'The video/audio stream is no longer available, for example because the webcam was unplugged.')
        }
      }
    }, 3000)
    this._debouncedNotify = debounce((finalState) => {
      this._notifyMuteStatus(finalState)
    }, 500)

    this.productModeName = store.getters.getProductMode === 'WSD' ? 'WeSeeDo' : '123meekijken'
  }

  async initializeEffectsSDK () {
    if (this.effectsSDK) {
      return
    }
    await ensureEffectsSdk()
    // A concurrent caller may have initialized it while we awaited the import.
    if (this.effectsSDK) {
      return
    }
    // eslint-disable-next-line new-cap
    this.effectsSDK = new tsvb(process.env.VUE_APP_EFFECTS_SDK_KEY)

    // Monitor SDK initialization progress
    this.effectsSDK.onError((errorObj) => {
      console.log('EffectsSDK error/info:', errorObj)

      switch (errorObj.code) {
        case 3020: // SESSION_INITIALIZATION_SUCCESS
          console.log('✅ Session token received')
          break
        case 3021: // EFFECT_INITIALIZATION_SUCCESS
          console.log('✅ Effects initialized (WASM loaded)')
          break
        case 1021: // SESSION_INITIALIZATION_ISSUE
          console.error('❌ Network issue or incorrect WASM files')
          break
        case 1020: // SESSION_INITIALIZATION_FAILED
          console.error('❌ Session server denied initialization')
          break
      }
    })

    this.effectsSDK.config({
      preset: 'balanced',
      provider: 'auto',
      proxy: true,
      wasmPaths: {
        'ort-wasm.wasm': 'https://effectssdk.ai/sdk/web/3.9.0/ort-wasm.wasm',
        'ort-wasm-simd.wasm': 'https://effectssdk.ai/sdk/web/3.9.0/ort-wasm-simd.wasm'
      }
    })
    this.effectsSDK.cache()
    this.effectsSDK.preload()
  }
  async resetEffectsSDK () {
    if (!this.effectsSDK) {
      return
    }
    // Stop the pipeline first: a discarded instance keeps rendering from the camera it was
    // given, and garbage collection never stops a MediaStreamTrack.
    try {
      this.effectsSDK.stop()
    } catch (e) { /* not running */ }
    await ensureEffectsSdk()
    delete this.effectsSDK
    // eslint-disable-next-line new-cap
    this.effectsSDK = new tsvb(process.env.VUE_APP_EFFECTS_SDK_KEY)
    // Monitor SDK initialization progress
    this.effectsSDK.onError((errorObj) => {
      console.log('EffectsSDK error/info:', errorObj)

      switch (errorObj.code) {
        case 3020: // SESSION_INITIALIZATION_SUCCESS
          console.log('✅ Session token received')
          break
        case 3021: // EFFECT_INITIALIZATION_SUCCESS
          console.log('✅ Effects initialized (WASM loaded)')
          break
        case 1021: // SESSION_INITIALIZATION_ISSUE
          console.error('❌ Network issue or incorrect WASM files')
          break
        case 1020: // SESSION_INITIALIZATION_FAILED
          console.error('❌ Session server denied initialization')
          break
      }
    })

    this.effectsSDK.config({
      preset: 'balanced',
      provider: 'auto',
      proxy: true,
      wasmPaths: {
        'ort-wasm.wasm': 'https://effectssdk.ai/sdk/web/3.9.0/ort-wasm.wasm',
        'ort-wasm-simd.wasm': 'https://effectssdk.ai/sdk/web/3.9.0/ort-wasm-simd.wasm'
      }
    })
    this.effectsSDK.cache()
    this.effectsSDK.preload()
  }
  /**
   * Persist interrupted meeting state before a hard reload so the agent lands on
   * the agent end screen (interrupted variant) instead of the wait screen after remount.
   * Only applies when an agent had an in-progress Direct/123meekijken call.
   */
  stampAgentMeetingInterruptedOnUnload () {
    const isAgent = store.getters.getUser !== false
    const meetingStarted = store.getters.getMeetingStarted === true
    const meeting = store.getters.getMeeting

    if (!isAgent || !store.getters.isDirectOrMeek || !meetingStarted || meeting === false) {
      return
    }

    store.commit('setLastMeetingInfo', buildAgentLastMeetingInfo(meeting, store.getters.getUser))
    store.commit('setMeetingEnded', true)
    store.commit('setMeetingEndedAs', 'agent')
    store.commit('setMeetingInterrupted', true)
    store.commit('setMeetingStarted', false)
    store.commit('setMeeting', {})
    store.commit('setDeviceList', {})
    // The page is unloading; the store's own persistence flush would come too late.
    persistStore()
  }

  disconnect (emitDisconnect) {
    console.log('TOTALLY DISCONNECTED')

    if (this.connection !== null) {
      this.connection.close()
      this.connection = null
    }
    if ((typeof emitDisconnect === 'undefined' || emitDisconnect !== false) && this.forcedLogout === false && this.pageUnloaded === false) {
      console.log('EMIT MY DISCONNECT')
      this.EventBus.$emit('onDisconnected')
    }
    this.disconnectActiveRoom()
  }

  /**
   * Intentionally disconnect the current Licode room.
   * Nulls `this.room` first so a late room-disconnected cannot match the active call.
   */
  disconnectActiveRoom () {
    if (!this.room) {
      return
    }
    console.log('DISCONNECT FROM ROOM (expected)')
    const room = this.room
    this.room = null
    try {
      room.disconnect()
    } catch (e) {
      console.warn('Error while disconnecting Licode room', e)
    }
  }

  hasActiveCall () {
    return this.room !== null || this.roomId !== null
  }

  /**
   * Mark this client as in an active call (or joining one). Clears the post-hangup guard.
   */
  activateCall (roomId) {
    this.isEndingCall = false
    this.roomConnectFailed = false
    this.roomId = roomId
    window.roomId = roomId
  }

  clearLocalStreamRefs () {
    this.activeVideoDevice = null
    this.activeAudioInput = null
    this.activeAudioOutput = null
    this.localStream = null
  }

  clearLocalStreamRefsIfCurrent (stream) {
    if (this.localStream === stream) {
      this.clearLocalStreamRefs()
    }
  }

  clearLocalStreamStopInterval () {
    if (this.localStreamStopInterval) {
      clearInterval(this.localStreamStopInterval)
      this.localStreamStopInterval = null
    }
    if (this.livekitAdapter) {
      console.log('DISCONNECT FROM LIVEKIT')
      this.livekitAdapter.disconnect()
      this.livekitAdapter = null
    }
  }

  resetIsInitialConnection () {
    if (this.connection) {
      this.connection.auth.isInitialConnection = true
    }
  }

  forceLogout (emitForcedLogout) {
    this.forcedLogout = true
    if (this.connection !== null) {
      this.connection.close()
      this.connection = null
    }
    if (typeof emitForcedLogout === 'undefined' || emitForcedLogout !== false) {
      this.EventBus.$emit('onForcedLogout')
    }
  }

  dataURItoFile (dataURI, fileName) {
    const blob = this.dataURItoBlob(dataURI)
    return new File([blob], fileName)
  }

  dataURItoBlob (dataURI) {
    // convert base64 to raw binary data held in a string
    // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
    let byteString = atob(dataURI.split(',')[1])

    // separate out the mime component
    let mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]

    // write the bytes of the string to an ArrayBuffer
    let ab = new ArrayBuffer(byteString.length)
    let ia = new Uint8Array(ab)
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i)
    }
    return new Blob([ab], { type: mimeString })
  }

  convertDataURIToBinary (dataURI) {
    let BASE64_MARKER = ';base64,'
    let base64Index = dataURI.indexOf(BASE64_MARKER) + BASE64_MARKER.length
    let base64 = dataURI.substring(base64Index)
    let raw = window.atob(base64)
    let rawLength = raw.length
    let array = new Uint8Array(new ArrayBuffer(rawLength))

    for (let i = 0; i < rawLength; i++) {
      array[i] = raw.charCodeAt(i)
    }
    return array
  }
  cancelInvite (agentId, roomId, accountId) {
    window.LoggerAPI.addLog('client', new Date(), 'log', 'CANCEL_INVITE', 'Participant cancels invitation or call forward to agent ' + agentId + '.')
    this.connection.emit('cancel invite', agentId, roomId, accountId)
  };

  connect () {
    this.forcedLogout = false

    if (!this.connection) {
      let token
      if (store.getters.getAccessToken !== null) {
        token = store.getters.getAccessToken
      } else if (store.getters.getParticipantAccessToken !== null) {
        token = store.getters.getParticipantAccessToken
      } else if (store.getters.getSiteAccessToken !== null) {
        token = store.getters.getSiteAccessToken
      }

      // Timings are deliberately tight: a user whose network drops should notice and
      // reconnect rather than sit in a silent retry loop. randomizationFactor jitters
      // the retries so a recovering API node is not hit by every client in one
      // synchronised wave; it does not lengthen the give-up window.
      // eslint-disable-next-line no-undef
      this.connection = io(process.env.VUE_APP_WEBSOCKET_HOST, {
        reconnection: true,
        reconnectionDelay: 1500,
        reconnectionDelayMax: 1500,
        reconnectionAttempts: 9,
        randomizationFactor: 0.5,
        timeout: 2000,
        transports: ['websocket'],
        auth: {
          token: token,
          isInitialConnection: true
        }
      })

      this.connection.on('connect', () => {
        const isReconnect = this.connection.auth.isInitialConnection === false
        this.connection.auth.isInitialConnection = false
        this.EventBus.$emit('onConnected')

        // An agent socket the server did not register never gets 'authenticated' and
        // is silently dead; give up like a failed reconnect so the agent is told.
        // Longer than the API's redis adapter requestsTimeout (10s).
        const user = store.getters.getUser
        if (isReconnect && user && ['agent', 'assistent_video'].includes(user.role)) {
          clearTimeout(this.authenticatedTimeout)
          this.authenticatedTimeout = setTimeout(() => {
            window.LoggerAPI.addLog('client', new Date(), 'log', 'CLIENT_NOT_AUTHENTICATED', 'Reconnected but not registered by the server.')
            this.EventBus.$emit('onReconnectFailed')
            this.disconnect()
          }, 12000)
        }
      })

      this.connection.on('PARTICIPANT_ALREADY_ACTIVE', () => {
        this.EventBus.$emit('onVisitorAlreadyActive')
      })

      this.connection.on('agent_status_changed', (agentId, accountId) => {
        this.EventBus.$emit('onAgentStatusChanged', {
          'agentId': agentId,
          'accountId': accountId
        })
      })

      this.connection.on('visitor online', (username, action, agentId, participantBSN, participantName) => {
        this.EventBus.$emit('onVisitorOnline', username, action, agentId, participantBSN, participantName)
      })

      this.connection.on('visitor offline', (username) => {
        this.EventBus.$emit('onVisitorOffline', username)
      })

      this.connection.on('remote_agent_disconnect', (userId) => {
        // this.EventBus.$emit('onRemoteAgentDisconnect', userId)
      })

      this.connection.on('remote_participant_disconnect', (participantId) => {
        delete this.muteStatusMap[participantId]
        // this.EventBus.$emit('onRemoteParticipantDisconnect', participantId)
      })

      this.connection.on('remote_agent_reconnect', (userId) => {
        // this.EventBus.$emit('onRemoteAgentReconnect', userId)
        // only works if it's direct & two-way, re-establishes your stream to the reconnected user
        this.reconnectStreamTwoWayDirect()
      })

      this.connection.on('remote_participant_reconnect', (participantId) => {
        // this.EventBus.$emit('onRemoteParticipantReconnect', participantId)
        // only works if it's direct & two-way, re-establishes your stream to the reconnected user
        this.reconnectStreamTwoWayDirect()
      })

      // when a visitor requested the agent to join a room
      this.connection.on('request_join', (room, account, requestedBy, action, product, isTwoWay) => {
        window.LoggerAPI.addLog('client', new Date(), 'log', 'REQUEST_JOIN', 'Participant wants to join the room.')
        console.log('Received request to join room ' + room + ' for account ' + account)

        // dispatch offer
        this.EventBus.$emit('onRequestJoin', {
          'room_id': room,
          'account': account,
          'requestedBy': requestedBy,
          'action': action,
          'product': product,
          'stream_camera_two_way': isTwoWay
        })
      })

      // Join the room
      this.connection.on('join', async (roomId, roomPassword, isFirstParticipant, realName, participantId, product, isTwoWay, meetingId, engine) => {
        console.log('RECEIVED JOIN', roomId, roomPassword, isFirstParticipant, realName, participantId, product, isTwoWay, meetingId, engine)
        // Pick the media engine for this meeting (LiveKit when the API flags it, Licode otherwise).
        this.mediaEngine = engine === 'livekit' ? 'livekit' : 'licode'
        this.setStatus('busy', true, false, false)

        window.LoggerAPI.addLog('client', new Date(), 'log', 'RECEIVED_JOIN', 'Participant received request to join the room.')
        window.LoggerAPI.addLog('client', new Date(), 'log', 'BROWSER_INFO', JSON.stringify(window.DetectRTC, undefined, 2))

        this.EventBus.$emit('onJoin', {
          'meeting_id': meetingId,
          'room_id': roomId,
          'room_password': roomPassword,
          'is_first_participant': isFirstParticipant,
          'real_name': realName,
          'product': product,
          'stream_camera_two_way': isTwoWay
        })
        this.openOrJoin(roomId, roomPassword, product, isTwoWay)
      })

      // Leave the room
      this.connection.on('leave', (isForcedDisconnect, meta) => {
        window.LoggerAPI.addLog('client', new Date(), 'log', 'CALL_LEFT', 'Participant wants to leave the call.')
        const endedForIdentification = !!(meta && meta.reason === 'identification')
        // Stale leave from a previous hangup must not tear down a new Direct invite/wait.
        if (this.isEndingCall || !this.hasActiveCall()) {
          console.log('Ignoring leave: no active call (already ending or not in a room)')
          // The local hangup already ran. Ask the room to close a feedback form that
          // is already open. Do not set skipFeedbackForIdentification here: this call
          // is already inactive, and a flag left behind would skip feedback on the
          // next ordinary end. The handler sets it only while a decision is in progress.
          if (endedForIdentification) {
            this.EventBus.$emit('onSuppressFeedbackForIdentification')
          }
          return
        }
        if (endedForIdentification) {
          this.skipFeedbackForIdentification = true
          this.EventBus.$emit('onSuppressFeedbackForIdentification')
        }
        this.endCall(isForcedDisconnect)
      })

      // Get promoted
      this.connection.on('promoted', () => {
        window.LoggerAPI.addLog('client', new Date(), 'log', 'AGENT_PROMOTED', 'Agent promoted')
        this.EventBus.$emit('onPromoted')
      })

      this.connection.on('session_unavailable', (errorData) => {
        this.EventBus.$emit('onRoomUnavailable', {
          'data': errorData
        })
      })

      this.connection.on('start-meeting-counter', () => {
        this.EventBus.$emit('start-meeting-counter', { trigger: 'remote' })
      })

      // Define Socket.IO connection related callbacks
      this.connection.on('disconnect', (reason) => {
        console.log('Disconnected!', reason)

        clearTimeout(this.authenticatedTimeout)
        if (this.status !== 'offline') {
          this.statusBeforeDisconnect = this.status
        }

        if (!reason.includes('disconnect')) {
          this.EventBus.$emit('onLostConnection')
        }

        window.LoggerAPI.addLog('client', new Date(), 'log', 'CLIENT_DISCONNECTED', 'Disconnected from the server.')

        if (status !== 'offline' && this.pageUnloaded === false) {
          this.setStatus('offline')
        }
      })

      this.connection.io.on('reconnect', () => {
        this.EventBus.$emit('onReconnected')
        if (store.getters.getProduct === 'direct') {
          this.startLocalStreams(this.lastProduct, this.lastStreamBothWays, undefined, this.activeVideoDevice, null, null, undefined, undefined, undefined)
        }
      })

      this.connection.io.on('reconnect_attempt', (attempt) => {
        this.EventBus.$emit('onReconnectAttempt', {
          attempt: attempt
        })
      })

      this.connection.io.on('reconnect_error', (error) => {
        console.log('Reconnect attempt failed', error)
        // this.EventBus.$emit('onReconnectAttemptFailed')
      })

      this.connection.io.on('reconnect_failed', () => {
        console.log('RECONNECT FAILED')
        this.EventBus.$emit('onReconnectFailed')
        this.disconnect()
      })

      this.connection.on('force-logout', () => {
        window.LoggerAPI.addLog('client', new Date(), 'log', 'CLIENT_DISCONNECTED', 'Disconnected from the server.')

        if (status !== 'offline') {
          this.setStatus('offline')
        }
        console.log('Forced logout! Disconnecting socket')
        this.forceLogout()
      })

      this.connection.on('connect_timeout', () => {
        console.log('SocketIO connection timed out')
      })

      this.connection.on('connect_error', (error) => {
        console.log('SocketIO connection failed:', error)
        // this.EventBus.$emit('onConnectError')
      })

      this.connection.on('error', (error) => {
        console.log('SocketIO connection error:', error)
        // this.EventBus.$emit('onConnectError')
      })

      this.connection.on('setStatus', (status) => {
        console.log('Received new status ' + status)
        this.setStatus(status, false)
      })

      this.connection.on('converted presentation', (pdf, filename, filesize, signature) => {
        let fileObject = this.dataURItoFile(pdf, 'presentation.pdf')

        this.EventBus.$emit('onPresentationConverted', fileObject, signature)
      })

      this.connection.on('chat_attachment_and_converted_presentation', (file, filename, filesize, signature) => {
        let fileObject = this.dataURItoFile(file, filename)

        this.EventBus.$emit('onPresentationByApi', fileObject, signature)
        this.EventBus.$emit('onShareFileByApi', fileObject)
      })

      this.connection.on('chat_attachment', (file, filename) => {
        let fileObject = this.dataURItoFile(file, filename)
        this.EventBus.$emit('onShareFileByApi', fileObject)
      })

      this.connection.on('conversion failed', () => {
        this.EventBus.$emit('onPresentationConversionFailed')
      })

      this.connection.on('cancel_offer', (roomId) => {
        // TODO: Make sure logger api works with this again
        window.LoggerAPI.addLog('client', new Date(), 'log', 'CANCEL_OFFER', 'The call offer was cancelled.')
        this.EventBus.$emit('onCancelOffer', {
          'roomId': roomId
        })
      })

      this.connection.on(`${store.getters.getUser.id}-scriptix transcription call received`, (data) => {
        this.EventBus.$emit('onScriptixTranscriptionReceived', {
          'data': data
        })
      })

      this.connection.on(`${store.getters.getUser.id}-bumicom transcription ready`, (data) => {
        // Emit event for UI to show toast/dialog with download link
        this.EventBus.$emit('onBumicomTranscriptionReady', {
          'id': data.id,
          'downloadUrl': data.downloadUrl,
          'expiresAt': data.expiresAt
        })
      })

      this.connection.on(`${store.getters.getUser.id}-bumicom transcription call received`, (data) => {
        console.log('Transcription received:', data)
      })

      this.connection.on(`${store.getters.getUser.id}-smartintake analysis ready`, (data) => {
        // Extract download URL from the document object in the payload
        const downloadUrl = data?.document?.downloadUrl
        if (downloadUrl) {
          this.EventBus.$emit('onSmartIntakeAnalysisReady', {
            'downloadUrl': downloadUrl,
            'expiresAt': data?.document?.expiresAt,
            'status': data?.status,
            'timestamp': data?.timestamp,
            'publicToken': data?.publicToken
          })
        } else {
          console.warn('SmartIntake analysis ready event received but no download URL found', data)
        }
      })

      this.connection.on(`zynyo-signing-link-ready`, (data) => {
        this.EventBus.$emit('onZynyoSigningLinkReady', data)
      })
      this.connection.on(`zynyo-participant-request`, (data) => {
        this.EventBus.$emit('onZynyoEmailRequest', data)
      })
      this.connection.on('zynyo-participant-request-response', (data) => {
        this.EventBus.$emit('onZynyoEmailResponse', data)
      })
      this.connection.on('onMuteUserAudio', (data) => {
        this.EventBus.$emit('onUserAudioMuted', data)
      })

      this.connection.on('muteActionComplete', (data) => {
        this.EventBus.$emit('muteActionComplete', data)
      })

      // Notify new users the current mute state of all participants
      this.connection.on('batchMuteActionComplete', (data) => {
        this.EventBus.$emit('batchMuteActionComplete', data)
      })

      this.connection.on('participantWaitingRoomState', (data) => {
        this.EventBus.$emit('participantWaitingRoomState', data)
      })

      this.connection.on('call held', (roomId, agentName) => {
        window.LoggerAPI.addLog('client', new Date(), 'log', 'CALL_HELD', 'The call was held.')
        this.EventBus.$emit('onCallHeld', {
          'roomId': roomId,
          'agentFullName': agentName
        })
      })

      this.connection.on('declined', (roomId, agentName) => {
        window.LoggerAPI.addLog('client', new Date(), 'log', 'CALL_DECLINED', 'The call was declined.')
        this.EventBus.$emit('onCallDeclined', {
          'roomId': roomId,
          'agentFullName': agentName
        })
      })

      this.connection.on('call colleague failed', () => {
        this.EventBus.$emit('onCallColleagueFailed')
      })

      this.connection.on('license_error', (permission) => {
        console.log(permission) // The permission that was denied by the API, for example "chat", "forward", it means that the user is not licensed to do what was requested
        // TODO: This license thing is not checked too well, the newest events probably are never checked at all, not important for now
        // The current Personal shows a dialog that tells you that you are not licensed to do that
        // For almost any event this event would have been caught already by the UI, but it is better to server-sided check for license requirements as well consistently
      })

      // Leave the room because of a call forward
      this.connection.on('forward', () => {
        // TODO: Make sure logger api works with this again
        window.LoggerAPI.addLog('client', new Date(), 'log', 'CALL_FORWARDED', 'Participant leaves because the call was forwarded.')

        // The idea here is that the call will stop and then restart with the new agent as the host
        // This will very likely break stuff in the new app, let's fix that together
        this.endCall()
      })

      this.connection.on('meeting_updated', (meeting) => {
        if (typeof meeting === 'undefined' || meeting === null) {
          meeting = null
        }
        try {
          meeting = JSON.parse(meeting)
        } catch (e) {
          // do nothing
        }

        this.EventBus.$emit('onMeetingUpdated', meeting)
      })

      this.connection.on('authenticated', () => {
        console.warn('User logged in succesfully')
        clearTimeout(this.authenticatedTimeout)

        if (store.getters.getProduct === 'personal') {
          this.setStatus(store.getters.getStatus === 'online' || store.getters.isUINavigationEnabled === false ? 'online' : 'offline', true, store.getters.isUINavigationEnabled, false)
        } else {
          this.setStatus('online', true, false)
        }

        // A link-agent still in the waiting room must be findable for the participant again.
        if (this.statusBeforeDisconnect === 'online_for_single_use_meeting' && !this.hasActiveCall()) {
          this.setStatus('online_for_single_use_meeting', true, false, false)
        }
        this.statusBeforeDisconnect = null

        this.EventBus.$emit('onAuthenticated')
      })

      this.connection.on('call colleague join', (roomId) => {
        this.EventBus.$emit('onCallColleagueAccept', {
          'roomId': roomId
        })
        this.join(roomId, 'personal', true, true)
      })
    }
  };

  sendOrientationChanged (orientation) {
    let sender = this.getRealNameFromStream(this.localStream)
    this.localStream.sendData({
      'sender': sender,
      'type': 'orientation_changed',
      'orientation': orientation
    })
  };

  showPopupDigid () {
    console.log('received emit show popup')
    if (this.localStream) {
      let sender = this.getRealNameFromStream(this.localStream)
      this.localStream.sendData({
        'sender': sender,
        'type': 'popup_digid'
      })
      this.EventBus.$emit('onShowPopupDigid', {
        'sender': sender,
        'type': 'popup_digid'
      })
    }
  }

  sendChatMessage (message) {
    message = message.replace(/^\s+|\s+$/g, '')
    if (!message.length) return

    if (this.localStream) {
      let now = new Date()
      let sender = this.getRealNameFromStream(this.localStream)
      this.localStream.sendData({
        'sender': sender,
        'type': 'chat',
        'content': message,
        'datetime': now
      })
      this.EventBus.$emit('onChatMessage', {
        'type': 'chat',
        'sender': sender,
        'message': message,
        'direction': 'out',
        'datetime': now
      })
    }
  }

  sendRepublishSignal () {
    if (this.localStream) {
      let sender = this.getRealNameFromStream(this.localStream)
      this.localStream.sendData({
        'sender': sender,
        'type': 'republish_stream'
      })
    }
  }

  republishLocalStream () {
    console.log('REPUBLISH LOCAL STREAM')
    this.startLocalStreams(this.lastProduct, this.lastStreamBothWays, undefined, this.activeVideoDevice, this.activeAudioInput, this.activeAudioOutput, undefined, undefined, undefined, true)
  }

  sendRaiseOrLowerHand (raiseHand) {
    if (this.localStream) {
      let senderName = this.getRealNameFromStream(this.localStream)
      let senderId = this.getUserIdFromStream(this.localStream)
      this.localStream.sendData({
        'sender': senderName,
        'senderId': senderId,
        'type': 'raiseOrLowerHand',
        'raiseHand': raiseHand
      })
    }
  }

  sendParticipantLocation (latitude, longitude) {
    if (!this.localStream || typeof latitude !== 'number' || typeof longitude !== 'number') {
      return
    }
    const sender = this.getRealNameFromStream(this.localStream)
    const senderId = this.getUserIdFromStream(this.localStream)
    this.localStream.sendData({
      sender,
      senderId,
      type: 'participant_location',
      latitude,
      longitude,
      datetime: new Date().toISOString()
    })
  }

  sendParticipantLocationRequest () {
    if (!this.localStream) {
      return
    }
    const sender = this.getRealNameFromStream(this.localStream)
    const senderId = this.getUserIdFromStream(this.localStream)
    this.localStream.sendData({
      sender,
      senderId,
      type: 'participant_location_request',
      datetime: new Date().toISOString()
    })
  }

  sendParticipantLocationRequestResponse (accepted) {
    if (!this.localStream) {
      return
    }
    const sender = this.getRealNameFromStream(this.localStream)
    const senderId = this.getUserIdFromStream(this.localStream)
    this.localStream.sendData({
      sender,
      senderId,
      type: 'participant_location_request_response',
      accepted: accepted === true,
      datetime: new Date().toISOString()
    })
  }

  sendParticipantLocationStop () {
    if (!this.localStream) {
      return
    }
    const sender = this.getRealNameFromStream(this.localStream)
    const senderId = this.getUserIdFromStream(this.localStream)
    this.localStream.sendData({
      sender,
      senderId,
      type: 'participant_location_stop',
      datetime: new Date().toISOString()
    })
  }

  sendParticipantLocationStopRequest () {
    if (!this.localStream) {
      return
    }
    const sender = this.getRealNameFromStream(this.localStream)
    const senderId = this.getUserIdFromStream(this.localStream)
    this.localStream.sendData({
      sender,
      senderId,
      type: 'participant_location_stop_request',
      datetime: new Date().toISOString()
    })
  }

  setStatus (newStatus, emitStatus, fireStatusChangedEvent, emitStore) {
    this.status = newStatus
    console.log('Sending status: ' + this.status, emitStatus, fireStatusChangedEvent, emitStore)
    if (this.connection && (typeof emitStatus === 'undefined' || emitStatus === true)) {
      console.log('Set status, emit: ' + this.status)
      this.connection.emit('set status', this.status)
    } else {
      console.log('Set status, dont emit: ' + this.status)
    }

    if (typeof fireStatusChangedEvent === 'undefined' || fireStatusChangedEvent === true) {
      this.EventBus.$emit('onStatusChanged', {
        'status': this.status
      })
    }
    if (typeof emitStore === 'undefined' || emitStore === true) {
      store.commit('setStatus', this.status)
    }
  }

  reconnectStreamTwoWayDirect () {
    if (store.getters.getProduct === 'direct') {
      if (this.lastStreamBothWays) {
        this.startLocalStreams(this.lastProduct, this.lastStreamBothWays, undefined, this.activeVideoDevice, null, null, undefined, undefined, undefined)
      }
    }
  }

  inviteAgent (agentId, roomId, accountId) {
    window.LoggerAPI.addLog('client', new Date(), 'log', 'INVITE_AGENT', 'Participant invited agent ' + agentId + ' to join.')

    this.connection.emit('invite agent', agentId, roomId, accountId)
  }

  forwardToAgent (agentId, roomId, accountId) {
    window.LoggerAPI.addLog('client', new Date(), 'log', 'FORWARD_CALL', 'Participant wants to forward the call to agent ' + agentId + '.')
    this.connection.emit('forward to agent', agentId, roomId, accountId)
  }

  callColleague (agentId) {
    window.LoggerAPI.addLog('client', new Date(), 'log', 'CALL_COLLEAGUE', 'Call colleague ' + agentId + '.')

    this.connection.emit('call colleague', agentId)
  }

  callColleagueAccept (roomId) {
    window.LoggerAPI.addLog('client', new Date(), 'log', 'CALL_COLLEAGUE_ACCEPTED', 'Call accepted by colleague.')

    this.connection.emit('call colleague accept', roomId)
  }

  // Annotations drawn during the transfer had no canvas to land on; ask for the state once
  // this side has rendered, rather than waiting for the presenter's next stroke.
  requestPresentationSync () {
    if (!this.localStream) {
      return
    }
    this.localStream.sendData({
      'sender': this.getRealNameFromStream(this.localStream),
      'type': 'request_presentation_sync'
    })
  }

  syncPresentation (scale, width, offsetX, offsetY, drawings) {
    this.localStream.sendData({
      'sender': typeof this.localStream.getAttributes() !== 'undefined' && typeof this.localStream.getAttributes().real_name !== 'undefined' ? this.localStream.getAttributes().real_name : 'Agent',
      'type': 'sync_presentation',
      'scale': scale,
      'offsetX': offsetX,
      'offsetY': offsetY,
      'drawings': drawings,
      'width': width
    })
  }

  endCall (isForcedDisconnect, hangupReason) {
    if (this.isEndingCall) {
      console.log('endCall ignored: hangup already in progress or completed for this call')
      return
    }
    // Ignore remote teardown while not in a room (e.g. Direct invite wait).
    if (!this.hasActiveCall()) {
      console.log('endCall ignored: no active call')
      return
    }

    // Signicat starts by ending the call. Mark that before onEndCall so the feedback
    // form is skipped for this handoff only.
    if (hangupReason === 'identification') {
      this.skipFeedbackForIdentification = true
    }

    this.isEndingCall = true
    window.LoggerAPI.addLog('client', new Date(), 'log', 'HANGUP', 'Participant wants to hangup.')
    this.stopLocalStreams()
    this.stopScreenSharing()
    // LiveKit room must be left explicitly on hangup, else it stays connected at the SFU.
    if (this.mediaEngine === 'livekit' && this.livekitAdapter) {
      this.livekitAdapter.disconnect()
      this.livekitAdapter = null
    }
    // Tear down zoom so state doesn't leak into the next call (webrtc is a singleton).
    this.destroyVideoZoom()
    this.zoomFeatureActive = false
    this.roomId = null
    window.roomId = null
    // eslint-disable-next-line no-unused-expressions
    if (hangupReason === 'identification') {
      this.connection?.emit('hangup', { reason: 'identification' })
    } else {
      this.connection?.emit('hangup')
    }
    this.isAudioMuted = false
    this.isVideoMuted = false
    this.resetEffectsSDK()
    this.EventBus.$emit('onEndCall', isForcedDisconnect)
    this.muteStatusMap = {} // clean up the map we do not need it anymore
    this.knownRemoteIdentities = new Set()
    this.clearRecordingNoticeState()
  };
  /**
   * Remember the devices behind a freshly captured stream so they can be released on
   * teardown no matter which stream is holding them by then.
   */
  registerAcquiredTracks (stream) {
    if (!stream || typeof stream.getTracks !== 'function') {
      return stream
    }
    for (const track of stream.getTracks()) {
      if (this.acquiredTracks.has(track)) {
        continue
      }
      this.acquiredTracks.add(track)
      track.addEventListener('ended', () => this.acquiredTracks.delete(track), { once: true })
    }
    return stream
  }

  /**
   * Stop every camera and microphone this client opened, and tear down the audio graph
   * that keeps a microphone alive independently of any stream.
   */
  releaseAcquiredDevices () {
    for (const track of this.acquiredTracks) {
      try {
        track.stop()
      } catch (e) { /* already ended */ }
    }
    this.acquiredTracks.clear()

    // The noise-suppression graph reads the microphone through a MediaStreamSource, which
    // holds the device open even once its track is out of every stream.
    if (this.noiseSuppressionContext) {
      const context = this.noiseSuppressionContext
      this.noiseSuppressionContext = null
      context.close().catch(() => { /* already closed */ })
    }
  }

  stopLocalStreams () {
    this.stopSoundMeter()
    this.stopScreenSharing()
    if (this.uploadBumicomRecording) {
      this.stopAutoRecording(this.axiosInstance)
    } else if (this.isScriptixRecording) {
      // Don't stop Scriptix recording here - let onEndCall handler handle it properly
      // This prevents the recording from being downloaded locally when participant ends the call
      console.log('Scriptix recording is active, skipping stopRecording() in stopLocalStreams()')
    } else if (this.recordingSaveInProgress) {
      // MeetingRoom.endCall already stopped/saving the recording; do not stop again.
    } else {
      this.stopRecording()
    }

    // Clear any pending stream retry timeouts
    for (let timerId of this.streamRetryTimeouts) {
      clearTimeout(timerId)
    }
    this.streamRetryTimeouts.clear()
    this.clearLocalStreamStopInterval()
    // The effect-publish retry only stops itself once the swap lands. A call that ends
    // before it does (a room that never connected) leaves it ticking against a stream and
    // an SDK instance that are both gone.
    clearInterval(this.replaceStreamWaitForPCInterval)
    this.replaceStreamWaitForPCInterval = null

    if (this.localStream) {
      console.log('Localstream is found, stop it', this.localStream)
      // If we already have the stream available, simply shut it down right away
      if (this.localStream !== null && typeof this.localStream.stream !== 'undefined' && this.localStream.stream.getVideoTracks().length > 0) {
        this.localStream.close()
        this.clearLocalStreamRefs()
      } else {
        // Wait for this specific stream to become ready. Capture and detach immediately:
        // one-way Direct has no video tracks, so hasLocalStream() would otherwise keep
        // treating this non-null stream as reusable and a re-invite could join with
        // streamToStop — after which this interval closes it and drops the new call.
        const streamToStop = this.localStream
        this.clearLocalStreamRefs()
        let giveUp = 100
        this.localStreamStopInterval = setInterval(() => {
          if (giveUp <= 0) {
            // Out of patience: close what we have rather than leaving the devices open.
            this.clearLocalStreamStopInterval()
            try { streamToStop.close() } catch (e) { /* noop */ }
            this.clearLocalStreamRefsIfCurrent(streamToStop)
            this.releaseAcquiredDevices()
            return
          }
          if (typeof streamToStop.stream !== 'undefined' && streamToStop.stream.getVideoTracks().length > 0) {
            this.clearLocalStreamStopInterval()
            streamToStop.close()
            this.clearLocalStreamRefsIfCurrent(streamToStop)
            this.releaseAcquiredDevices()
            return
          }
          giveUp--
        }, 100)
      }
    }
    this.releaseAcquiredDevices()
    this.EventBus.$emit('onStoppedLocalStreams')
    // Always disconnect Licode on stream stop. An earlier early-return when video tracks
    // existed skipped this and left the room connected, so a later unexpected-disconnection
    // (e.g. server deleteRoom) could endCall during the next session.
    this.disconnectActiveRoom()
  }

  stopScreenSharing () {
    if (this.mediaEngine === 'livekit') {
      if (this.livekitAdapter) {
        this.livekitAdapter.stopScreenShare()
      }
      window.LoggerAPI.addLog('client', new Date(), 'log', 'STOP_SCREENSHARING', 'Screen sharing stopped.')
      return
    }
    // TODO: (MEDIA_SERVER) Erizo screen-stop path below — delete with Licode.
    if (this.localStreamScreen) {
      this.localStreamScreen.close()
      this.localStreamScreen = null
      this.EventBus.$emit('onScreensharingStopped')
      window.LoggerAPI.addLog('client', new Date(), 'log', 'STOP_SCREENSHARING', 'Screen sharing stopped.')
    }
  }

  getConstraints (product, streamBothWays, deviceIdVideoInput, deviceIdAudioInput, isCamSettings) {
    if (product === 'weseedo_direct' || product === '123meekijken' || product === 'direct') {
      if (streamBothWays) {
        this.facingMode = 'user'
      } else {
        this.facingMode = 'environment'
      }
    } else {
      this.facingMode = 'user'
    }

    // A deliberate front/rear flip (mobile, LiveKit) overrides the product default.
    if (this.flipFacingMode) {
      this.facingMode = this.flipFacingMode
    }

    let constraints = {
      audio: true,
      video: {
        width: { ideal: 640 }, // min: 640, max: 1920
        height: { ideal: 360 }, // min :480, max: 1080
        facingMode: { ideal: this.facingMode },
        focusMode: { ideal: 'continuous' }
      },
      data: true,
      local: true,
      attributes: { codec: 'VP8' }
    }

    // This is a switch of devices, keep all constraints the same except for devices
    if (deviceIdVideoInput || deviceIdAudioInput) {
      constraints.video.deviceId = { exact: deviceIdVideoInput || this.activeVideoDevice }
      if (typeof constraints.audio === 'boolean') {
        constraints.audio = { deviceId: { exact: deviceIdAudioInput || this.activeAudioInput } }
      } else {
        constraints.audio.deviceId = { exact: deviceIdAudioInput || this.activeAudioInput }
      }
    }

    let browser = window.DetectRTC.browser.name
    let isMobileDevice = window.DetectRTC.isMobileDevice
    if (!isMobileDevice) {
      isMobileDevice = /iPad/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    }

    this.getVideoResolution(constraints, isMobileDevice, isCamSettings)

    if (!isMobileDevice) {
      if (browser === 'Firefox') {
        constraints.video.facingMode = this.facingMode
      }
    } else {
      if (browser === 'Firefox') {
        constraints.video.facingMode = this.facingMode
      }
    }

    let userId = null
    let realName = ''

    if (store.getters.getUser !== false) {
      userId = store.getters.getUser.id
      realName = this.setFullName(store.getters.getUser)

      if (product === 'weseedo_direct' || product === '123meekijken' || product === 'direct') {
        constraints.audio = false
        if (browser === 'Firefox') {
          constraints.audio = true // this is to enable firefox to record video
        }
        if (!streamBothWays) {
          constraints.video = false
        }
      }
    } else {
      if (product === 'weseedo_direct' || product === '123meekijken' || product === 'direct') {
        userId = store.getters.getMeeting.participant.id
        realName = 'Bezoeker'
        constraints.audio = false
      } else {
        userId = store.getters.getMeeting.participant.id
        realName = store.getters.getMeeting.participant.name
      }
    }

    constraints.attributes = {
      'user_id': userId,
      'real_name': realName
    }
    this.EventBus.$emit('onAskStreamPermissions')

    return constraints
  }
  /**
 * This logic ensures that video quality scales down as more participants join,
 * which reduces bandwidth and CPU load. On mobile, all resolutions are further halved to optimize performance.
 * @param {Object} constraints
 * @param {Boolean} isCamSettings If in camera settings,
 * @param {Boolean} isMobileDevice
 */
  getVideoResolution (constraints, isMobileDevice, isCamSettings) {
    // LiveKit: fixed 720p capture (preview + in-meeting); simulcast/dynacast +
    // adjustLivekitCaptureForCount handle quality live, not the provisioned count.
    if (this.mediaEngine === 'livekit') {
      constraints.video.width.ideal = 1280
      constraints.video.height.ideal = 720
      return
    }

    // Get the current meeting object from Vuex store
    const meeting = store.getters.getMeeting

    if (meeting) {
      // TODO: (MEDIA_SERVER) The participant_count capture-scaling below is the Licode
      // path; LiveKit uses the fixed 720 above + live adjustLivekitCaptureForCount().
      // When Licode is removed, this block goes and the livekit branch becomes the body.
      // Default participant count = 2 (assume 1-to-1 call if not specified)
      let participantCount = 2

      // Resolve participant count (supports both `participant_count` and `participants`)
      if (typeof meeting.participant_count !== 'undefined') {
        participantCount = meeting.participant_count
      } else if (Array.isArray(meeting.participants)) {
        participantCount = meeting.participants.length
      }

      // Define base resolutions per participant count
      const resolutionRules = [
        { max: 2, width: 1280, height: 720 }, // 1-to-1 call
        { max: 6, width: 640, height: 360 }, // 3–6 participants
        { max: 9, width: 320, height: 180 }, // 5–9 participants
        { max: Infinity, width: 160, height: 90 } // 10+ participants
      ]

      // Determine target resolution
      /* eslint-disable no-unused-vars */
      let chosenResolution = resolutionRules.find(rule => participantCount <= rule.max)

      // // If in camera settings, always force HD
      if (isCamSettings === true) {
        chosenResolution = { width: 1280, height: 720 }
      }

      // Apply resolution with multiplier (for mobile scaling)
      constraints.video.width.ideal = chosenResolution.width
      constraints.video.height.ideal = chosenResolution.height
    }
  }

  setFullName (user) {
    if (user !== false) {
      if (typeof user.first_name !== 'undefined' && typeof user.last_name !== 'undefined') {
        return (user.first_name + ' ' + user.last_name).trim()
      } else if (typeof user.first_name !== 'undefined' && typeof user.last_name === 'undefined') {
        return user.first_name
      } else if (typeof user.first_name === 'undefined' && typeof user.last_name !== 'undefined') {
        return user.last_name
      } else {
        return 'Agent'
      }
    } else {
      return 'Agent'
    }
  }

  tmpVarsCamera = {
    product: null,
    streamBothWays: null,
    callback: null
  }

  lastProduct = null
  lastStreamBothWays = null

  switchCameraDevice (deviceId, isCamSettings) {
    if (this.deviceSwitching) {
      return
    }
    this.deviceSwitching = true
    if (deviceId === this.activeVideoDevice) {
      this.deviceSwitching = false
      // No actual device change. The caller (e.g. the CamSettings form.device watcher,
      // triggered when onDeviceList sets form.device to the already-active camera) may
      // have cleared the preview first, so re-attach the existing stream instead of
      // leaving the loader spinning forever.
      this.emitExistingLocalStream()
      return
    }

    let facingMode = null
    if (typeof this.localStream.stream !== 'undefined' && this.localStream.stream.getVideoTracks().length > 0) {
      facingMode = this.localStream.stream.getVideoTracks()[0].getSettings().facingMode
    }
    /* await this.localStream.stream.getTracks().forEach(track => {
      track.stop()
    }) */
    // Keep the real facing when reported; 'user' is only the desktop fallback.
    this.facingMode = facingMode || 'user'
    this.startLocalStreams(this.lastProduct, this.lastStreamBothWays, () => {
      this.initializeRnnoise()
    }, deviceId, null, null, undefined, undefined, isCamSettings)
    this.deviceSwitching = false
  }

  switchAudioInputDevice (deviceId) {
    if (this.deviceSwitching) {
      return
    }
    this.deviceSwitching = true
    if (deviceId === this.activeAudioInput) {
      this.deviceSwitching = false
      return
    }

    /* this.localStream.stream.getTracks().forEach(track => {
      track.stop()
    }) */

    this.startLocalStreams(this.lastProduct, this.lastStreamBothWays, () => {
      this.initializeRnnoise()
    }, null, deviceId, null)
    this.deviceSwitching = false
  }

  switchAudioOutputDevice (deviceId, videos, callback) {
    if (this.deviceSwitching) {
      return
    }
    this.deviceSwitching = true
    if (deviceId === this.activeAudioOutput) {
      this.deviceSwitching = false
    } else if (deviceId !== null && deviceId !== '') {
      this.activeAudioOutput = deviceId
    }
    videos.forEach((video) => {
      video.setSinkId(this.activeAudioOutput)
    })

    this.deviceSwitching = false
  }

  // Attach audio output device to video element using device/sink ID.
  attachSinkId (element, sinkId) {
    if (!element) {
      return
    }

    if (typeof element.sinkId !== 'undefined') {
      element.setSinkId(sinkId)
        .then(() => {
          this.activeAudioOutput = sinkId
          console.log('Success, audio output device attached: ' + sinkId)
        })
        .catch(error => {
          let errorMessage = error
          console.error(errorMessage)
        })
    } else {
      console.warn('Browser does not support output device selection.')
    }
  }

  // AnalyserNode mic meter (replaces the deprecated ScriptProcessor SoundMeter). Emits the same
  // onMicrophoneVolume RMS (0..1) the settings pane reads. Engine-agnostic: reads localStream's
  // audio track for both Licode and LiveKit.
  startSoundMeter () {
    this.stopSoundMeter()
    if (!this.localStream || !this.localStream.stream || this.localStream.stream.getAudioTracks().length === 0) {
      return
    }
    try {
      let AudioContext = window.AudioContext || window.webkitAudioContext
      const context = new AudioContext()
      this.soundMeterContext = context
      // Created after the getUserMedia await, the context starts suspended under the autoplay
      // policy; without resume the analyser only ever reads silence so the bar never moves.
      if (context.state === 'suspended') {
        context.resume().catch(() => {})
      }
      const source = context.createMediaStreamSource(this.localStream.stream)
      const analyser = context.createAnalyser()
      analyser.fftSize = 1024
      source.connect(analyser)
      this.soundMeterSource = source
      this.soundMeterAnalyser = analyser
      const samples = new Float32Array(analyser.fftSize)
      let lastEmit = 0
      const tick = (timestamp) => {
        // stopSoundMeter nulls the analyser to end the loop.
        if (this.soundMeterAnalyser !== analyser) {
          return
        }
        this.soundMeterRaf = requestAnimationFrame(tick)
        // ~20/s so the level bar tracks the voice smoothly (a CSS transition on the fill
        // interpolates between updates).
        if (timestamp - lastEmit < 50) {
          return
        }
        lastEmit = timestamp
        // With mic permission already granted getUserMedia resolves without a gesture, so the
        // context stays suspended and the analyser reads only silence. Keep nudging resume(): it
        // takes effect once the page has had any interaction (sticky activation).
        if (context.state === 'suspended') {
          context.resume().catch(() => {})
        }
        analyser.getFloatTimeDomainData(samples)
        let sum = 0
        for (let i = 0; i < samples.length; i++) {
          sum += samples[i] * samples[i]
        }
        const rms = Math.sqrt(sum / samples.length)
        this.EventBus.$emit('onMicrophoneVolume', {
          'volume': rms.toFixed(3)
        })
      }
      this.soundMeterRaf = requestAnimationFrame(tick)
    } catch (e) {
      console.log('SoundMeter not supported.')
    }
  }

  // Tear down the mic meter: stop the loop, disconnect the graph and close the AudioContext so
  // repeated captures/device switches never leak toward the browser's ~6-context cap.
  stopSoundMeter () {
    if (this.soundMeterRaf) {
      cancelAnimationFrame(this.soundMeterRaf)
      this.soundMeterRaf = null
    }
    if (this.soundMeterSource) {
      try {
        this.soundMeterSource.disconnect()
      } catch (e) {}
      this.soundMeterSource = null
    }
    if (this.soundMeterAnalyser) {
      try {
        this.soundMeterAnalyser.disconnect()
      } catch (e) {}
      this.soundMeterAnalyser = null
    }
    if (this.soundMeterContext) {
      this.soundMeterContext.close().catch(() => {})
      this.soundMeterContext = null
    }
  }

  // TODO: (MEDIA_SERVER) Licode-only (picks ErizoV9 P2P vs Erizo MCU). Delete with Licode;
  // also remove the setP2P() call sites in CamSettings.vue / WaitingRoom.vue / MeetingRoom.vue.
  setP2P (p2p) {
    this.p2p = p2p
  }
  async initializeVirtualEffect (backgroundImage) {
    await this.initializeEffectsSDK()
    this.backgroundImage = backgroundImage
    this.isVideoEffectRunning = 'virtual'
    if (this.localStream && this.localStream.stream) {
      this.effectsSDK.useStream(new MediaStream(this.localStream.stream.getVideoTracks()))
    } else {
      console.error('No local stream available for EffectsSDK')
      return
    }
    this.effectsSDK.onReady = () => {
      this.effectsSDK.stop()
      this.effectsSDK.run()
      this.effectsSDK.clearBackground()
      this.effectsSDK.clearBlur()
      this.effectsSDK.setBackground(this.backgroundImage)
    }
    try {
      this.effectsSDK.getStream()
      this.effectsSDK.run()
      this.effectsSDK.clearBackground()
      this.effectsSDK.clearBlur()
      this.effectsSDK.setBackground(this.backgroundImage)
    } catch (e) {
      console.log('Error while starting blurring effect: ', e)
      this.effectsSDK.useStream(new MediaStream(this.localStream.stream.getVideoTracks()))
    }
    const localVideoElementLoop = setInterval(() => {
      let localVideoElement
      try {
        localVideoElement = document.querySelector('#local') || document.querySelector(`#${CSS.escape(this.localStream?.getID())}`)
      } catch (e) {
        console.warn('Blurring local video error: ', e)
      } finally {
        console.log('Blurring local: ', localVideoElement)
        if (localVideoElement) {
          localVideoElement.srcObject = this.effectsSDK.getStream()
          clearInterval(localVideoElementLoop)
        }
      }
    }, 50)
    clearInterval(this.replaceStreamWaitForPCInterval)
    this.replaceStreamWaitForPCInterval = setInterval(() => {
      this.replaceTracksInPeerConnection(this.effectsSDK.getStream().getVideoTracks()[0], null)
    }, 100)
  }
  async initializeBlurringEffect () {
    await this.initializeEffectsSDK()
    this.isVideoEffectRunning = 'blurring'
    if (this.localStream && this.localStream.stream) {
      this.effectsSDK.useStream(new MediaStream(this.localStream.stream.getVideoTracks()))
    } else {
      console.error('No local stream available for EffectsSDK')
      return
    }
    this.effectsSDK.onReady = () => {
      this.effectsSDK.run()
      this.effectsSDK.clearBackground()
      this.effectsSDK.clearBlur()
      this.effectsSDK.setBlur(1)
    }
    try {
      this.effectsSDK.getStream()
      this.effectsSDK.run()
      this.effectsSDK.clearBackground()
      this.effectsSDK.clearBlur()
      this.effectsSDK.setBlur(1)
    } catch (e) {
      console.log('Error while starting blurring effect: ', e)
      this.effectsSDK.useStream(new MediaStream(this.localStream.stream.getVideoTracks()))
    }
    const localVideoElementLoop = setInterval(() => {
      let localVideoElement
      try {
        localVideoElement = document.querySelector('#local') || document.querySelector(`#${CSS.escape(this.localStream?.getID())}`)
      } catch (e) {
        console.warn('Blurring local video error: ', e)
      } finally {
        console.log('Blurring local: ', localVideoElement)
        if (localVideoElement) {
          localVideoElement.srcObject = this.effectsSDK.getStream()
          clearInterval(localVideoElementLoop)
        }
      }
    }, 50)
    clearInterval(this.replaceStreamWaitForPCInterval)
    this.replaceStreamWaitForPCInterval = setInterval(() => {
      this.replaceTracksInPeerConnection(this.effectsSDK.getStream().getVideoTracks()[0], null)
    }, 1000)
  }
  async reloadVideoEffect () {
    this.effectsSDK.clear()
    if (this.isVideoEffectRunning === 'virtual') {
      this.effectsSDK.onReady = () => {
        this.effectsSDK.run()
        this.effectsSDK.setBackground(this.backgroundImage)
      }
    } else if (this.isVideoEffectRunning === 'blurring') {
      this.effectsSDK.onReady = () => {
        this.effectsSDK.run()
        this.effectsSDK.setBlur(1)
      }
    } else {
      return
    }
    this.effectsSDK.useStream(new MediaStream(this.localStream.stream.getVideoTracks()))
    const localVideoElementLoop = setInterval(() => {
      let localVideoElement
      try {
        localVideoElement = document.querySelector('#local') || document.querySelector(`#${CSS.escape(this.localStream?.getID())}`)
      } catch (e) {
        console.warn('Blurring local video error: ', e)
      } finally {
        console.log('Blurring local: ', localVideoElement)
        if (localVideoElement) {
          localVideoElement.srcObject = this.effectsSDK.getStream()
          clearInterval(localVideoElementLoop)
        }
      }
    }, 50)
    clearInterval(this.replaceStreamWaitForPCInterval)
    this.replaceStreamWaitForPCInterval = setInterval(() => {
      this.replaceTracksInPeerConnection(this.effectsSDK.getStream().getVideoTracks()[0], null)
    }, 100)
  }
  async reloadEffectsForRemoteParticipants () {
    clearInterval(this.replaceStreamWaitForPCInterval)
    this.replaceStreamWaitForPCInterval = setInterval(() => {
      this.replaceTracksInPeerConnection(this.effectsSDK.getStream().getVideoTracks()[0], null)
    }, 100)
  }
  async removeVideoEffect () {
    // effectsSDK is null if it was never initialised (or was reset) — turning the effect
    // off in that state must not throw "reading 'stop' of null".
    if (this.effectsSDK) {
      this.effectsSDK.stop()
    }
    clearInterval(this.replaceStreamWaitForPCInterval)
    if (!this.localStream) {
      this.isVideoEffectRunning = false
      return
    }

    let localVideoElement = document.querySelector('#local') || document.querySelector(`#${CSS.escape(this.localStream?.getID())}`)

    // Check if video element exists before setting srcObject
    if (localVideoElement && this.localStream && this.localStream.stream) {
      localVideoElement.srcObject = this.localStream.stream

      this.replaceStreamWaitForPCInterval = setInterval(() => {
        if (this.localStream && this.localStream.stream) {
          const videoTracks = this.localStream.stream.getVideoTracks()
          if (videoTracks.length > 0) {
            this.replaceTracksInPeerConnection(videoTracks[0], null)
          }
        }
      }, 1000)
    } else {
      console.warn('[WebRTC] Cannot remove video effect: video element or stream not found')
    }

    this.isVideoEffectRunning = false
  }
  /**
   * Replaces the video track in the peer connection with the specified track.
   *
   * @param {MediaStreamTrack} replaceVideoTrack - The video track to replace.
   * @param {MediaStreamTrack} replaceAudioTrack - The audio track to replace.
   */
  replaceTracksInPeerConnection (replaceVideoTrack, replaceAudioTrack) {
    // Validate inputs before processing
    if (!replaceVideoTrack) {
      console.warn('[WebRTC] Cannot replace tracks: no video track provided')
      return false
    }

    if (this.mediaEngine === 'livekit') {
      // Only stop retrying once the video is actually published. An effect enabled
      // pre-join ticks before publishLocalTracks runs; clearing unconditionally then
      // would publish the raw camera and never swap in the effect for subscribers.
      if (this.livekitAdapter && this.livekitAdapter.localVideoTrack) {
        this.livekitAdapter.replaceVideoTrack(replaceVideoTrack)
        if (replaceAudioTrack) {
          this.livekitAdapter.replaceAudioTrack(replaceAudioTrack)
        }
        clearInterval(this.replaceStreamWaitForPCInterval)
        return true
      }
      return false
    }

    // TODO: (MEDIA_SERVER) Erizo peer-connection track replacement below — delete with Licode.
    let pc = this.getPeerConnection()
    let senderFound = false
    if (pc) {
      if (pc.forEach) {
        // OR the result so a single connection without the sender doesn't
        // clear a successful replacement on another connection.
        pc.forEach((pcInstance) => {
          if (this.processSender(pcInstance, replaceVideoTrack, replaceAudioTrack)) {
            senderFound = true
          }
        })
      } else {
        senderFound = this.processSender(pc, replaceVideoTrack, replaceAudioTrack)
      }

      if (senderFound) {
        clearInterval(this.replaceStreamWaitForPCInterval)
      }
    }
    return senderFound
  }
  /**
   * Processes the sender in the peer connection and replaces the video track.
   * @param {RTCPeerConnection} pc - The peer connection object.
   * @param {MediaStreamTrack} replaceVideoTrack - The video track to replace.
   * @param {MediaStreamTrack} replaceAudioTrack - The audio track to replace.
   * @returns {boolean} - Indicates whether the sender was found and processed.
   */
  processSender (pc, replaceVideoTrack, replaceAudioTrack) {
    // Validate inputs before processing
    if (!pc || !pc.peerConnection || !replaceVideoTrack) {
      console.warn('[WebRTC] Invalid parameters for processSender')
      return false
    }

    const senderVideo = pc.peerConnection.getSenders().find(s => s.track && s.track.kind === replaceVideoTrack.kind)
    const senderAudio = replaceAudioTrack ? pc.peerConnection.getSenders().find(s => s.track && s.track.kind === replaceAudioTrack.kind) : null

    if (senderVideo || senderAudio) {
      if (senderVideo) {
        senderVideo.replaceTrack(replaceVideoTrack).catch((err) => {
          console.error('Error replacing video track:', err)
        })
      }
      if (senderAudio && replaceAudioTrack) {
        senderAudio.replaceTrack(replaceAudioTrack).catch((err) => {
          console.error('Error replacing audio track:', err)
        })
      }
      clearInterval(this.replaceStreamWaitForPCInterval)
      return true // Indicates sender was found and processed
    }
    return false // Indicates no sender was found
  }
  /**
   * Initializes the Rnnoise noise suppression for audio processing.
   *
   * @return {Promise} A promise that resolves when the Rnnoise noise suppression is successfully initialized.
   */
  async initializeRnnoise () {
    if (!this.localStream || !this.localStream.stream || this.localStream.stream.getAudioTracks().length === 0) {
      return
    }
    await ensureRnnoise()
    // Close any prior noise-suppression context before opening a new one (mic switch /
    // re-filter). Browsers cap concurrent AudioContexts (~6); leaking them silently kills
    // audio after a few switches. The previous filtered track is already unpublished by the
    // device-switch re-capture before this runs, so closing causes no audible gap.
    if (this.noiseSuppressionContext) {
      try { await this.noiseSuppressionContext.close() } catch (e) { /* already closed */ }
    }
    // Create a new AudioContext for audio processing.
    this.noiseSuppressionContext = new AudioContext()
    // Load the RNNoise WebAssembly (Wasm) binary asynchronously. This is used for noise suppression.
    // The `loadRnnoise` function takes URLs for the normal and SIMD (Single Instruction, Multiple Data) optimized versions of the RNNoise Wasm.
    const rnnoiseWasmBinary = await loadRnnoise({ url: rnnoiseWasmPath, simdUrl: rnnoiseWasmSimdPath })
    // Add the RNNoise audio worklet module to the AudioContext. This allows custom audio processing within the browser's audio engine.
    await this.noiseSuppressionContext.audioWorklet.addModule(rnnoiseWorkletPath)

    const muteStatus = this.isAudioMuted
    // Unmute audio stream if it was previously muted before filtering.
    if (muteStatus) {
      this.unmuteAudio()
    }

    // Retrieve the original audio track from the local media stream.
    const originalAudioTrack = this.localStream.stream.getAudioTracks()[0]
    // Create a MediaStreamSource node from the original audio track. This node acts as the input to the audio processing graph.
    try {
      const source = this.noiseSuppressionContext.createMediaStreamSource(new MediaStream([originalAudioTrack]))
      // Create an instance of RnnoiseWorkletNode, which processes the audio to suppress noise.
      // It uses the loaded RNNoise Wasm binary and is configured to process a single audio channel.
      const rnnoise = new RnnoiseWorkletNode(this.noiseSuppressionContext, {
        wasmBinary: rnnoiseWasmBinary,
        maxChannels: 1
      })
      // Connect the source (original audio) to the RNNoise worklet node to start the noise suppression processing.
      source.connect(rnnoise)
      // Create a MediaStreamDestination node, which collects the processed audio output.
      const destination = this.noiseSuppressionContext.createMediaStreamDestination()
      // RNNoise (maxChannels:1) only writes output channel 0; its output bus follows the
      // input channel count, so a 2-ch capture yields [signal, silent] = left-only, and a
      // 'speakers' up-mix can't fix that (no up-mix when the bus is already 2-ch). On LiveKit,
      // duplicate the mono output into both channels with a ChannelMerger so the published
      // track is true dual-mono (L==R) and plays in both ears. Licode stays mono.
      if (this.mediaEngine === 'livekit') {
        const merger = this.noiseSuppressionContext.createChannelMerger(2)
        rnnoise.connect(merger, 0, 0)
        rnnoise.connect(merger, 0, 1)
        merger.connect(destination)
      } else {
        rnnoise.connect(destination)
      }
      // Extract the filtered (processed) audio track from the destination stream.
      const filteredAudioTrack = destination.stream.getAudioTracks()[0]
      // Remove the original (unprocessed) audio track from the local stream.
      this.localStream.stream.removeTrack(originalAudioTrack)
      // Add the filtered (noise-suppressed) audio track to the local stream.
      this.localStream.stream.addTrack(filteredAudioTrack)

      // When re-filtering mid-call on LiveKit (e.g. mic switch), publish the
      // filtered track. At initial join the engine isn't LiveKit yet, so this
      // is skipped and the already-filtered track is published on connect.
      if (this.mediaEngine === 'livekit' && this.livekitAdapter && this.livekitAdapter.isConnected()) {
        await this.livekitAdapter.replaceAudioTrack(filteredAudioTrack)
      }

      // Mute audio stream if it was previously muted before filter.
      if (muteStatus) {
        this.muteAudio()
      }
    } catch (e) {
      console.warn('Failed to initialize RNNoise noise suppression: ', e)
    }
  }
  lastConstraints = null
  startLocalStreams (product, streamBothWays, callback, deviceIdVideoInput, deviceIdAudioInput, deviceIdAudioOutput, videoMuted, audioMuted, isCamSettings, force = false) {
    // Resolve the engine from meeting/user config before capture: the socket 'join'
    // event that sets this.mediaEngine arrives only after capture, so stereo channel
    // decisions would otherwise be wrong on the first join (mono left-ear).
    const meeting = store.getters.getMeeting
    const user = store.getters.getUser
    this.mediaEngine = (meeting && meeting.media_engine) || (user && user.media_engine) || this.mediaEngine
    if (typeof videoMuted !== 'undefined') {
      this.isVideoMuted = videoMuted
    }
    if (typeof audioMuted !== 'undefined') {
      this.isAudioMuted = audioMuted
    }
    if (deviceIdVideoInput === false) {
      this.activeVideoDevice = null
    }
    if (deviceIdAudioInput === false) {
      this.activeAudioInput = null
    }
    if (deviceIdAudioOutput === false) {
      this.activeAudioOutput = null
    } else if (deviceIdAudioOutput && deviceIdAudioOutput !== '') {
      this.activeAudioOutput = deviceIdAudioOutput
    }

    this.lastProduct = product
    this.lastStreamBothWays = streamBothWays
    if (this.hasLocalStream() && !deviceIdVideoInput && !deviceIdAudioInput && !force) {
      if (typeof callback !== 'undefined') {
        callback(this.localStream)
      } else {
        // No callback (CamSettings preview uses onStreamAccessGranted). Stream survives
        // a remount (e.g. LiveKit socket reconnect) — re-emit so the preview re-attaches.
        this.emitExistingLocalStream()
      }
    } else {
      let constraints = this.getConstraints(product, streamBothWays, deviceIdVideoInput, deviceIdAudioInput, isCamSettings)
      // setup constraints for noise cancellation
      if (typeof constraints.audio === 'object') {
        constraints.audio = {
          ...constraints.audio,
          echoCancellation: true,
          echoCancellationType: { ideal: 'system' },
          // Capture mono. RNNoise (maxChannels:1) collapses to a single channel anyway;
          // the dual-mono stereo for both-ears playback is built after RNNoise via a
          // ChannelMerger (see initializeRnnoise). A 2-ch capture only causes left-only.
          channelCount: 1,
          noiseSuppression: false,
          autoGainControl: true,
          googEchoCancellation: false,
          googAutoGainControl: false,
          googExperimentalAutoGainControl: false,
          googNoiseSuppression: false,
          googExperimentalNoiseSuppression: false,
          googHighpassFilter: false,
          googTypingNoiseDetection: false,
          googBeamforming: false,
          googArrayGeometry: false,
          googAudioMirroring: false,
          googNoiseReduction: false,
          mozNoiseSuppression: false,
          mozAutoGainControl: false,
          latency: 0.01
        }
      }
      // TODO: (MEDIA_SERVER) LiveKit captures via startLocalStreamsLivekit; the Erizo
      // path below (unpublish + Erizo.Stream creation + access-accepted) is deleted with Licode.
      if (this.mediaEngine === 'livekit') {
        this.startLocalStreamsLivekit(constraints, callback)
        return
      }
      if (this.room) {
        this.room.unpublish(this.localStream)
      }
      if (this.p2p) {
        // eslint-disable-next-line no-undef
        this.localStream = ErizoV9.Stream(constraints)
      } else {
        // eslint-disable-next-line no-undef
        this.localStream = Erizo.Stream(constraints)
      }
      this.lastConstraints = constraints

      this.localStream.addEventListener('bandwidth-alert', (streamEvent) => {
        console.error('THERE IS A BANDWIDTH PROBLEM NORMAL STREAM')
        console.error(streamEvent)
      })
      this.localStream.addEventListener('access-accepted', async (stream) => {
        console.log('stream in access-accepted event listener', stream)
        if (constraints.audio !== false) {
          this.startSoundMeter()
        }
        if (this.isVideoEffectRunning) {
          await this.reloadVideoEffect()
        } else {
          await this.resetEffectsSDK()
        }
        if (this.room) {
          await this.room.publish(this.localStream, {
            disableIceRestart: true,
            forceTurn: true,
            simulcast: false,
            scheme: 'notify-break-recover'
          })
        }

        let devices = await navigator.mediaDevices.enumerateDevices()

        this.videoDevices = []
        this.audioDevices = []
        this.outputDevices = []
        let video = null
        let facingMode = null
        if (constraints.video !== false) {
          if (!('getCapabilities' in this.localStream.stream.getVideoTracks()[0])) {
            facingMode = this.localStream.stream.getVideoTracks()[0].getSettings().facingMode
            this.activeVideoDevice = this.localStream.stream.getVideoTracks()[0].getSettings().deviceId
          } else {
            this.activeVideoDevice = this.localStream.stream.getVideoTracks()[0].getCapabilities().deviceId
          }

          if (this.localStream.stream.getAudioTracks().length > 0) {
            if (!('getCapabilities' in this.localStream.stream.getAudioTracks()[0])) {
              this.activeAudioInput = this.localStream.stream.getAudioTracks()[0].getSettings().deviceId
            } else {
              this.activeAudioInput = this.localStream.stream.getAudioTracks()[0].getCapabilities().deviceId
            }
          } else {
            this.activeAudioInput = null
          }

          video = this.getVideoTag(this.localStream)

          if (this.isVideoMuted) {
            this.muteVideo()
          }
          if (this.isAudioMuted) {
            this.muteAudio()
          }

          if (!facingMode) {
            facingMode = this.facingMode
          }
        }

        let devicesVideo = []
        let devicesAudio = []
        let devicesSpeaker = []
        let replacements = ['Default - ', 'Standaard - ']

        for (let device of devices) {
          device = JSON.parse(JSON.stringify(device))
          if (device.deviceId === 'communications') {
            continue
          }
          for (let replacement of replacements) {
            device.label = device.label.replace(replacement, '')
          }
          if (device.kind === 'audioinput') {
            let index = devicesAudio.indexOf(device.label)
            if (index < 0) {
              devicesAudio.push(device.label)
              this.audioDevices.push(device)
            } else if (device.deviceId === this.activeAudioInput) {
              this.audioDevices.pop()
              this.audioDevices.push(device)
            }
          } else if (device.kind === 'videoinput') {
            let index = devicesVideo.indexOf(device.label)
            if (index < 0) {
              devicesVideo.push(device.label)
              this.videoDevices.push(device)
            } else if (device.deviceId === this.activeVideoDevice) {
              this.videoDevices.pop()
              this.videoDevices.push(device)
            }
          } else if (device.kind === 'audiooutput') {
            let index = devicesSpeaker.indexOf(device.label)
            if (index < 0) {
              devicesSpeaker.push(device.label)
              this.outputDevices.push(device)
            } else if (device.deviceId === this.activeAudioOutput) {
              this.outputDevices.pop()
              this.outputDevices.push(device)
            }
          }
        }

        let isMobileDevice = window.DetectRTC.isMobileDevice
        if (!isMobileDevice) {
          isMobileDevice = /iPad/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
        }

        this.EventBus.$emit('onStreamAccessGranted', {
          'can_switch_camera': isMobileDevice,
          'stream': this.localStream.stream,
          'video': video,
          'mirrored': facingMode === 'user',
          'video_muted': this.isVideoMuted,
          'audio_muted': this.isAudioMuted
        })

        let deviceFeedback = {
          'current_video_input': this.activeVideoDevice,
          'current_audio_input': this.activeAudioInput,
          'current_audio_output': this.activeAudioOutput,
          'devices': this.videoDevices,
          'audio_input': this.audioDevices,
          'audio_output': this.outputDevices
        }

        this.EventBus.$emit('onDeviceList', deviceFeedback)

        if (typeof callback !== 'undefined') {
          callback(this.localStream, deviceFeedback)
        }
      })
      this.localStream.addEventListener('access-denied', async (event) => {
        let devices = await navigator.mediaDevices.enumerateDevices()
        this.videoDevices = []
        this.audioDevices = []
        this.outputDevices = []
        for (const device of devices) {
          if (device.kind === 'videoinput') {
            this.videoDevices.push({
              'label': device.label,
              'deviceId': device.deviceId
            })
          } else if (device.kind === 'audioinput') {
            this.audioDevices.push({
              'label': device.label,
              'deviceId': device.deviceId
            })
          } else if (device.kind === 'audiooutput') {
            this.outputDevices.push({
              'label': device.label,
              'deviceId': device.deviceId
            })
          }
        }

        let deviceFeedback = {
          'video_input': this.videoDevices,
          'audio_input': this.audioDevices,
          'audio_output': this.outputDevices
        }
        this.localStream = null
        this.activeVideoDevice = null
        this.activeAudioInput = null
        this.EventBus.$emit('onStreamAccessDenied', {
          'error': event.msg,
          'devices': deviceFeedback
        })
      })
      this.localStream.init()
    }
  }

  // Re-emit onStreamAccessGranted for an already-running stream so a remounted view
  // re-attaches its preview.
  emitExistingLocalStream () {
    if (!this.localStream || !this.localStream.stream || typeof this.localStream.stream.getVideoTracks !== 'function') {
      return
    }
    const video = this.getVideoTag(this.localStream)
    const vt = this.localStream.stream.getVideoTracks()[0]
    const settings = vt && typeof vt.getSettings === 'function' ? vt.getSettings() : {}
    const facingMode = settings.facingMode || this.facingMode || 'user'
    let isMobileDevice = window.DetectRTC.isMobileDevice
    if (!isMobileDevice) {
      isMobileDevice = /iPad/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    }
    this.EventBus.$emit('onStreamAccessGranted', {
      'can_switch_camera': isMobileDevice,
      'stream': this.localStream.stream,
      'video': video,
      'mirrored': facingMode === 'user',
      'video_muted': this.isVideoMuted,
      'audio_muted': this.isAudioMuted
    })
  }

  // LiveKit local capture: getUserMedia directly, wrap in an Erizo-Stream-shaped shim,
  // then emit the same access-granted/device events.
  async startLocalStreamsLivekit (constraints, callback) {
    try {
      // Receive-only agent (direct/123meekijken viewer) gets {audio:false, video:false}.
      // getUserMedia rejects when both are false, so skip capture and use an empty stream;
      // the agent joins as a pure subscriber (Licode used a data-only Erizo.Stream here).
      const noMedia = constraints.audio === false && constraints.video === false
      // Android AND iOS/iPadOS open only one physical camera at a time: free the previous
      // camera BEFORE getUserMedia so a flip / device switch can open the other camera instead
      // of failing with NotReadableError (a "no device rights"-style error) or returning a
      // dead/transparent track. Old audio is freed after the swap below. (Do NOT skip this on
      // iOS: it needs the pre-stop just like Android — confirmed on iPad Safari + Chrome.)
      if (!noMedia && constraints.video && this.localStream && this.localStream.stream) {
        this.localStream.stream.getVideoTracks().forEach(t => { try { t.stop() } catch (e) { /* noop */ } })
      }
      const mediaStream = noMedia
        ? new MediaStream()
        : this.registerAcquiredTracks(await navigator.mediaDevices.getUserMedia({
          audio: constraints.audio,
          video: constraints.video
        }))

      let userId = null
      let realName = ''
      if (store.getters.getUser !== false) {
        userId = store.getters.getUser.id
        realName = store.getters.getUser.name
      } else {
        userId = store.getters.getMeeting.participant.id
        realName = store.getters.getMeeting.participant.name
      }

      // Keep a handle on the old stream so we can free its devices after switching.
      const previousStream = (this.localStream && this.localStream.stream) ? this.localStream.stream : null
      const isLiveSwitch = !!(this.livekitAdapter && this.livekitAdapter.isConnected())

      const self = this
      this.localStream = {
        stream: mediaStream,
        getID: () => 'lk-local',
        close: () => { try { mediaStream.getTracks().forEach(t => t.stop()) } catch (e) { /* noop */ } },
        sendData: (obj) => (self.livekitAdapter ? self.livekitAdapter.sendData(obj) : undefined),
        muteAudio: (mute, cb) => {
          if (self.livekitAdapter) self.livekitAdapter.setAudioMuted(mute)
          const ok = true
          if (typeof cb === 'function') cb(ok)
        },
        muteVideo: (mute, cb) => {
          if (self.livekitAdapter) self.livekitAdapter.setVideoMuted(mute)
          const ok = true
          if (typeof cb === 'function') cb(ok)
        },
        getAttributes: () => ({ user_id: userId, real_name: realName }),
        hasScreen: () => false
      }

      const newVideoTrack = mediaStream.getVideoTracks()[0]
      const newAudioTrack = mediaStream.getAudioTracks()[0]
      // Track the active devices so repeated-switch guards work.
      if (newVideoTrack) {
        this.activeVideoDevice = newVideoTrack.getSettings().deviceId || this.activeVideoDevice
      }
      if (newAudioTrack) {
        this.activeAudioInput = newAudioTrack.getSettings().deviceId || this.activeAudioInput
      }

      // Sound meter / effects must never block the preview; if they throw we still
      // create the video tag and emit onStreamAccessGranted below.
      if (constraints.audio !== false) {
        try {
          this.startSoundMeter()
        } catch (e) {
          console.warn('LiveKit startSoundMeter failed (preview continues)', e)
        }
      }

      try {
        // Device switch mid-meeting: replace the published tracks live instead of
        // recreating the connection, then free the previous devices.
        if (isLiveSwitch) {
          if (newAudioTrack) {
            await this.livekitAdapter.replaceAudioTrack(newAudioTrack)
          }
          if (this.isVideoEffectRunning === 'virtual') {
            // Re-apply via the full init path, NOT reloadVideoEffect: its effectsSDK.clear()
            // stops the still-published effects track and relies on onReady re-firing to
            // restart it, which it does not on an already-ready instance — leaving the local
            // preview black and the remote camera muted after a device switch.
            await this.initializeVirtualEffect(this.backgroundImage)
          } else if (this.isVideoEffectRunning === 'blurring') {
            await this.initializeBlurringEffect()
          } else if (newVideoTrack) {
            await this.livekitAdapter.replaceVideoTrack(newVideoTrack)
          }
          if (this.isVideoEffectRunning && this.effectsSDK && !this.isVideoMuted) {
            // The re-initialised effect pipeline comes up black until it re-runs against the
            // new camera. Replicate the mute/unmute recovery (the manual workaround) once the
            // switch has settled, so the switch doesn't leave a black local preview and a
            // muted remote track.
            setTimeout(async () => {
              try {
                this.effectsSDK.stop()
                await this.livekitAdapter.setVideoMuted(true)
                this.effectsSDK.run()
                await this.livekitAdapter.setVideoMuted(false)
              } catch (e) {
                console.warn('LiveKit effect recovery after camera switch failed', e)
              }
            }, 400)
          }
        } else {
          await this.resetEffectsSDK()
        }
        if (previousStream && previousStream !== mediaStream) {
          // Old video was already stopped before getUserMedia (single-camera devices);
          // free the old audio here. Runs for a pre-join swap too, not just a live one.
          previousStream.getAudioTracks().forEach(t => { try { t.stop() } catch (e) { /* noop */ } })
        }
      } catch (e) {
        console.warn('LiveKit effects/track wiring failed (preview continues)', e)
      }

      // Build the local preview element the same way the Erizo path does
      // (webrtc-video class + id) so it sizes correctly.
      const video = this.getVideoTag(this.localStream)

      const settings = newVideoTrack ? newVideoTrack.getSettings() : {}
      const facingMode = settings.facingMode || 'user'

      let isMobileDevice = window.DetectRTC.isMobileDevice
      if (!isMobileDevice) {
        isMobileDevice = /iPad/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
      }

      this.EventBus.$emit('onStreamAccessGranted', {
        'can_switch_camera': isMobileDevice,
        'stream': mediaStream,
        'video': video,
        'mirrored': facingMode === 'user',
        'video_muted': this.isVideoMuted,
        'audio_muted': this.isAudioMuted
      })

      const devices = await navigator.mediaDevices.enumerateDevices()
      this.videoDevices = []
      this.audioDevices = []
      this.outputDevices = []
      for (const device of devices) {
        if (device.kind === 'videoinput') {
          this.videoDevices.push({ 'label': device.label, 'deviceId': device.deviceId })
        } else if (device.kind === 'audioinput') {
          this.audioDevices.push({ 'label': device.label, 'deviceId': device.deviceId })
        } else if (device.kind === 'audiooutput') {
          this.outputDevices.push({ 'label': device.label, 'deviceId': device.deviceId })
        }
      }
      const deviceFeedback = {
        'current_video_input': this.activeVideoDevice,
        'current_audio_input': this.activeAudioInput,
        'current_audio_output': this.activeAudioOutput,
        'devices': this.videoDevices,
        'audio_input': this.audioDevices,
        'audio_output': this.outputDevices
      }
      this.EventBus.$emit('onDeviceList', deviceFeedback)

      if (typeof callback !== 'undefined') {
        callback(this.localStream, deviceFeedback)
      }
    } catch (error) {
      console.error('LiveKit getUserMedia failed', error)
      // Clear the switch flags on every failure so the flip button re-enables.
      this.flipFacingMode = null
      this.isCameraSwitching = false
      // A failed mid-meeting device switch must NOT tear down the live session: the old
      // video was already stopped, but audio + the room connection are still up.
      if (this.livekitAdapter && this.livekitAdapter.isConnected()) {
        return
      }
      this.localStream = null
      this.EventBus.$emit('onStreamAccessDenied', {
        'error': error.message,
        'devices': { 'video_input': [], 'audio_input': [], 'audio_output': [] }
      })
    }
  }

  runningStreams = [];
  getAllStreams () {
    return this.runningStreams
  }

  /*
  publishStream (stream) {
    console.log('PUBLISH', stream)
    stream.toLog = function () { console.log('TO LOG CALLED') }
    this.room.publish(stream, { disableIceRestart: true, forceTurn: true, simulcast: false }, (id, error) => {
      console.log('STREAM PUBLISHED', id, error)
    })
  } */

  startScreenSharing () {
    window.LoggerAPI.addLog('client', new Date(), 'log', 'START_SCREENSHARING', 'Screen sharing starting.')
    if (this.mediaEngine === 'livekit') {
      if (this.livekitAdapter) {
        this.livekitAdapter.startScreenShare()
      }
      return
    }
    // TODO: (MEDIA_SERVER) Erizo screen-share path below — delete with Licode (keep livekit branch).
    if (this.p2p) {
      // eslint-disable-next-line no-undef
      this.localStreamScreen = ErizoV9.Stream({
        screen: true,
        video: true,
        audio: true,
        data: true,
        local: true
      })
    } else {
      // eslint-disable-next-line no-undef
      this.localStreamScreen = Erizo.Stream({
        screen: true,
        video: true,
        data: true,
        local: true
      })
    }

    this.localStreamScreen.addEventListener('bandwidth-alert', (streamEvent) => {
      console.error('THERE IS A BANDWIDTH PROBLEM STREAM SCREEN')
      console.error(streamEvent)
    })
    this.localStreamScreen.addEventListener('access-accepted', (stream) => {
      window.LoggerAPI.addLog('client', new Date(), 'log', 'START_SCREENSHARING_ACCESS_GRANTED', 'Screen sharing access granted.')
      this.localStreamScreen.stream.addEventListener('inactive', (e) => {
        this.stopScreenSharing()
      })
      this.localStreamScreen.stream.addEventListener('ended', (e) => {
        this.stopScreenSharing()
      })
      if (typeof this.localStreamScreen.stream !== 'undefined' && typeof this.localStreamScreen.stream.getVideoTracks() !== 'undefined' && this.localStreamScreen.stream.getVideoTracks().length > 0) {
        this.localStreamScreen.stream.getVideoTracks()[0].addEventListener('ended', (e) => {
          this.stopScreenSharing()
        })
        this.localStreamScreen.stream.getVideoTracks()[0].addEventListener('inactive', (e) => {
          this.stopScreenSharing()
        })
      }
      this.room.publish(this.localStreamScreen, {
        disableIceRestart: true,
        forceTurn: true,
        simulcast: false,
        scheme: 'notify-break-recover'
      }, (id, error) => {
        console.log('Screen shared', id, error)
        this.EventBus.$emit('onStreamAccessGrantedScreen')
      })
    })
    this.localStreamScreen.addEventListener('access-denied', (event) => {
      window.LoggerAPI.addLog('client', new Date(), 'log', 'START_SCREENSHARING_ACCESS_DENIED', 'Screen sharing access denied.')
      this.localStreamScreen = null
      this.EventBus.$emit('onStreamAccessDeniedScreen', {
        'error': event.msg
      })
    })
    this.localStreamScreen.init()
  }

  isAudioMuted = false
  muteAudio () {
    if (this.localStream === null) {
      return
    }
    window.LoggerAPI.addLog('client', new Date(), 'log', 'MUTE_AUDIO', 'Participant muted audio.')
    try {
      this.localStream.muteAudio(true, (result) => { })
    } catch (e) { }

    if (typeof this.localStream.stream !== 'undefined' && this.localStream.stream.getAudioTracks().length > 0) {
      this.localStream.stream.getAudioTracks()[0].enabled = false
    }

    this.isAudioMuted = true
    this.notifyMuteStatusIfChanged({ audioMuted: true, videoMuted: this.isVideoMuted })
    return this.isAudioMuted
  };

  unmuteAudio () {
    if (this.localStream === null) {
      return
    }
    window.LoggerAPI.addLog('client', new Date(), 'log', 'UNMUTE_AUDIO', 'Participant unmuted audio.')

    try {
      this.localStream.muteAudio(false, (result) => { })
    } catch (e) { }

    if (typeof this.localStream.stream !== 'undefined' && this.localStream.stream.getAudioTracks().length > 0) {
      this.localStream.stream.getAudioTracks()[0].enabled = true
    }

    this.isAudioMuted = false
    this.notifyMuteStatusIfChanged({ audioMuted: false, videoMuted: this.isVideoMuted })
    return this.isAudioMuted
  };

  getIsAudioMuted () {
    return this.isAudioMuted
  }
  isVideoMuted = false
  muteVideo () {
    if (this.localStream === null) {
      return
    }
    if (this.isVideoEffectRunning && this.effectsSDK) {
      this.effectsSDK.stop()
    }
    window.LoggerAPI.addLog('client', new Date(), 'log', 'MUTE_VIDEO', 'Participant muted audio.')
    try {
      this.localStream.muteVideo(true, (result) => { })
    } catch (e) { }

    if (typeof this.localStream.stream !== 'undefined' && this.localStream.stream.getVideoTracks().length > 0) {
      this.localStream.stream.getVideoTracks()[0].enabled = false
    }

    this.isVideoMuted = true
    this.notifyMuteStatusIfChanged({ audioMuted: this.isAudioMuted, videoMuted: true })
    return this.isVideoMuted
  };

  unmuteVideo () {
    if (this.localStream === null) {
      return
    }
    if (this.isVideoEffectRunning) {
      this.effectsSDK.run()
    }
    window.LoggerAPI.addLog('client', new Date(), 'log', 'UNMUTE_AUDIO', 'Participant unmuted audio.')

    try {
      this.localStream.muteVideo(false, (result) => { })
    } catch (e) { }

    if (typeof this.localStream.stream !== 'undefined' && this.localStream.stream.getVideoTracks().length > 0) {
      this.localStream.stream.getVideoTracks()[0].enabled = true
    }

    this.isVideoMuted = false
    this.notifyMuteStatusIfChanged({ audioMuted: this.isAudioMuted, videoMuted: false })
    return this.isVideoMuted
  };

  getIsVideoMuted () {
    return this.isVideoMuted
  }

  hasLocalStream () {
    return this.localStream !== null && ((this.localStream.stream && typeof this.localStream.stream.getVideoTracks === 'function' && this.localStream.stream.getVideoTracks().length > 0) || store.getters.getProduct === 'direct')
  }

  getPeerConnection () {
    if (!this.localStream || !this.localStream.pc) {
      return false
    }
    return this.localStream.pc
  }

  pushStream () {
    this.room.publish(this.localStream, {
      disableIceRestart: true,
      forceTurn: true,
      simulcast: false,
      scheme: 'notify-break-recover'
    }, (id, error) => {
      console.log('PUBLISHED STREAM', id, error)
      // Disable this video again if it was muted
      if (this.isAudioMuted) {
        this.muteAudio()
      }
      if (this.isVideoMuted) {
        this.muteVideo()
      }
    })
  }

  room = null
  // TODO: (MEDIA_SERVER) Everything below the livekit guard in this method is the Erizo
  // room setup (publish/subscribe/stream-data/room events). When Licode is removed,
  // delete the Erizo body and keep only setStreamHandlersLivekit (rename it to this).
  setStreamHandlers (roomToken) {
    if (this.mediaEngine === 'livekit') {
      return this.setStreamHandlersLivekit(roomToken)
    }

    // Replace any previous Licode room cleanly so its disconnect cannot end a newer call.
    this.disconnectActiveRoom()

    if (this.p2p) {
      // eslint-disable-next-line no-undef
      this.room = ErizoV9.Room({ token: roomToken })
    } else {
      // eslint-disable-next-line no-undef
      this.room = Erizo.Room({ token: roomToken })
    }
    // Identity of this Licode room for stale error/disconnect guards only.
    // All Licode calls below keep using this.room (existing code pattern).
    const boundRoom = this.room
    let firstParticipantJoined = false
    let subscribeToStreams = (streams) => {
      console.log('Show all streams', streams.length, streams)
      for (let index in streams) {
        let stream = streams[index]
        let attributes = stream.getAttributes()
        let userId = null
        if (store.getters.getUser !== false) {
          userId = store.getters.getUser.id
        } else {
          userId = store.getters.getMeeting.participant.id
        }
        console.log('MY USER ID =', userId)
        if (attributes && attributes.user_id && attributes.user_id === userId) {
          console.log('STREAM IS MY OWN, IGNORE IT')
          continue
        }

        if (this.localStream && this.localStream.getID() !== stream.getID() && (!this.localStreamScreen || this.localStreamScreen.getID() !== stream.getID())) {
          window.LoggerAPI.addLog('client', new Date(), 'log', 'SUBSCRIBE_STREAM', 'Participant subscribed stream ' + stream.getID() + '.')
          console.log('Stream really start subscribing')
          this.room.subscribe(stream, {
            disableIceRestart: true,
            audio: true,
            video: true,
            data: true,
            forceTurn: true
            // startVideoBW: 250,
            // defaultVideoBW: 300,
            // maxVideoBW: 5000,
            // maxAudioBW: 5000,
            // limitMaxVideoBW: 5000,
            // limitMaxAudioBW: 5000
          }, function (result, error) {
            console.log('SUBSCRIBETOSTREAMS', result, error)
            if (typeof result === 'undefined') {
              console.log('Error subscribing to stream', error)
            } else {
              console.log('Stream subscribed!', typeof stream, stream)
            }
          })
        }
      }
    }

    this.room.addEventListener('room-connected', (roomEvent) => {
      window.LoggerAPI.addLog('client', new Date(), 'log', 'ROOM_CONNECTED', 'Participant connected to room.')
      subscribeToStreams(roomEvent.streams)
      this.EventBus.$emit('onRoomConnected')

      this.room.publish(this.localStream, {
        disableIceRestart: true,
        forceTurn: true,
        simulcast: false,
        // startVideoBW: 25000,
        // defaultVideoBW: 50000,
        // maxVideoBW: 50000,
        // maxAudioBW: 50000,
        // limitMaxVideoBW: 50000,
        // limitMaxAudioBW: 50000,
        scheme: 'notify-break-recover'
      }, (id, error) => {
        console.log('PUBLISHED STREAM', id, error)
        // Disable this video again if it was muted
        if (this.isAudioMuted) {
          this.muteAudio()
        }
        if (this.isVideoMuted) {
          this.muteVideo()
        }
        // Emit event that stream is published and ready
        if (!error && id) {
          this.EventBus.$emit('onStreamPublished', { streamId: id })
        }
      })
    })

    this.room.addEventListener('room-error', (roomEvent) => {
      // Ignore errors from a room that is no longer the active one.
      if (this.room !== boundRoom) {
        console.log('Ignoring room-error from stale Licode room')
        return
      }
      console.log('ROOM ERROR', roomEvent)
      window.LoggerAPI.addLog('client', new Date(), 'log', 'ROOM_ERROR', 'Room error.')
      this.endCall()
    })

    this.room.addEventListener('room-disconnected', (roomEvent) => {
      console.log('ROOM DISCONNECTED', roomEvent)
      window.LoggerAPI.addLog('client', new Date(), 'log', 'ROOM_DISCONNECTED', 'Room disconnected.')
      // Only the active room may end the call. Intentional teardown nulls this.room first;
      // a replaced room's late disconnect also fails this check.
      if (this.room !== boundRoom) {
        console.log('Ignoring room-disconnected from non-active Licode room')
        return
      }
      this.room = null
      if (this.isEndingCall || roomEvent.message === 'expected-disconnection') {
        return
      }
      this.endCall()
    })

    this.room.addEventListener('stream-unsubscribed', (streamEvent) => {
      console.log('STREAM UNSUBSCRIBED', streamEvent)
      window.LoggerAPI.addLog('client', new Date(), 'log', 'SUBSCRIBE_STREAM', 'Participant unsubscribed stream ' + streamEvent.stream.getID() + '.')
    })

    this.room.addEventListener('stream-subscribed', async (streamEvent) => {
      // Should never happen, but ignore subscribes to your own stream
      console.log('STREAM SUBSCRIBED', streamEvent)
      let stream = streamEvent.stream
      if (stream.local === true || (this.localStream && stream.getID() === this.localStream.getID())) {
        console.log(this.localStream.pc.peerConnection)
        console.log('UNSUBSCRIBE MY OWN STREAM')
        this.room.unsubscribe(stream)
        return
      }

      // Defensive check: ensure stream.stream is valid before using it
      if ((!stream.stream || typeof stream.stream.getTracks !== 'function') && store.getters.getProduct !== 'direct') {
        console.warn('STREAM SUBSCRIBE: Invalid stream.stream object, stream may not be ready yet', stream)
        // Try to wait a bit and retry, or handle gracefully
        let timerId = setTimeout(() => {
          if (this.room && stream.stream && typeof stream.stream.getTracks === 'function') {
            console.log('STREAM SUBSCRIBE: Stream is now valid, retrying subscription')
            let streams = []
            streams.push(stream)
            subscribeToStreams(streams)
          }
        }, 500)
        this.streamRetryTimeouts.add(timerId)
        return
      }

      let video = document.createElement('video')
      video.setAttribute('autoplay', 'autoplay')
      video.setAttribute('playsinline', 'playsinline')
      video.setAttribute('muted', 'muted')
      video.classList.add('remote_video')
      video.muted = true
      video.volume = 0
      video.srcObject = stream.stream

      console.log('Created video element')

      this.runningStreams[stream.getID()] = stream.stream

      console.log('Added stream to running streams array')

      if (this.recorder !== null) {
        console.log('Recorder is active, add the video to the recorder')
        this.addVideoToRecorder(stream.stream)
      }
      if (!stream.screen) {
        const attrs = typeof stream.getAttributes === 'function' ? stream.getAttributes() : {}
        this.notifyJoinerOfActiveRecording(attrs && attrs.user_id)
      }

      if (!firstParticipantJoined) {
        firstParticipantJoined = true
      }

      if (stream.screen) {
        console.log('The subscribed stream is a ScreenSharing stream')
        video.classList.add('webrtc_screen')
        video.classList.add('webrtc_screen_' + stream.getID())
        video.id = stream.getID()
        if (store.getters.getProduct !== 'direct') {
          video.muted = false
          video.volume = 1
        }

        this.EventBus.$emit('onAddScreen', {
          'type': 'remote',
          'video': video
        })
      } else if (stream.video || store.getters.getProduct === 'direct') {
        console.log('The subscribed stream is a Video stream (or data-only for direct)')
        video.classList.add('webrtc-video')
        video.classList.add('webrtc_video_' + stream.getID())
        video.classList.add('mx-auto')
        video.id = stream.getID()
        /*
        if (store.getters.getProduct === 'direct') {
          video.muted = true
          video.volume = 0
        } else {
          video.muted = false
          video.volume = 1
        }
        */
        video.muted = true
        video.volume = 0

        if (typeof video.sinkId !== 'undefined' && this.activeAudioOutput) {
          this.attachSinkId(video, this.activeAudioOutput)
        }

        console.log('Resulting video tag', video)

        if (this.isVideoEffectRunning) {
          this.reloadEffectsForRemoteParticipants()
        }
        let attrs = stream.getAttributes()
        console.log('Stream attributes', attrs)

        let realName = typeof attrs !== 'undefined' && typeof attrs.real_name !== 'undefined' ? attrs.real_name : null
        if (realName === 'Bezoeker') {
          realName = i18n.global.t('MeetingRoom.Visitor')
        }
        this.EventBus.$emit('onAddVideo', {
          'stream': stream,
          'type': 'remote',
          'video': video,
          'real_name': realName,
          'id': typeof attrs !== 'undefined' && typeof attrs.user_id !== 'undefined' ? attrs.user_id : null
        })
        console.log('ADD VIDEO FIRED')
      }

      stream.addEventListener('stream-data', (evt) => {
        this.routeDataMessage(evt.msg, (stream && typeof stream.getAttributes === 'function') ? stream.getAttributes() : {})
      })
    })

    this.room.addEventListener('stream-attributes-update', (streamEvent) => {
      console.log('STREAM ATTRIBUTES UPDATED', streamEvent)
      // Ignored
    })

    this.room.addEventListener('stream-added', (streamEvent) => {
      console.log('STREAM ADDED', streamEvent, streamEvent.stream.getAttributes())
      let streams = []
      streams.push(streamEvent.stream)
      subscribeToStreams(streams)
    })

    this.room.addEventListener('stream-removed', (streamEvent) => {
      console.log('STREAM REMOVED', streamEvent)
      let stream = streamEvent.stream

      // Defensive check: ensure stream is valid before accessing its properties
      if (!stream || typeof stream.getID !== 'function') {
        console.warn('STREAM REMOVED: Invalid stream object received', streamEvent)
        return
      }

      try {
        const streamId = stream.getID()
        delete this.runningStreams[streamId]
        if (!stream.screen && typeof stream.getAttributes === 'function') {
          const attrs = stream.getAttributes() || {}
          this.scheduleForgetRemoteIdentity(attrs.user_id)
        }

        if (this.recorder !== null) {
          let streams = []
          if (this.localStream && this.localStream.stream) {
            streams.push(this.localStream.stream)
          }
          let allRemoteStreams = this.getAllStreams()
          Object.keys(allRemoteStreams).forEach(function (stream) {
            if (allRemoteStreams[stream]) {
              streams.push(allRemoteStreams[stream])
            }
          })
          this.recorder.getInternalRecorder().resetVideoStreams(streams)
        }

        if (stream.screen) {
          this.EventBus.$emit('onRemoveScreen', {
            'stream_id': streamId
          })
        } else {
          this.EventBus.$emit('onRemoveVideo', {
            'stream_id': streamId
          })
        }
      } catch (error) {
        console.error('Error handling stream-removed event:', error, streamEvent)
      }
    })

    this.room.addEventListener('stream-failed', (streamEvent) => {
      console.log('STREAM FAILED', streamEvent)
      window.LoggerAPI.addLog('client', new Date(), 'log', 'FAILED_STREAM', 'Participant encountered a failed stream ' + streamEvent.stream.getID() + '.')
      // var stream = streamEvent.stream
      /* this.EventBus.$emit('onRemoveVideo', {
        'stream_id': stream.getID()
      }) */
      /* if (this.localStream && this.localStream.getID() === stream.getID()) {
        this.room.publish(this.localStream, { forceTurn: true }, () => {
          if (this.isAudioMuted) {
            this.muteAudio()
          }
          if (this.isVideoMuted) {
            this.muteVideo()
          }
        })
      } else if (this.localStreamScreen && this.localStreamScreen.getID() === stream.getID()) {
        this.room.publish(this.localStreamScreen, { forceTurn: true })
      } else {
        console.log('A REMOTE STREAM HAD FAILED, ORIGIN:', streamEvent.origin)
        this.EventBus.$emit('onRemoveVideo', {
          'stream_id': stream.getID()
        })
      } */
    })

    this.room.addEventListener('stream-ended', (streamEvent) => {
      console.log('STREAM ENDED', streamEvent)
      window.LoggerAPI.addLog('client', new Date(), 'log', 'ENDED_STREAM', 'A remote stream ended: ' + streamEvent.stream.getID() + '.')
      var stream = streamEvent.stream
      if (stream.screen) {
        this.EventBus.$emit('onRemoveScreen', {
          'stream_id': stream.getID()
        })
      } else {
        this.EventBus.$emit('onRemoveVideo', {
          'stream_id': stream.getID()
        })
      }
    })

    this.room.addEventListener('connection-failed', function () {
      console.log('CONNECTION FAILED')
    })

    this.room.addEventListener('ice-state-changed', function (event) {
      console.log('ICE CHANGE', event)
    })

    this.room.connect({ singlePC: true })
  }

  // LiveKit equivalent of setStreamHandlers: connect via the adapter and publish
  // the already-captured local MediaStream. Remote streams and lifecycle are
  // mapped onto the same EventBus events inside the adapter.
  async setStreamHandlersLivekit (roomToken) {
    await ensureLivekitAdapter()
    this.livekitAdapter = new LivekitAdapter(this.EventBus, {
      onData: (decoded, participant) => this.handleLivekitData(decoded, participant),
      onRemoteStreamAdded: (streamId, mediaStream) => this.registerRemoteStreamForRecording(streamId, mediaStream),
      onRemoteStreamRemoved: (streamId) => this.unregisterRemoteStreamForRecording(streamId),
      onParticipantCountChanged: (count) => this.adjustLivekitCaptureForCount(count),
      // ParticipantActive is when they can send/receive data; Connected is too early.
      onParticipantConnected: (participant) => this.notifyJoinerOfActiveRecording(participant && participant.identity),
      onParticipantDisconnected: (participant) => this.forgetRemoteIdentity(participant && participant.identity),
      // Route new remote elements to the selected output device, like Licode (attachSinkId).
      getAudioOutput: () => this.activeAudioOutput
    })
    const localMediaStream = this.localStream ? this.localStream.stream : null
    // TODO: (MEDIA_SERVER) The preview stream is still an Erizo.Stream, so route its
    // data/mute through the adapter (else chat/whiteboard/mute hit a dead Erizo channel).
    // Drop this shim once Licode is gone and we capture natively.
    if (this.localStream) {
      const adapter = this.livekitAdapter
      this.localStream.sendData = (obj) => adapter.sendData(obj)
      this.localStream.muteAudio = (mute, cb) => {
        adapter.setAudioMuted(mute)
        const ok = true
        if (typeof cb === 'function') cb(ok)
      }
      this.localStream.muteVideo = (mute, cb) => {
        adapter.setVideoMuted(mute)
        const ok = true
        if (typeof cb === 'function') cb(ok)
      }
    }
    this.connectLivekit(localMediaStream, roomToken)
  }

  // Bump the preview track to 720p before publishing; simulcast can only build
  // layers at/below the source, and the preview may have been captured lower.
  async connectLivekit (localMediaStream, roomToken) {
    try {
      await this.setLocalCaptureResolution(1280, 720)
      await this.livekitAdapter.connect(roomToken, localMediaStream)
      this.notifyExistingLivekitParticipants()
    } catch (error) {
      console.error('LiveKit connect failed', error)
      // Carry the SDK's message: it names the transport and the ICE state it gave up in,
      // which is what separates a blocked media path from a signalling or permission fault.
      window.LoggerAPI.addLog('client', new Date(), 'error', 'ROOM_ERROR', 'LiveKit room connect failed: ' + ((error && error.message) || error))
      // Hanging up during the attempt aborts it and rejects here too. That is the user's
      // own doing, so it must not be reported as a connection problem.
      if (this.isEndingCall) {
        return
      }
      // Only a room that never reached Connected failed to connect. This block also covers
      // publish/notify errors on a live room; those end the call but a conversation did
      // happen, so the feedback form and the wording below still apply to it.
      if (!this.livekitAdapter || !this.livekitAdapter.isConnected()) {
        // Read by MeetingRoom.onEndCall: this call never started, so no feedback form.
        this.roomConnectFailed = true
        this.EventBus.$emit('onRoomConnectFailed', { message: (error && error.message) || String(error) })
      }
      this.endCall()
    }
  }

  // Set the local capture resolution reliably (LiveKit). Lowering uses
  // applyConstraints (seamless); RAISING re-captures via getUserMedia because
  // applyConstraints with `ideal` does not reliably upscale an already-running track.
  async setLocalCaptureResolution (width, height) {
    if (this.mediaEngine !== 'livekit' || !this.localStream || !this.localStream.stream) {
      return
    }
    const track = this.localStream.stream.getVideoTracks()[0]
    if (!track) {
      return
    }
    const cur = track.getSettings() || {}
    if (cur.height === height) {
      return
    }
    if ((cur.height || 0) < height) {
      // RAISE via re-capture. Under an active video effect, localStream.stream holds the
      // effects-SDK INPUT camera, NOT the published canvas. Re-capturing + track.stop() here
      // freezes/blacks the canvas (input ended) and clobbers the published effect track with
      // the raw camera (dropping the background). Skip the raise; the canvas keeps running on
      // the still-live input. (Lowering below is safe — applyConstraints never stops the track.)
      // Digital zoom has the same shape (canvas drawn from the preview this track feeds).
      if (this.isVideoEffectRunning || this.zoomFeatureActive) {
        return
      }
      // RAISE: re-capture, swap the published track first, then stop the old one.
      const newTrack = await this.captureVideoTrack(track, width, height)
      if (!newTrack) {
        return
      }
      if (this.livekitAdapter && this.livekitAdapter.isConnected()) {
        await this.livekitAdapter.replaceVideoTrack(newTrack)
      }
      this.localStream.stream.removeTrack(track)
      this.localStream.stream.addTrack(newTrack)
      try { track.stop() } catch (e) { /* noop */ }
    } else {
      await track.applyConstraints({
        width: { ideal: width },
        height: { ideal: height },
        frameRate: { ideal: 30 }
      }).catch((e) => { console.warn('LiveKit: could not lower capture resolution', e) })
    }
  }

  // getUserMedia a fresh video track at a target resolution (same camera as the
  // current track). Does not touch the existing stream — caller orchestrates the swap.
  async captureVideoTrack (currentTrack, width, height) {
    const deviceId = currentTrack ? currentTrack.getSettings().deviceId : null
    const videoConstraints = {
      width: { ideal: width },
      height: { ideal: height },
      frameRate: { ideal: 30 }
    }
    if (deviceId) {
      videoConstraints.deviceId = { exact: deviceId }
    }
    const fresh = await navigator.mediaDevices.getUserMedia({ video: videoConstraints }).catch((e) => {
      console.warn('LiveKit: re-capture at target resolution failed', e)
      return null
    })
    return fresh ? this.registerAcquiredTracks(fresh).getVideoTracks()[0] : null
  }

  // LiveKit data-channel messages are routed through the same switch as Erizo's
  // stream-data, with the sender attributes derived from participant metadata.
  handleLivekitData (decoded, participant) {
    let attributes = {}
    try {
      if (participant && participant.metadata) {
        const meta = JSON.parse(participant.metadata)
        attributes = { user_id: meta.user_id, real_name: meta.real_name }
      }
    } catch (e) { /* metadata optional */ }
    // Name and metadata come from the server-signed token, unlike the payload's `sender`.
    if (participant) {
      attributes.username = participant.name
      attributes.signed = true
    }
    this.routeDataMessage(decoded, attributes)
  }

  // Debug stats overlay (Tier 2): per-stream LiveKit WebRTC stats. LiveKit only.
  async getLivekitStats () {
    if (this.mediaEngine !== 'livekit' || !this.livekitAdapter) {
      return null
    }
    return this.livekitAdapter.collectStats()
  }

  // Capture tier by live participant count: cap the source as more join so the
  // encoder/bandwidth don't get overwhelmed (simulcast derives layers from it).
  getLivekitCaptureResolution (count) {
    if (count <= 2) {
      return { width: 1280, height: 720 } // 1:1 — full HD
    } else if (count <= 4) {
      return { width: 1280, height: 720 } // small group — keep HD
    } else if (count <= 8) {
      return { width: 960, height: 540 }
    }
    // 9+: floor at 360p, don't scale into the ground. Real per-tile downscaling
    // comes with the adaptiveStream refactor (TODO: (MEDIA_SERVER) in the adapter).
    return { width: 640, height: 360 }
  }

  // Re-scale the local capture to match the live participant count (LiveKit only).
  // Uses applyConstraints so there is no re-getUserMedia / renegotiation.
  adjustLivekitCaptureForCount (count) {
    const target = this.getLivekitCaptureResolution(count)
    // setLocalCaptureResolution lowers via applyConstraints and raises via re-capture.
    this.setLocalCaptureResolution(target.width, target.height)
  }

  // Keep runningStreams in sync with LiveKit remote streams so client-side
  // recording (RecordRTC) composes local + remote, mirroring the Erizo path.
  registerRemoteStreamForRecording (streamId, mediaStream) {
    this.runningStreams[streamId] = mediaStream
    if (this.recorder !== null) {
      this.addVideoToRecorder(mediaStream)
    }
  }

  unregisterRemoteStreamForRecording (streamId) {
    delete this.runningStreams[streamId]
    if (this.recorder !== null) {
      let streams = []
      if (this.localStream && this.localStream.stream) {
        streams.push(this.localStream.stream)
      }
      const allRemoteStreams = this.getAllStreams()
      Object.keys(allRemoteStreams).forEach((id) => {
        if (allRemoteStreams[id]) {
          streams.push(allRemoteStreams[id])
        }
      })
      this.recorder.getInternalRecorder().resetVideoStreams(streams)
    }
  }

  // Engine-agnostic routing for an inbound data-channel message. `attributes` is
  // the sender's { user_id, real_name } (from Erizo stream attributes or LiveKit
  // participant metadata).
  routeDataMessage (data, attributes = {}) {
    if (!data || typeof data !== 'object') {
      return
    }
    // On LiveKit the payload's `sender` is free text any participant can set; show the
    // signed token name instead so nobody can pose as someone else.
    let sender = typeof data.sender === 'string' ? data.sender : ''
    if (attributes.signed) {
      // Direct visitors have no name, only a 'visitor.' username (mapped below).
      sender = [attributes.real_name, attributes.username].find(n => typeof n === 'string' && n !== '') || ''
    }
    if (sender.indexOf('visitor.') > -1 || sender === 'Bezoeker') {
      sender = i18n.global.t('MeetingRoom.Visitor')
    }

    // We have a chat message
    if (data.type === 'chat') {
      this.EventBus.$emit('onChatMessage', {
        'sender': sender,
        'message': data.content,
        'direction': 'in',
        'datetime': data.datetime,
        'type': 'chat'
      })
    } else if (data.type === 'popup_digid') {
      this.EventBus.$emit('onShowPopupDigid', {
        'sender': sender,
        'type': 'popup_digid'
      })
    } else if (data.type === 'digid_process') {
      this.EventBus.$emit('ongenerateIdentificationProcess', {
        'sender': sender,
        'type': 'digid_process'
      })
    } else if (data.type === 'filepart') {
      if (data.dataChunk.first) {
        this.fileChunks = [] // Reset the array just in case

        let now = new Date()
        this.EventBus.$emit('onFileStarted', {
          'type': 'file',
          'sentByMe': false,
          'id': data.dataChunk.uuid,
          'fileName': data.dataChunk.fileName,
          'fileSize': this.bytesToSize(data.dataChunk.fileSizeReal),
          'progress': 0,
          'sender': sender,
          'datetime': now,
          'direction': 'in'
        })
      }

      this.EventBus.$emit('onFileProgress', {
        'id': data.dataChunk.uuid,
        'progress': data.progress > 100 ? 100 : parseFloat(data.progress).toFixed(2),
        'sender': sender,
        'direction': 'in'
      })

      // Push the file chunk to the chunks array
      this.fileChunks.push(data.dataChunk.message)

      if (data.dataChunk.last) {
        // Join the chunk array so it becomes one file again
        let url = this.fileChunks.join('')
        this.EventBus.$emit('onFileCompleted', {
          'id': data.dataChunk.uuid,
          'fileURL': url,
          'fileName': data.dataChunk.fileName,
          'sender': sender,
          'direction': 'in'
        })

        this.fileChunks = [] // resetting array since we're done
      }
    } else if (data.type === 'end_presentation') {
      this.abandonPresentationTransfers()
      this.EventBus.$emit('onPresentationEnded')
    } else if (data.type === 'pdf_bin') {
      this.collectPresentationChunk(data)
    } else if (data.type === 'pdf') {
      // A chunked presentation carries no content here; hold the envelope until the binary
      // parts arrive. They can overtake it, so either may open the buffer.
      if (data.pdf_bin_total > 0) {
        const buffer = this.presentationBuffer(data.pdf_chunk_id, data.pdf_bin_total)
        buffer.envelope = data
        buffer.sender = sender
        buffer.mime = data.pdf_bin_mime
        this.storePresentationPart(data.pdf_chunk_id, buffer, -1, null)
          .catch(err => console.error('[WebRTC] presentation assembly failed:', err))
        return
      }
      this.emitPresentationReceived(data, data.content, sender)
    } else if (data.type === 'screenshot_taken') {
      this.EventBus.$emit('onScreenshotTaken', {
        'sender': sender,
        'datetime': data.datetime
      })
    } else if (data.type === 'start_recording') {
      if (!this.isRecordingNoticeForLocalUser(data)) {
        return
      }
      this.EventBus.$emit('onStartRecording', {
        'sender': sender
      })
    } else if (data.type === 'stop_recording') {
      this.EventBus.$emit('onStopRecording', {
        'sender': sender
      })
    } else if (data.type === 'stop_scriptix_recording') {
      this.EventBus.$emit('onStopScriptixRecording', {
        'sender': sender
      })
    } else if (data.type === 'start_scriptix_recording') {
      if (!this.isRecordingNoticeForLocalUser(data)) {
        return
      }
      this.EventBus.$emit('onStartScriptixRecording', {
        'sender': sender
      })
    } else if (data.type === 'orientation_changed') {
      this.EventBus.$emit('onRemoteOrientationChanged', {
        'sender': sender,
        'orientation': data.orientation
      })
    } else if (data.type === 'request_presentation_sync') {
      this.EventBus.$emit('onPresentationSyncRequested')
    } else if (data.type === 'sync_presentation') {
      this.EventBus.$emit('onPresentationSync', {
        'sender': sender,
        'offsetX': data.offsetX,
        'offsetY': data.offsetY,
        'scale': data.scale,
        'drawings': data.drawings,
        'width': data.width
      })
    } else if (data.type === 'raiseOrLowerHand') {
      this.EventBus.$emit('onRaiseOrLowerHand', {
        'senderId': data.senderId,
        'sender': sender,
        'raiseHand': data.raiseHand
      })
    } else if (data.type === 'annotation') {
      let annotation
      try {
        annotation = JSON.parse(data.annotation)
      } catch (e) {
        console.warn('[WebRTC] ignored malformed annotation')
        return
      }
      this.EventBus.$emit('annotation', annotation)
    } else if (data.type === 'republish_stream') {
      this.EventBus.$emit('onRepublishStream')
    } else if (data.type === 'participant_location') {
      const userId = data.senderId || attributes.user_id
      if (userId === null || typeof userId === 'undefined' || userId === '') {
        return
      }
      this.EventBus.$emit('onParticipantLocationShared', {
        userId,
        latitude: data.latitude,
        longitude: data.longitude,
        datetime: data.datetime,
        sender
      })
    } else if (data.type === 'participant_location_request') {
      this.EventBus.$emit('onParticipantLocationRequest', {
        senderId: data.senderId,
        sender,
        datetime: data.datetime
      })
    } else if (data.type === 'participant_location_request_response') {
      this.EventBus.$emit('onParticipantLocationRequestResponse', {
        senderId: data.senderId,
        sender,
        accepted: data.accepted === true,
        datetime: data.datetime
      })
    } else if (data.type === 'participant_location_stop') {
      const userId = data.senderId || attributes.user_id
      if (userId === null || typeof userId === 'undefined' || userId === '') {
        return
      }
      this.EventBus.$emit('onParticipantLocationStopped', {
        userId,
        sender,
        datetime: data.datetime
      })
    } else if (data.type === 'participant_location_stop_request') {
      this.EventBus.$emit('onParticipantLocationStopRequest', {
        senderId: data.senderId,
        sender,
        datetime: data.datetime
      })
    }
  }

  async openOrJoin (roomIdToJoin, roomToken, product, isTwoWay) {
    this.isCameraSwitching = false
    this.activateCall(roomIdToJoin)
    console.log('OPENORJOIN', roomIdToJoin, roomToken, product, isTwoWay)

    let audioMuted = false
    let videoMuted = false
    // If we have no stream (which is currently only possible using the autologin/1 URL which will be removed later on), request it now
    // TODO: Remove when the entire "if/else" block here when old API is phased out and simply always this.setStreamHandlers(roomToken) here
    if (!this.hasLocalStream()) {
      console.log('No LocalStream found, start it')
      let cameraInputDevice = null
      if (store.getters.getCamSettings.device && store.getters.getCamSettings.device !== '') {
        cameraInputDevice = store.getters.getCamSettings.device
      }
      let audioInputDevice = null
      if (store.getters.getCamSettings.device && store.getters.getCamSettings.audio_input !== '') {
        audioInputDevice = store.getters.getCamSettings.audio_input
      }
      let audioOutputDevice = null
      if (store.getters.getCamSettings.device && store.getters.getCamSettings.audio_output !== '') {
        audioOutputDevice = store.getters.getCamSettings.audio_output
      }

      if (store.getters.getCamSettings.audio_input_on === false) {
        audioMuted = true
      }

      if (store.getters.getCamSettings.device_on === false) {
        videoMuted = true
      }

      console.log('AUDIO MUTED?2', audioMuted)
      console.log('VIDEO MUTED?2', videoMuted)

      this.startLocalStreams(product, isTwoWay, async () => {
        console.log('LocalStream is started')
        this.setStreamHandlers(roomToken)
      }, cameraInputDevice, audioInputDevice, audioOutputDevice, videoMuted, audioMuted)
    } else {
      console.log('LocalStream found already, setStreamHandlers')
      this.setStreamHandlers(roomToken)
    }
    // we use a time out to allow users properly settle in the meeting before requesting and broadcasting mute stats
    setTimeout(() => {
      try {
        console.log('[Timeout] Triggered for mute state broadcast')

        const userId = this.getUserIdFromStream(this.localStream)
        const roomId = store.getters.getMeeting.room_id

        if (!userId || !roomId) {
          console.warn('Missing userId or roomId', { userId, roomId })
          return
        }
        const userIdentifier = store.getters.getUser?.name || 'visitor'
        // Tell the group your status
        this.notifyMuteStatusIfChanged({
          audioMuted: this.isAudioMuted,
          videoMuted: this.isVideoMuted,
          description: 'Initial mute status',
          requestMapBack: true,
          isAgent: userIdentifier !== 'visitor',
          force: true
        })
      } catch (err) {
        console.error('Error in setTimeout for mute broadcast:', err)
      }
    }, 2000)
  }

  join (roomId, product, streamBothWays, isAgent) {
    // Mark active before Licode tokens arrive so leave during join is handled,
    // while leave during Direct wait (no roomId) is ignored.
    this.activateCall(roomId)

    if (typeof streamBothWays === 'undefined') {
      streamBothWays = true
    }
    if (!this.hasLocalStream()) {
      let cameraInputDevice = null
      if (store.getters.getCamSettings.device && store.getters.getCamSettings.device !== '') {
        cameraInputDevice = store.getters.getCamSettings.device
      }
      let audioInputDevice = null
      if (store.getters.getCamSettings.device && store.getters.getCamSettings.audio_input !== '') {
        audioInputDevice = store.getters.getCamSettings.audio_input
      }
      let audioOutputDevice = null
      if (store.getters.getCamSettings.device && store.getters.getCamSettings.audio_output !== '') {
        audioOutputDevice = store.getters.getCamSettings.audio_output
      }

      let audioMuted = false
      if (store.getters.getCamSettings.audio_input_on === false) {
        audioMuted = true
      }

      let videoMuted = false
      if (store.getters.getCamSettings.device_on === false) {
        videoMuted = true
      }

      console.log(store.getters.getCamSettings)
      console.log('AUDIO MUTED?', audioMuted)
      console.log('VIDEO MUTED?', videoMuted)

      this.startLocalStreams(product, streamBothWays, () => {
        this.initializeRnnoise()
        this.connection.emit('join room', roomId)
      }, cameraInputDevice, audioInputDevice, audioOutputDevice, videoMuted, audioMuted)
    } else {
      this.initializeRnnoise()
      this.connection.emit('join room', roomId)
    }

    setTimeout(() => {
      try {
        const userId = this.getUserIdFromStream(this.localStream)
        const targetRoomId = roomId || (store.getters.getMeeting && store.getters.getMeeting.room_id)

        if (!userId || !targetRoomId) {
          return
        }
        const userIdentifier = store.getters.getUser?.name || 'visitor'
        this.notifyMuteStatusIfChanged({
          audioMuted: this.isAudioMuted,
          videoMuted: this.isVideoMuted,
          description: 'Initial mute status',
          requestMapBack: true,
          isAgent: userIdentifier !== 'visitor',
          force: true
        })
      } catch (err) {
        console.error('Error in setTimeout for mute broadcast from join():', err)
      }
    }, 2000)
  }

  decline (roomId) {
    window.LoggerAPI.addLog('client', new Date(), 'log', 'STOP_STREAMS', 'Participant wants to decline the call.')
    this.connection.emit('decline', roomId)
  }

  hold (roomId) {
    window.LoggerAPI.addLog('client', new Date(), 'log', 'HOLD_CALL', 'Participant wants to hold the call.')
    this.connection.emit('hold call', roomId)
  };

  takeOver (meetingId, newAgentId) {
    return new Promise((resolve, reject) => {
      if (!this.connection) {
        reject(new Error('Not connected'))
        return
      }
      // Without a timeout, a disconnect leaves this ack pending and the waiting room stays on Starting.
      this.connection.timeout(10000).emit('meeting_takeover', meetingId, newAgentId, (err) => {
        if (err) {
          reject(err)
          return
        }
        resolve()
      })
    })
  };

  getVideoDimension (element) {
    if (element.nodeName === 'VIDEO') {
      return {
        width: element.videoWidth,
        height: element.videoHeight
      }
    }
    // get the video child from the element
    const video = element.querySelector('video')
    return this.getVideoDimension(video)
  }

  /**
   * this function appends the annotation canvas to the screenshot canvas.
   * It draws the annotation canvas on the screenshot canvas at the last X position where the annotation was drawn.
   * It uses the canvasOptions object to get the context of the canvas where the screenshot will be drawn,
   * the annotation canvas, the last X position where the annotation was drawn,
   * and the video element where the annotation was drawn.
   * @param {*} videoId
   * @param {*} canvasOptions this contains the canvas context and the annotationCanvas
   * @param {*} canvasOptions.context the context of the canvas where the screenshot will be drawn
   * @param {*} canvasOptions.lastX the last X position where the annotation was drawn
   * @param {*} canvasOptions.videoElement the video element where the annotation was drawn
   * @returns  void
   */
  appendAnnotationToScreenshot (videoId, canvasOptions = {}) {
    if (!videoId) {
      return
    }

    const annotationCanvas = document.querySelector(`canvas[id^="annotationCanvas-${videoId}"]`)
    const { context, videoElement } = canvasOptions
    if (annotationCanvas && canvasOptions) {
      if (context && videoElement) {
        context.drawImage(annotationCanvas, canvasOptions.lastX || 0, 0, canvasOptions.videoElement.clientWidth || 0, canvasOptions.videoElement.clientHeight || 0)
      }
    }
  }
  makeScreenshot (element) {
    let canvas
    if (element.nodeName === 'VIDEO') {
      canvas = document.createElement('canvas')
      canvas.width = element.videoWidth
      canvas.height = element.videoHeight
      const ctx = canvas.getContext('2d')
      ctx.drawImage(element, 0, 0, canvas.width, canvas.height)
      this.appendAnnotationToScreenshot(element?.id, {
        context: ctx,
        lastX: 0,
        videoElement: element
      })
    } else {
      let cWidth = 0
      let cHeight = 0
      for (let videoElement of element.querySelectorAll('video')) {
        cWidth += videoElement.clientWidth
        if (videoElement.clientHeight > cHeight) {
          cHeight = videoElement.clientHeight
        }
      }

      // create a canvas with required dimensions
      let lastX = 0
      canvas = document.createElement('canvas')
      canvas.width = cWidth
      canvas.height = cHeight
      let ctx = canvas.getContext('2d')

      for (let videoElement of element.querySelectorAll('video')) {
        ctx.drawImage(videoElement, lastX, 0, videoElement.clientWidth, videoElement.clientHeight)
        this.appendAnnotationToScreenshot(videoElement?.id, {
          context: ctx,
          lastX,
          videoElement
        })
        lastX += videoElement.clientWidth
      }
    }
    return canvas
  }

  notifyScreenshotTaken () {
    if (!this.localStream) {
      return
    }
    this.localStream.sendData({
      'sender': this.getRealNameFromStream(this.localStream),
      'type': 'screenshot_taken',
      'datetime': new Date()
    })
  }

  takeScreenshot (element) {
    let canvas = this.makeScreenshot(element)

    let now = new Date().toISOString()
    canvas.toBlob(function (blob) {
      saveAs(blob, now + ' - Screenshot.png')
    }, 'image/png')
  }

  recorder = null
  recordingFileName = ''
  /** Flagmint experiment: RecordRTC timeSlice ms. 0 = control (omit option). */
  recordingTimeSliceMs = 0
  /** Recording path label for telemetry: agent | scriptix | auto */
  recordingSource = 'agent'
  /**
   * Where the recording goes after stop (for Esmee / Flagmint extras):
   * local | osaw | scriptix | bumicom | smartintake | unknown
   */
  recordingDestination = 'local'
  recordingStartTime = null
  /** Size-budget state; only active for destinations with a known upload cap. */
  recordingSizeLimit = null
  recordingSizeInterval = null
  recordedBytes = 0
  recordingSizeWarningsSent = []
  recordingEstimatedBytesPerSecond = TRANSCRIPTION_AUDIO_BITS_PER_SECOND / 8

  /**
   * Apply Flagmint multivariate timeSlice before starting a recorder.
   * @param {{ timeSliceMs?: number, source?: string, destination?: string, smartIntakeChunkedUpload?: boolean }} config
   */
  setRecordingExperimentConfig (config = {}) {
    const ms = Number(config.timeSliceMs)
    this.recordingTimeSliceMs = Number.isFinite(ms) && ms > 0 ? ms : 0
    if (config.source) {
      this.recordingSource = config.source
    }
    if (config.destination) {
      this.recordingDestination = config.destination
    }
    if (typeof config.smartIntakeChunkedUpload === 'boolean') {
      this.smartIntakeChunkedUpload = config.smartIntakeChunkedUpload
    }
  }

  buildRecordingExperimentExtra (overrides = {}) {
    const ctx = clientContext()
    return {
      timeSliceMs: this.recordingTimeSliceMs || 0,
      durationMs: this.recordingStartTime ? (Date.now() - this.recordingStartTime) : 0,
      blobSize: overrides.blobSize ?? null,
      browser: ctx.browser,
      os: ctx.os,
      device: ctx.device,
      source: this.recordingSource || 'agent',
      destination: this.recordingDestination || 'unknown',
      smartIntakeChunkedUpload: this.smartIntakeChunkedUpload === true,
      ...overrides
    }
  }

  /**
   * Watch the recording against its destination's upload limit.
   *
   * Size, not duration, is what the transcription services reject, and the byte rate
   * varies with codec, silence and browser. So the budget is driven by the bytes we
   * actually produce, falling back to the configured bitrate when RecordRTC gives us no
   * chunks (no timeSlice). Destinations without a known cap are not watched at all.
   */
  startRecordingSizeBudget (options = {}) {
    this.stopRecordingSizeBudget()

    let limit = RECORDING_SIZE_LIMITS[this.recordingDestination] ?? null
    if (this.recordingDestination === 'smartintake') {
      limit = this.smartIntakeChunkedUpload
        ? SMARTINTAKE_MAX_FILE_BYTES
        : SMARTINTAKE_SINGLE_FILE_MAX_BYTES
    }
    if (!limit) {
      return
    }

    this.recordingSizeLimit = limit
    this.recordedBytes = 0
    this.recordingSizeWarningsSent = []
    // Bits per second the recorder was asked for, used until real chunks arrive.
    this.recordingEstimatedBytesPerSecond =
      (options.audioBitsPerSecond || options.videoBitsPerSecond || TRANSCRIPTION_AUDIO_BITS_PER_SECOND) / 8

    this.recordingSizeInterval = setInterval(() => this.checkRecordingSizeBudget(), 10000)
  }

  stopRecordingSizeBudget () {
    if (this.recordingSizeInterval) {
      clearInterval(this.recordingSizeInterval)
      this.recordingSizeInterval = null
    }
    this.recordingSizeLimit = null
    this.recordedBytes = 0
    this.recordingSizeWarningsSent = []
  }

  /** RecordRTC hands us each timeSlice chunk; their sum is the real recording size. */
  onRecordingChunk (blob) {
    this.recordedBytes += blob?.size || 0
  }

  checkRecordingSizeBudget () {
    if (!this.recordingSizeLimit || !this.recordingStartTime) {
      return
    }

    const elapsedSeconds = (Date.now() - this.recordingStartTime) / 1000
    if (elapsedSeconds <= 0) {
      return
    }

    // Measured rate once chunks exist, otherwise the bitrate the recorder was configured with.
    const bytesSoFar = this.recordedBytes > 0
      ? this.recordedBytes
      : this.recordingEstimatedBytesPerSecond * elapsedSeconds
    const bytesPerSecond = bytesSoFar / elapsedSeconds
    if (bytesPerSecond <= 0) {
      return
    }

    const budget = this.recordingSizeLimit - RECORDING_SIZE_SAFETY_MARGIN
    const secondsLeft = (budget - bytesSoFar) / bytesPerSecond

    if (secondsLeft <= 0) {
      const extra = { blobSize: Math.round(bytesSoFar), limitBytes: this.recordingSizeLimit }
      window.LoggerAPI.addLog('client', new Date(), 'warn', 'RECORDING_SIZE_LIMIT_REACHED',
        JSON.stringify(this.buildRecordingExperimentExtra(extra)))
      this.stopRecordingSizeBudget()
      // MeetingRoom owns the stop: each destination needs different upload arguments.
      EventBus.$emit('recordingSizeLimitReached', { destination: this.recordingDestination })
      return
    }

    const minutesLeft = Math.ceil(secondsLeft / 60)
    const due = RECORDING_SIZE_WARNING_MINUTES.filter(
      (m) => minutesLeft <= m && !this.recordingSizeWarningsSent.includes(m)
    )
    if (due.length) {
      // The budget can cross several thresholds between checks, and the first check can
      // already be under all of them. Warn once with the time that is actually left, and
      // consume the thresholds it passed so the countdown never reports a larger number
      // than the previous warning.
      this.recordingSizeWarningsSent.push(...due)
      EventBus.$emit('recordingSizeWarning', {
        minutesLeft: Math.min(...due),
        destination: this.recordingDestination
      })
    }
  }

  /**
   * LoggerAPI + EventBus so MeetingRoom can Flagmint track / trackError.
   *
   * 'upload_fail' is kept separate from 'fail': a rejected upload (vendor CORS, 413,
   * outage) says nothing about whether the timeSlice variation produced a usable file,
   * so it must not count against the experiment or trip the guardrail alarm.
   *
   * @param {'start'|'success'|'empty'|'fail'|'upload_fail'} kind
   */
  reportRecordingExperimentOutcome (kind, overrides = {}, error = null) {
    const extra = this.buildRecordingExperimentExtra(overrides)
    const message = JSON.stringify(extra)
    try {
      if (kind === 'start') {
        window.LoggerAPI.addLog('client', new Date(), 'log', 'RECORDING_START', message)
      } else if (kind === 'success') {
        window.LoggerAPI.addLog('client', new Date(), 'log', 'RECORDING_STOP_OK', message)
        EventBus.$emit('recordingExperimentOutcome', { type: 'success', extra })
      } else if (kind === 'empty') {
        window.LoggerAPI.addLog('client', new Date(), 'error', 'RECORDING_STOP_EMPTY', message)
        EventBus.$emit('recordingExperimentOutcome', {
          type: 'failure',
          extra,
          error: error || new Error('Empty recording blob')
        })
      } else if (kind === 'fail') {
        window.LoggerAPI.addLog('client', new Date(), 'error', 'RECORDING_STOP_FAIL', message)
        EventBus.$emit('recordingExperimentOutcome', {
          type: 'failure',
          extra,
          error: error || new Error('Recording stop failed')
        })
      } else if (kind === 'upload_fail') {
        window.LoggerAPI.addLog('client', new Date(), 'error', 'RECORDING_UPLOAD_FAIL', message)
        EventBus.$emit('recordingExperimentOutcome', {
          type: 'upload_failure',
          extra,
          error: error || new Error('Recording upload failed')
        })
      }
    } catch (err) {
      console.error('Failed to report recording experiment outcome:', err)
    }
  }

  // Tell only a newly joined participant that transcription/recording is already
  // running. People already in the room were notified when recording started;
  // media re-subscribe must not count as a join.
  notifyJoinerOfActiveRecording (destinationIdentity) {
    if (destinationIdentity) {
      this.cancelForgetRemoteIdentity(destinationIdentity)
      this.knownRemoteIdentities.add(String(destinationIdentity))
    }
    if (!this.localStream || !destinationIdentity) {
      return
    }
    if (this.recorder === null && !this.isScriptixRecording) {
      return
    }
    if (store.getters.userHasFeature('weseedo_direct_recording')) {
      return
    }
    const id = String(destinationIdentity)
    if (this.recordingNotifiedIdentities.has(id)) {
      return
    }
    const localId = this.localRecordingNoticeIdentity()
    if (localId && id === String(localId)) {
      return
    }
    const payload = {
      sender: this.getRealNameFromStream(this.localStream) || 'Agent',
      type: this.isScriptixRecording ? 'start_scriptix_recording' : 'start_recording',
      targetUserId: id
    }
    this.recordingNotifiedIdentities.add(id)
    if (this.mediaEngine === 'livekit' && this.livekitAdapter) {
      this.sendJoinRecordingNotice(payload, id, 3)
      return
    }
    this.localStream.sendData(payload)
  }

  sendJoinRecordingNotice (payload, id, attemptsLeft) {
    if (!this.livekitAdapter) {
      this.recordingNotifiedIdentities.delete(id)
      return
    }
    this.livekitAdapter.sendData(payload, [id]).then((sent) => {
      if (sent) {
        return
      }
      this.recordingNotifiedIdentities.delete(id)
      if (attemptsLeft <= 1) {
        return
      }
      if (this.recorder === null && !this.isScriptixRecording) {
        return
      }
      setTimeout(() => {
        if (!this.livekitAdapter || !this.knownRemoteIdentities.has(id)) {
          return
        }
        if (this.recordingNotifiedIdentities.has(id)) {
          return
        }
        if (this.recorder === null && !this.isScriptixRecording) {
          return
        }
        this.recordingNotifiedIdentities.add(id)
        this.sendJoinRecordingNotice(payload, id, attemptsLeft - 1)
      }, 400)
    })
  }

  notifyExistingLivekitParticipants () {
    if (!this.livekitAdapter || !this.livekitAdapter.room) {
      return
    }
    for (const participant of this.livekitAdapter.room.remoteParticipants.values()) {
      if (participant && participant.isActive) {
        this.notifyJoinerOfActiveRecording(participant.identity)
      }
    }
  }

  localRecordingNoticeIdentity () {
    if (this.livekitAdapter && this.livekitAdapter.room && this.livekitAdapter.room.localParticipant) {
      return this.livekitAdapter.room.localParticipant.identity || ''
    }
    return this.getUserIdFromStream(this.localStream) || ''
  }

  seedRecordingNotifiedIdentities () {
    this.recordingNotifiedIdentities = new Set(this.knownRemoteIdentities)
    if (this.livekitAdapter && this.livekitAdapter.room) {
      for (const participant of this.livekitAdapter.room.remoteParticipants.values()) {
        if (participant && participant.isActive && participant.identity) {
          this.recordingNotifiedIdentities.add(String(participant.identity))
        }
      }
    }
  }

  clearRecordingNoticeState () {
    Object.keys(this.recordingNoticeForgetTimers).forEach((key) => {
      clearTimeout(this.recordingNoticeForgetTimers[key])
    })
    this.recordingNoticeForgetTimers = {}
    this.recordingNotifiedIdentities = new Set()
  }

  cancelForgetRemoteIdentity (id) {
    if (id == null || id === '') {
      return
    }
    const key = String(id)
    if (this.recordingNoticeForgetTimers[key]) {
      clearTimeout(this.recordingNoticeForgetTimers[key])
      delete this.recordingNoticeForgetTimers[key]
    }
  }

  scheduleForgetRemoteIdentity (id) {
    if (id == null || id === '') {
      return
    }
    const key = String(id)
    this.cancelForgetRemoteIdentity(key)
    this.recordingNoticeForgetTimers[key] = setTimeout(() => {
      delete this.recordingNoticeForgetTimers[key]
      this.forgetRemoteIdentity(key)
    }, 3000)
  }

  forgetRemoteIdentity (id) {
    if (id == null || id === '') {
      return
    }
    const key = String(id)
    this.cancelForgetRemoteIdentity(key)
    this.knownRemoteIdentities.delete(key)
    this.recordingNotifiedIdentities.delete(key)
  }

  isRecordingNoticeForLocalUser (data) {
    if (!data || data.targetUserId == null || data.targetUserId === '') {
      return true
    }
    const localIdentity = this.localRecordingNoticeIdentity()
    if (!localIdentity) {
      return false
    }
    return String(data.targetUserId) === String(localIdentity)
  }

  /**
   * Map transcription factory service names to stable telemetry destinations.
   * @param {string} serviceName
   * @returns {'scriptix'|'bumicom'|'smartintake'|string}
   */
  normalizeRecordingDestination (serviceName) {
    switch (serviceName) {
      case 'Scriptix':
        return 'scriptix'
      case 'Bumicom':
        return 'bumicom'
      case 'SmartIntakeAI':
        return 'smartintake'
      default:
        return typeof serviceName === 'string' && serviceName
          ? serviceName.toLowerCase()
          : 'unknown'
    }
  }

  /**
   * Starts the Scriptix recording.
   *
   * @returns {boolean} Returns false if the local stream is not available or the recorder is already started, otherwise returns true.
   */
  startScriptixRecording () {
    if (!this.localStream || this.recorder !== null) {
      return false
    }

    window.LoggerAPI.addLog('client', new Date(), 'log', 'START_SCRIPTIX_RECORDING', 'Scriptix recording started.')
    this.isScriptixRecording = true
    this.seedRecordingNotifiedIdentities()
    this.localStream.sendData({
      'sender': typeof this.localStream.getAttributes() !== 'undefined' && typeof this.localStream.getAttributes().real_name !== 'undefined' ? this.localStream.getAttributes().real_name : 'Agent',
      'type': 'start_scriptix_recording'
    })

    let today = new Date()
    let date = today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate()
    let time = today.getHours() + ':' + today.getMinutes() + ':' + today.getSeconds()
    this.recordingFileName = `${this.productModeName}_${date}_${time}.webm`
    this.recordingSource = 'scriptix'
    this.recordingDestination = this.recordingDestination || 'unknown'
    this.addVideosToRecorder(null, 'audio')
    this.reportRecordingExperimentOutcome('start', { type: 'audio' })
  }
  /**
   * Stops the Scriptix recording and uploads the recording to the server.
   *
   * @param {AxiosInstance} axiosInstance - The Axios instance used for making HTTP requests.
   * @param {Object} progressToast - The progress toast object used to display the upload progress.
   * @param {string} apiKey - The API key used for authentication.
   */
  stopScriptixRecording (axiosInstance, progressToast, transcriptionService, uploadService, multiStepTracker = null) {
    if (this.recorder) {
      // Check feature flag from current meeting or fallback to lastMeetingInfo
      let companyHasOSAW = store.getters.companyHasFeature('osaw_transcription')

      // If meeting is cleared, check saved company info
      if (!companyHasOSAW) {
        const lastMeetingInfo = store.getters.getLastMeetingInfo
        if (lastMeetingInfo && lastMeetingInfo.company && lastMeetingInfo.company.features) {
          companyHasOSAW = lastMeetingInfo.company.features.includes('osaw_transcription')
        }
      }

      // Prefer stop-time destination: OSAW vs configured transcription vendor.
      if (companyHasOSAW) {
        this.recordingDestination = 'osaw'
      } else if (transcriptionService?.service?.name) {
        this.recordingDestination = this.normalizeRecordingDestination(transcriptionService.service.name)
      }

      this.stopRecordingSizeBudget()
      this.recorder.stopRecording(() => {
        window.LoggerAPI.addLog('client', new Date(), 'log', 'STOP_RECORDING', 'Recording stopped.')

        if (this.localStream) {
          this.localStream.sendData({
            'sender': typeof this.localStream.getAttributes() !== 'undefined' && typeof this.localStream.getAttributes().real_name !== 'undefined' ? this.localStream.getAttributes().real_name : 'Agent',
            'type': 'stop_scriptix_recording'
          })
        }

        const durationInMs = this.recordingStartTime ? (Date.now() - this.recordingStartTime) : 0
        const rawBlob = this.recorder.getBlob()
        const rawSize = rawBlob?.size || 0
        if (!rawBlob || rawSize === 0) {
          this.reportRecordingExperimentOutcome('empty', { blobSize: 0, durationMs: durationInMs })
          EventBus.$toast.add({
            severity: 'error',
            detail: 'Recording failed: the file was empty. Please try again.',
            life: 5000
          })
          this.recorder = null
          this.isScriptixRecording = false
          return
        }
        fixWebmDuration(rawBlob, durationInMs, (seekableBlob) => {
          const blobSize = seekableBlob?.size || 0
          if (!seekableBlob || blobSize === 0) {
            this.reportRecordingExperimentOutcome('empty', { blobSize: 0, durationMs: durationInMs })
            EventBus.$toast.add({
              severity: 'error',
              detail: 'Recording failed: the file was empty. Please try again.',
              life: 5000
            })
            this.recorder = null
            this.isScriptixRecording = false
            return
          }
          const recordingSessionId = typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `session-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`

          // The recording itself is what the experiment measures: a usable file exists
          // here, whatever the upload service does with it next.
          this.reportRecordingExperimentOutcome('success', { blobSize, durationMs: durationInMs })

          if (companyHasOSAW) {
            // upload to OSAW (one upload per session; session id prevents duplicates)
            axiosInstance.uploadRecordingToOSAW(seekableBlob, recordingSessionId)
              .then(res => {
                console.log('osaw success uploading')
              })
              .catch(err => {
                console.log(err, 'osaw error uploading')
                this.reportRecordingExperimentOutcome('upload_fail', { blobSize, durationMs: durationInMs }, err)
              })
          } else {
            if (seekableBlob instanceof Blob) {
              RecordRTC.invokeSaveAsDialog(seekableBlob, this.recordingFileName)
            }
            // Show toast only if not using MultiStepUpload component
            if (!multiStepTracker) {
              progressToast.toast('show')
            }
            transcriptionService.recordingDurationSeconds = durationInMs
            uploadService.uploadData(seekableBlob, this.recordingFileName, progressToast, transcriptionService, multiStepTracker, {
              chunkedUploadEnabled: this.smartIntakeChunkedUpload === true
            })
              .then(res => {
                console.log('scriptix success uploading')
              })
              .catch(err => {
                console.log(err, 'scriptix error uploading')
                this.reportRecordingExperimentOutcome('upload_fail', { blobSize, durationMs: durationInMs }, err)
              })
          }

          this.recorder = null
          this.isScriptixRecording = false
        })
      })
    }
  }

  startRecording (element = null) {
    if (!this.localStream || this.recorder !== null) {
      return false
    }

    window.LoggerAPI.addLog('client', new Date(), 'log', 'START_RECORDING', 'Recording started.')
    this.isScriptixRecording = false // Reset flag for regular recording
    this.recordingSource = 'agent'
    this.recordingDestination = this.recordingDestination || 'local'
    this.seedRecordingNotifiedIdentities()
    this.localStream.sendData({
      'sender': typeof this.localStream.getAttributes() !== 'undefined' && typeof this.localStream.getAttributes().real_name !== 'undefined' ? this.localStream.getAttributes().real_name : 'Agent',
      'type': 'start_recording'
    })

    let today = new Date()
    let date = today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate()
    let time = today.getHours() + ':' + today.getMinutes() + ':' + today.getSeconds()
    this.recordingFileName = `${this.productModeName}_${date}_${time}.webm`
    this.addVideosToRecorder(element)
    this.reportRecordingExperimentOutcome('start', { type: 'video' })
  }
  // method that auto starts recording, made only for one way video
  // removed localstream as it made RecordRTC fail
  // Stopping the recording is done through the stopRecording method
  autoStartRecording (element = null) {
    if (this.recorder !== null) {
      return false
    }

    window.LoggerAPI.addLog('client', new Date(), 'log', 'START_RECORDING', 'Recording started.')
    this.recordingSource = 'auto'
    this.recordingDestination = this.recordingDestination || 'bumicom'

    let today = new Date()
    let date = today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate()
    let time = today.getHours() + ':' + today.getMinutes() + ':' + today.getSeconds()
    this.recordingFileName = `${this.productModeName}_${date}_${time}.webm`
    this.addVideosToAutoRecorder(element)
    this.reportRecordingExperimentOutcome('start', { type: 'video' })
  }

  stopRecording () {
    if (!this.recorder) {
      return Promise.resolve()
    }

    // Hangup calls stopRecording() from MeetingRoom.endCall and again from
    // stopLocalStreams via webrtc.endCall. A second stop on an already-stopped
    // RecordRTC yields an empty blob and a false "Recording failed" toast even
    // when the first stop already produced a valid download.
    if (this.recordingSaveInProgress) {
      return this.recordingSavePromise || Promise.resolve()
    }

    this.recordingSaveInProgress = true
    this.stopRecordingSizeBudget()
    this.recordingSavePromise = new Promise((resolve) => {
      this.recordingDestination = this.recordingDestination || 'local'
      this.recorder.stopRecording(() => {
        EventBus.$toast.add({ severity: 'info', detail: 'File processing in progress' })
        window.LoggerAPI.addLog('client', new Date(), 'log', 'STOP_RECORDING', 'Recording stopped.')

        if (this.localStream) {
          try {
            const attrs = typeof this.localStream.getAttributes !== 'undefined'
              ? this.localStream.getAttributes()
              : null

            this.localStream.sendData({
              sender: attrs?.real_name || 'Agent',
              type: 'stop_recording'
            })
          } catch (err) {
            // Socket may already be gone when the meeting ends remotely; still save the file.
            console.error('Failed to notify peers of stop_recording:', err)
          }
        }

        const blob = this.recorder.getBlob()
        const blobSize = blob?.size || 0

        if (!blob || blobSize === 0) {
          EventBus.$toast.removeAllGroups()
          EventBus.$toast.add({
            severity: 'error',
            detail: 'Recording failed: the file was empty. Please try again.',
            life: 5000
          })
          this.reportRecordingExperimentOutcome('empty', { blobSize: 0 })
          this.recorder = null
          this.isScriptixRecording = false
          this.recordingSaveInProgress = false
          resolve()
          return
        }

        // Use the worker to process the blob into a seekable one
        const worker = new Worker('/seekable-worker.js')

        worker.onmessage = async (e) => {
          const data = e.data
          const finalBlob = data instanceof Blob ? data : blob
          const finalSize = finalBlob?.size || 0

          if (!(data instanceof Blob) && data?.error) {
            console.error('Seekable processing failed:', data.error)
            // Falls through to saving the non-seekable blob.
          }

          if (!finalBlob || finalSize === 0) {
            EventBus.$toast.removeAllGroups()
            EventBus.$toast.add({
              severity: 'error',
              detail: 'Recording failed: the file was empty. Please try again.',
              life: 5000
            })
            this.reportRecordingExperimentOutcome('empty', { blobSize: 0 })
            worker.terminate()
            this.recorder = null
            this.isScriptixRecording = false
            this.recordingSaveInProgress = false
            resolve()
            return
          }

          let handledNatively = false
          try {
            handledNatively = await this.saveRecordingBlob(finalBlob, this.recordingFileName)
            this.reportRecordingExperimentOutcome('success', { blobSize: finalSize })
          } catch (err) {
            console.error('Saving recording failed:', err)
            this.reportRecordingExperimentOutcome('fail', { blobSize: finalSize }, err)
            EventBus.$toast.removeAllGroups()
            EventBus.$toast.add({
              severity: 'error',
              detail: 'Recording failed to save. Please try again.',
              life: 5000
            })
            worker.terminate()
            this.recorder = null
            this.isScriptixRecording = false
            this.recordingSaveInProgress = false
            resolve()
            return
          }

          EventBus.$toast.removeAllGroups()
          // The native saver shows its own result toast, so only notify here for the
          // in-browser save-as path to avoid a duplicate notification in the app.
          if (!handledNatively) {
            EventBus.$toast.add({ severity: 'success', detail: 'File processing done', life: 3000 })
          }
          worker.terminate()
          this.recorder = null
          this.isScriptixRecording = false
          this.recordingSaveInProgress = false
          resolve()
        }

        worker.onerror = (err) => {
          console.error('Seekable worker crashed:', err)
          this.reportRecordingExperimentOutcome('fail', { blobSize }, err)
          EventBus.$toast.removeAllGroups()
          EventBus.$toast.add({
            severity: 'error',
            detail: 'Recording processing failed. Please try again.',
            life: 5000
          })
          try { worker.terminate() } catch (e) {}
          this.recorder = null
          this.isScriptixRecording = false
          this.recordingSaveInProgress = false
          resolve()
        }

        worker.postMessage(blob)
      })
    })

    return this.recordingSavePromise
  }

  /**
   * Persist a finished recording. Inside the native mobile webview the in-browser saveAs()
   * yields a blob: URL the WebView can't store, so the file is streamed to the native layer.
   * In a real browser it falls back to the standard save-as download dialog.
   */
  async saveRecordingBlob (blob, fileName) {
    const bridge = window.AndroidBridge
    const hasNativeSave = bridge &&
      (typeof bridge.saveFileStart === 'function' || typeof bridge.saveFile === 'function')

    if (hasNativeSave) {
      const saved = await this.saveBlobViaNativeBridgeChunked(blob, fileName)
      if (saved) {
        return true
      }
    }

    RecordRTC.invokeSaveAsDialog(blob, fileName)
    return false
  }

  /**
   * Stream a blob to the native bridge in base64 chunks so a large recording never has to
   * exist as one giant base64 string in memory. Chunks are cut on 3-byte boundaries so each
   * one base64-decodes cleanly on the native side. Falls back to the single-shot saveFile
   * (older apps / iOS) when the chunked API isn't available. Returns true on hand-off.
   */
  async saveBlobViaNativeBridgeChunked (blob, fileName) {
    const bridge = window.AndroidBridge
    const mime = blob.type || 'video/webm'
    const hasChunkedApi = bridge &&
      typeof bridge.saveFileStart === 'function' &&
      typeof bridge.saveFileChunk === 'function' &&
      typeof bridge.saveFileEnd === 'function'

    if (hasChunkedApi) {
      const CHUNK_SIZE = 768 * 1024 // multiple of 3 → no base64 padding mid-stream
      try {
        bridge.saveFileStart(fileName, mime)
        for (let start = 0; start < blob.size; start += CHUNK_SIZE) {
          const slice = blob.slice(start, Math.min(start + CHUNK_SIZE, blob.size))
          bridge.saveFileChunk(await this.blobSliceToBase64(slice))
          // Yield so the UI thread and native writes can keep up.
          await new Promise(resolve => setTimeout(resolve, 0))
        }
        bridge.saveFileEnd()
        return true
      } catch (err) {
        console.error('Chunked native save failed:', err)
        try {
          if (typeof bridge.saveFileAbort === 'function') {
            bridge.saveFileAbort()
          }
        } catch (abortErr) {
          console.error('Native save abort failed:', abortErr)
        }
        return false
      }
    }

    // Fallback: hand the whole file over as one data URI.
    const dataUri = await this.blobSliceToBase64(blob, true)
    return this.saveFileViaNativeBridge(dataUri, fileName)
  }

  /**
   * Read a blob (or slice) as base64. With asDataUri=false (default) only the base64 payload
   * is returned; with asDataUri=true the full `data:<mime>;base64,...` URI is returned.
   */
  blobSliceToBase64 (blob, asDataUri = false) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result
        if (asDataUri) {
          resolve(result)
          return
        }
        const commaIdx = result.indexOf(',')
        resolve(commaIdx >= 0 ? result.substring(commaIdx + 1) : '')
      }
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(blob)
    })
  }

  /**
   * Wait for any in-flight recording save to finish before the meeting tears down. If a
   * recording is still running (e.g. the meeting ended without tapping stop) it is stopped
   * first. Capped by a timeout so a stalled save can never trap the user in the call.
   */
  async flushPendingRecordingSave (timeoutMs = 120000) {
    if (this.recorder && !this.recordingSaveInProgress) {
      this.stopRecording()
    }
    if (!this.recordingSavePromise) {
      return
    }
    const timeout = new Promise(resolve => setTimeout(resolve, timeoutMs))
    await Promise.race([this.recordingSavePromise, timeout])
  }

  stopAutoRecording (axiosInstance) {
    if (this.recorder) {
      this.recordingDestination = 'bumicom'
      this.stopRecordingSizeBudget()
      this.recorder.stopRecording(() => {
        window.LoggerAPI.addLog('client', new Date(), 'log', 'STOP_RECORDING', 'Recording stopped.')

        const rawBlob = this.recorder.getBlob()
        const rawSize = rawBlob?.size || 0
        if (!rawBlob || rawSize === 0) {
          this.reportRecordingExperimentOutcome('empty', { blobSize: 0 })
          this.recorder = null
          this.isScriptixRecording = false
          this.axiosInstance = null
          return
        }

        RecordRTC.getSeekableBlob(rawBlob, (seekableBlob) => {
          const blobSize = seekableBlob?.size || 0
          if (!seekableBlob || blobSize === 0) {
            this.reportRecordingExperimentOutcome('empty', { blobSize: 0 })
            this.recorder = null
            this.isScriptixRecording = false
            this.axiosInstance = null
            return
          }
          this.reportRecordingExperimentOutcome('success', { blobSize })

          // Replace this with API Post with the blob
          axiosInstance
            .uploadRecordingToBumicom(seekableBlob, this.recordingFileName)
            .then(res => {
              console.log('Bumicom success uploading')
            })
            .catch(err => {
              console.log(err, 'Bumicom error uploading')
              this.reportRecordingExperimentOutcome('upload_fail', { blobSize }, err)
            })
          this.recorder = null
          this.isScriptixRecording = false
          this.axiosInstance = null
        })
      })
    }
  }

  addVideoToRecorder (stream) {
    if (!this.recorder || !stream) {
      return false
    }
    this.recorder.getInternalRecorder().addStreams([stream])
  }

  addVideosToRecorder (element = null, type = 'video') {
    let streams = []
    // Use the effected stream only when an effect is actually running; otherwise
    // record the real local camera (an idle effects stream is empty and skews the
    // recording layout / drops the local video).
    if (this.isVideoEffectRunning && this.effectsSDK) {
      try {
        const effectStream = this.effectsSDK.getStream()
        if (effectStream) {
          streams.push(effectStream)
        }
      } catch (e) {
        if (this.localStream && this.localStream.stream) {
          streams.push(this.localStream.stream)
        }
      }
    } else if (this.localStream && this.localStream.stream) {
      streams.push(this.localStream.stream)
    }
    let allRemoteStreams = this.getAllStreams()
    Object.keys(allRemoteStreams).forEach(function (stream) {
      streams.push(allRemoteStreams[stream])
    })

    let options = {
      type: 'video',
      mimeType: 'video/webm;codecs=vp8,opus',
      videoBitsPerSecond: 512 * 8 * 1024
    }

    if (store.getters.getProduct === 'direct' && element) {
      const { width, height } = this.getVideoDimension(element)
      options.video = {
        width,
        height
      }
    }
    if (type === 'audio' && store.getters.getProduct !== 'direct') {
      // Speech at 32 kbps Opus keeps an hour-long conversation well inside the upload
      // caps of the transcription services; the browser default is ~4x this and puts a
      // ~35 minute ceiling on a 32MB limit.
      options = { type: 'audio', audioBitsPerSecond: TRANSCRIPTION_AUDIO_BITS_PER_SECOND }
    }

    // Positive Flagmint timeSlice enables RecordRTC arrayOfBlobs accumulate-on-stop.
    // Omit for control (0) so the library keeps its internal 1h MediaRecorder interval.
    if (this.recordingTimeSliceMs > 0) {
      options.timeSlice = this.recordingTimeSliceMs
      // Chunks are the only way to know the real byte rate; RecordRTC never calls this
      // without a timeSlice, so the size budget falls back to an estimate under control.
      options.ondataavailable = (blob) => this.onRecordingChunk(blob)
    }

    if (this.recorder === null) {
      const availableStreams = streams.filter(Boolean)

      this.recorder = RecordRTC(availableStreams, options)
      this.recorder.startRecording()
      // Capture actual recording start time after recorder is initialized
      this.recordingStartTime = Date.now()
      this.startRecordingSizeBudget(options)
    }
  }

  addVideosToAutoRecorder (element = null) {
    let streams = []
    let allRemoteStreams = this.getAllStreams()
    Object.keys(allRemoteStreams).forEach(function (stream) {
      streams.push(allRemoteStreams[stream])
    })
    let options = {
      type: 'video',
      mimeType: 'video/webm;codecs=vp8,opus',
      videoBitsPerSecond: 512 * 8 * 1024
    }

    if (store.getters.getProduct === 'direct' && element) {
      const { width, height } = this.getVideoDimension(element)
      options.video = {
        width,
        height
      }
    }
    if (this.recordingTimeSliceMs > 0) {
      options.timeSlice = this.recordingTimeSliceMs
      options.ondataavailable = (blob) => this.onRecordingChunk(blob)
    }
    if (this.recorder === null) {
      this.recorder = RecordRTC(streams, options)
      this.recorder.startRecording()
      this.recordingStartTime = Date.now()
      this.startRecordingSizeBudget(options)
    }
  }
  videoDevices = [];
  audioDevices = []
  outputDevices = [];
  activeVideoDevice = null;
  activeAudioInput = null;
  activeAudioOutput = null;

  isCameraSwitching = false
  isCameraSwitchingNow () {
    return this.isCameraSwitching
  }

  deviceSwitching = false
  isDeviceSwitchingNow () {
    return this.deviceSwitching
  }

  facingMode = 'environment'
  flipFacingMode = null
  // Remembers the exact camera deviceId last used per facing ('user'/'environment') so a
  // front/rear flip returns to the camera the user picked, not the OS default for that side.
  // iPad Safari reports a desktop Mac UA, so also match MacIntel + touch.
  isIOSDevice () {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  }

  cameraDeviceByFacing = {}
  switchCamera () {
    if (this.isCameraSwitching) {
      return
    }
    // The Licode path below uses this.room/Erizo; LiveKit re-captures + live-replaces.
    if (this.mediaEngine === 'livekit') {
      this.switchCameraLivekit()
      return
    }
    this.isCameraSwitching = true
    this.activeVideoDevice = null

    let facingMode = null
    if (typeof this.localStream.stream !== 'undefined' && this.localStream.stream.getVideoTracks().length > 0) {
      facingMode = this.localStream.stream.getVideoTracks()[0].getSettings().facingMode
    }
    this.facingMode = facingMode

    if (this.facingMode === 'environment') {
      this.facingMode = 'user'
    } else {
      this.facingMode = 'environment'
    }

    let constraints = this.lastConstraints
    constraints.attributes = this.localStream.getAttributes()
    constraints.video.facingMode = this.facingMode
    if (constraints.video && constraints.video.deviceId) {
      delete constraints.video.deviceId
    }
    this.lastConstraints = constraints

    this.room.unpublish(this.localStream)
    if (this.p2p) {
      // eslint-disable-next-line no-undef
      this.localStream = ErizoV9.Stream(constraints)
    } else {
      // eslint-disable-next-line no-undef
      this.localStream = Erizo.Stream(constraints)
    }
    this.localStream.addEventListener('bandwidth-alert', (streamEvent) => {
      console.error('THERE IS A BANDWIDTH PROBLEM WITH STREAM THAT WAS SWITCHED AT LEAST ONCE')
      console.error(streamEvent)
    })
    this.localStream.addEventListener('access-accepted', async (stream) => {
      if (constraints.audio !== false) {
        this.startSoundMeter()
      }

      await this.room.publish(this.localStream, {
        disableIceRestart: true,
        forceTurn: true,
        simulcast: false,
        scheme: 'notify-break-recover'
      })

      let facingMode = null
      if (!('getCapabilities' in this.localStream.stream.getVideoTracks()[0])) {
        this.activeVideoDevice = this.localStream.stream.getVideoTracks()[0].getSettings().deviceId
        facingMode = this.localStream.stream.getVideoTracks()[0].getSettings().facingMode
      } else {
        this.activeVideoDevice = this.localStream.stream.getVideoTracks()[0].getCapabilities().deviceId
      }

      let video = this.getVideoTag(this.localStream)

      let isMobileDevice = window.DetectRTC.isMobileDevice
      if (!isMobileDevice) {
        isMobileDevice = /iPad/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
      }

      if (!facingMode) {
        facingMode = this.facingMode
      }

      this.EventBus.$emit('onStreamAccessGranted', {
        'can_switch_camera': isMobileDevice,
        'stream': this.localStream.stream,
        'video': video,
        'mirrored': facingMode === 'user',
        'video_muted': this.isVideoMuted,
        'audio_muted': this.isAudioMuted
      })

      // Update zoom functionality with new video track
      this.updateZoomForNewCamera()

      if (this.isVideoMuted) {
        this.muteVideo()
      }
      if (this.isAudioMuted) {
        this.muteAudio()
      }
      this.isCameraSwitching = false
    })
    this.localStream.init()
  }

  // LiveKit front/rear flip: re-capture with the toggled facingMode (no deviceId, so
  // the OS picks the other camera) and live-replace the published track via the same
  // startLocalStreams path that switchCameraDevice uses.
  switchCameraLivekit () {
    if (!this.localStream || !this.localStream.stream) {
      return
    }
    this.isCameraSwitching = true
    const currentTrack = this.localStream.stream.getVideoTracks()[0]
    const currentSettings = currentTrack ? currentTrack.getSettings() : {}
    const currentFacing = currentSettings.facingMode || this.facingMode
    // Remember the exact camera in use for this facing before flipping away, so a later flip
    // back restores it (phones expose several cameras per side; a facing-only flip would come
    // back to the OS default rather than the one the user selected).
    if (currentFacing && currentSettings.deviceId) {
      this.cameraDeviceByFacing[currentFacing] = currentSettings.deviceId
    }
    const targetFacing = currentFacing === 'environment' ? 'user' : 'environment'
    // Prefer the exact device last used for the target side; null on the first flip to a side
    // falls back to facingMode (OS default).
    const targetDeviceId = this.cameraDeviceByFacing[targetFacing] || null

    // iOS/iPadOS (every browser is WebKit) is single-camera, so re-capturing forces us to stop the
    // published track first — and WebKit then leaves the sender's encoder frozen on the old frame
    // (remote stays stuck) even though the local preview updates. Switch on the LIVE track via
    // applyConstraints instead: the same track keeps the same sender/encoder running so the remote
    // updates. With a video effect the published track is the effects CANVAS but the raw camera is
    // the SDK's input — re-capturing stops that input and freezes the canvas the same way, so the
    // effect case gets its own in-place switch. Zoom (direct only) also uses the live track.
    if (this.isIOSDevice() && currentTrack) {
      const liveSwitch = this.isVideoEffectRunning
        ? this.switchCameraLivekitEffectLiveTrack(currentTrack, targetFacing, targetDeviceId)
        : this.switchCameraLivekitLiveTrack(currentTrack, targetFacing, targetDeviceId)
      // A rejection must not leave isCameraSwitching stuck true.
      liveSwitch.catch((e) => {
        console.error('Live camera switch failed', e)
        this.isCameraSwitching = false
      })
      return
    }

    this.flipFacingMode = targetFacing
    this.activeVideoDevice = targetDeviceId
    // force=true so startLocalStreams re-captures instead of re-emitting the existing stream.
    this.startLocalStreams(this.lastProduct, this.lastStreamBothWays, () => {
      this.initializeRnnoise()
      this.flipFacingMode = null
      this.isCameraSwitching = false
    }, targetDeviceId, null, null, undefined, undefined, false, true)
  }

  // Pick another camera by label when facingMode is unavailable (iPad Safari omits it).
  async pickOtherCameraDeviceId (currentDeviceId, targetFacing) {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const cams = devices.filter((d) => d.kind === 'videoinput' && d.deviceId && d.deviceId !== currentDeviceId)
      if (cams.length === 0) {
        return null
      }
      const pattern = targetFacing === 'environment' ? /back|rear|environment|achter/i : /front|user|face|voor/i
      const match = cams.find((d) => pattern.test(d.label || ''))
      return (match || cams[0]).deviceId
    } catch (e) {
      return null
    }
  }

  // Live-track switch ladder: remembered deviceId → facingMode → any other camera.
  // Verifies the device actually changed; false = caller must re-capture.
  async applyLiveTrackCameraSwitch (track, targetFacing, targetDeviceId) {
    const beforeId = (track.getSettings() || {}).deviceId || null
    // Ignore a stale remembered id (already the active camera).
    if (targetDeviceId && targetDeviceId === beforeId) {
      targetDeviceId = null
    }
    const switched = () => {
      const afterId = (track.getSettings() || {}).deviceId || null
      return track.readyState === 'live' && (!beforeId || !afterId || afterId !== beforeId)
    }
    if (targetDeviceId) {
      try {
        await track.applyConstraints({ deviceId: { exact: targetDeviceId } })
        if (switched()) {
          return true
        }
      } catch (e) { /* next rung */ }
    }
    try {
      await track.applyConstraints({ facingMode: { exact: targetFacing } })
      if (switched()) {
        return true
      }
    } catch (e) { /* next rung */ }
    const otherId = await this.pickOtherCameraDeviceId(beforeId, targetFacing)
    if (otherId) {
      try {
        await track.applyConstraints({ deviceId: { exact: otherId } })
        if (switched()) {
          return true
        }
      } catch (e) { /* fall through */ }
    }
    window.LoggerAPI.addLog('client', new Date(), 'warn', 'CAMERA_FLIP', 'Live camera switch failed on every constraint rung; falling back to re-capture.')
    return false
  }

  // iOS/iPadOS live-track camera switch (see switchCameraLivekit): repoint the EXISTING
  // MediaStreamTrack with applyConstraints so the sender/encoder is never stopped and the remote
  // keeps updating. Reuses the normal switch UI (mirror flip, preview resize, zoom resync) by
  // re-emitting onStreamAccessGranted. Falls back to a full re-capture if the live switch fails.
  async switchCameraLivekitLiveTrack (track, targetFacing, targetDeviceId) {
    const recapture = () => {
      this.flipFacingMode = targetFacing
      this.activeVideoDevice = targetDeviceId
      this.startLocalStreams(this.lastProduct, this.lastStreamBothWays, () => {
        this.initializeRnnoise()
        this.updateZoomForNewCamera()
        this.flipFacingMode = null
        this.isCameraSwitching = false
      }, targetDeviceId, null, null, undefined, undefined, false, true)
    }
    if (!(await this.applyLiveTrackCameraSwitch(track, targetFacing, targetDeviceId))) {
      recapture()
      return
    }
    try {
      const settings = track.getSettings()
      this.facingMode = settings.facingMode || targetFacing
      this.activeVideoDevice = settings.deviceId || this.activeVideoDevice
      // Audio is untouched, so RNNoise is not re-initialised. Emit the SAME event the re-capture
      // path emits so the mirror flip / preview resize / zoom UI all run through existing code.
      const video = this.getVideoTag(this.localStream)
      let isMobileDevice = window.DetectRTC.isMobileDevice
      if (!isMobileDevice) {
        isMobileDevice = /iPad/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
      }
      this.EventBus.$emit('onStreamAccessGranted', {
        'can_switch_camera': isMobileDevice,
        'stream': this.localStream.stream,
        'video': video,
        'mirrored': this.facingMode === 'user',
        'video_muted': this.isVideoMuted,
        'audio_muted': this.isAudioMuted
      })
      this.updateZoomForNewCamera()
    } finally {
      // Always release the flag, even if a listener throws.
      this.isCameraSwitching = false
    }
  }

  // iOS/iPadOS camera switch while a video effect (virtual background / blur) runs. The published
  // track is the effects-SDK CANVAS; the raw camera is only the SDK's input (the localStream video
  // track). Re-capturing stops that raw track, which halts the canvas draw loop and leaves WebKit's
  // sender encoder frozen. Repoint the SAME raw track with applyConstraints instead: the SDK keeps
  // drawing the new camera onto the same canvas, so the published sender is never touched and the
  // remote keeps updating. Only the local mirror needs flipping. Falls back to a full re-capture.
  async switchCameraLivekitEffectLiveTrack (track, targetFacing, targetDeviceId) {
    const recapture = () => {
      this.flipFacingMode = targetFacing
      this.activeVideoDevice = targetDeviceId
      this.startLocalStreams(this.lastProduct, this.lastStreamBothWays, () => {
        this.initializeRnnoise()
        this.flipFacingMode = null
        this.isCameraSwitching = false
      }, targetDeviceId, null, null, undefined, undefined, false, true)
    }
    if (!(await this.applyLiveTrackCameraSwitch(track, targetFacing, targetDeviceId))) {
      recapture()
      return
    }
    try {
      const settings = track.getSettings()
      this.facingMode = settings.facingMode || targetFacing
      this.activeVideoDevice = settings.deviceId || this.activeVideoDevice
      // The canvas preview and published sender keep running on the same track; only flip the local
      // mirror (front mirrored, rear not) without rebuilding the DOM or re-initialising the effect.
      this.EventBus.$emit('onCameraSwitchedEffect', {
        'mirrored': this.facingMode === 'user'
      })
    } finally {
      this.isCameraSwitching = false
    }
  }

  getVideoTag (stream) {
    var video = document.createElement('video')
    video.setAttribute('autoplay', 'autoplay')
    video.setAttribute('playsinline', 'playsinline')
    video.classList.add('webrtc-video')
    // video.setAttribute('style', 'flex-grow: 1; max-height: 100%; max-width: 100%;')
    video.id = stream.getID()
    video.srcObject = stream.stream
    video.muted = true
    video.volume = 0
    return video
  }

  getRealNameFromStream (stream) {
    if (typeof stream === 'undefined') {
      stream = this.localStream
    }
    let realName = typeof stream !== 'undefined' && stream !== null && typeof stream.getAttributes() !== 'undefined' && typeof stream.getAttributes().real_name !== 'undefined' ? stream.getAttributes().real_name : ''
    return realName
  }
  getUserIdFromStream (stream) {
    if (typeof stream === 'undefined') {
      stream = this.localStream
    }
    let userId = typeof stream !== 'undefined' && stream !== null && typeof stream.getAttributes() !== 'undefined' && typeof stream.getAttributes().user_id !== 'undefined' ? stream.getAttributes().user_id : ''
    return userId
  }
  sendChunks = {}
  sendFilePart (dataChunk, chunkId) {
    if (!this.localStream) {
      return
    }

    let event = {
      'sender': typeof this.localStream.getAttributes() !== 'undefined' && typeof this.localStream.getAttributes().real_name !== 'undefined' ? this.localStream.getAttributes().real_name : 'Agent',
      'type': 'filepart',
      'dataChunk': dataChunk,
      'fileName': dataChunk.fileName
    }
    if (dataChunk.first) {
      let now = new Date()
      this.sendChunks[dataChunk.uuid] = []
      this.EventBus.$emit('onFileStarted', {
        'type': 'file',
        'sentByMe': true,
        'id': dataChunk.uuid,
        'fileName': dataChunk.fileName,
        'fileSize': this.bytesToSize(dataChunk.fileSizeReal),
        'progress': 0,
        'sender': typeof this.localStream.getAttributes() !== 'undefined' && typeof this.localStream.getAttributes().real_name !== 'undefined' ? this.localStream.getAttributes().real_name : 'Agent',
        'datetime': now,
        'direction': 'out'
      })

      event['fileSize'] = this.bytesToSize(dataChunk.fileSize)
      event['progress'] = 0
    }
    this.sendChunks[dataChunk.uuid].push(dataChunk.message)
    if (dataChunk.last) {
      let url = this.sendChunks[dataChunk.uuid].join('')
      this.EventBus.$emit('onFileProgress', {
        'id': dataChunk.uuid,
        'progress': 100,
        'sender': typeof this.localStream.getAttributes() !== 'undefined' && typeof this.localStream.getAttributes().real_name !== 'undefined' ? this.localStream.getAttributes().real_name : 'Agent',
        'direction': 'out'
      })

      this.EventBus.$emit('onFileCompleted', {
        'id': dataChunk.uuid,
        'fileURL': url,
        'fileName': dataChunk.fileName,
        'direction': 'out'
      })

      event['progress'] = 100
    }

    if (!dataChunk.first && !dataChunk.last) {
      // Derived from the chunk actually sent, not a hardcoded size: the chunk length differs
      // per engine, so a fixed constant here reports a fraction of the real progress.
      event['progress'] = ((chunkId * (dataChunk.message ? dataChunk.message.length : 0)) / dataChunk.fileSize) * 100

      this.EventBus.$emit('onFileProgress', {
        'id': dataChunk.uuid,
        'progress': event['progress'] > 100 ? 100 : parseFloat(event['progress']).toFixed(2),
        'sender': typeof this.localStream.getAttributes() !== 'undefined' && typeof this.localStream.getAttributes().real_name !== 'undefined' ? this.localStream.getAttributes().real_name : 'Agent',
        'direction': 'out'
      })
    }
    // File chunks travel over the Erizo room's socket.io connection. A failed send must
    // never bubble up and tear the call down (which previously bounced the agent back to
    // the invite screen) — swallow it and surface it as a file error instead.
    try {
      return this.localStream.sendData(event)
    } catch (err) {
      console.error('[WebRTC] sendData failed for file chunk:', err)
      this.EventBus.$emit('onFileSendError', { id: dataChunk.uuid, error: err })
    }
  };

  /**
   * Best-effort read of the raw WebSocket send buffer behind the Erizo room's socket.io
   * connection, used to throttle large file sends. Returns the buffered byte count, or
   * null when the underlying socket can't be reached (then we pace conservatively).
   */
  getDataSocketBufferedAmount () {
    const paths = [
      () => this.room && this.room.socket && this.room.socket.socket && this.room.socket.socket.io.engine.transport.ws,
      () => this.room && this.room.socket && this.room.socket.io.engine.transport.ws,
      () => this.connection && this.connection.io.engine.transport.ws
    ]
    for (const get of paths) {
      try {
        const ws = get()
        if (ws && typeof ws.bufferedAmount === 'number') {
          return ws.bufferedAmount
        }
      } catch (err) {
        // path not available on this Licode build / transport; try the next
      }
    }
    return null
  }

  /**
   * Backpressure between file chunks. When the socket buffer is observable we wait for it
   * to drain before queuing more (true flow control); otherwise we fall back to a
   * conservative pace so a burst of chunks can't flood the socket and drop the call on
   * slow / low-memory devices.
   */
  async waitBeforeNextFileChunk (chunkIndex) {
    // Deliberate throttle: the SFU drops data packets above ~0.45 MB/s.
    if (this.mediaEngine === 'livekit') {
      await new Promise(resolve => setTimeout(resolve, 5))
      return
    }

    const buffered = this.getDataSocketBufferedAmount()
    if (buffered === null) {
      // Unobservable buffer: ~18ms between chunks, with a longer breather every ~1MB.
      const delay = (chunkIndex > 0 && chunkIndex % 16 === 0) ? 120 : 18
      await new Promise(resolve => setTimeout(resolve, delay))
      return
    }

    const HIGH_WATERMARK = 4 * 1024 * 1024
    const LOW_WATERMARK = 1 * 1024 * 1024
    if (buffered <= HIGH_WATERMARK) {
      await new Promise(resolve => setTimeout(resolve, 5))
      return
    }
    // Saturated: wait for the buffer to drain below the low watermark (capped at 15s).
    const start = Date.now()
    while (this.getDataSocketBufferedAmount() > LOW_WATERMARK && (Date.now() - start) < 15000) {
      await new Promise(resolve => setTimeout(resolve, 50))
    }
  };

  bytesToSize (bytes) {
    let k = 1000
    let sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    if (bytes === 0) {
      return '0 Bytes'
    }
    let i = parseInt(Math.floor(Math.log(bytes) / Math.log(k)), 10)
    return (bytes / Math.pow(k, i)).toPrecision(3) + ' ' + sizes[i]
  }

  download (data, strFileName, strMimeType) {
    // Inside the native mobile apps the in-browser saveAs() produces a blob: URL the
    // WebView can't persist, so the download silently does nothing. Hand the file to the
    // native layer to save instead, and only fall back to saveAs in a real browser.
    if (this.saveFileViaNativeBridge(data, strFileName)) {
      return
    }
    let file = this.dataURItoFile(data, strFileName)
    saveAs(file)
  }

  /**
   * Save a data-URI file through the native mobile bridge when running inside the
   * 123meekijken Android / iOS app. Both apps expose `window.AndroidBridge.saveFile`
   * (Android: a JavascriptInterface; iOS: an injected shim that posts to the native
   * message handler). Returns true if the save was handed off natively.
   */
  saveFileViaNativeBridge (dataUri, fileName) {
    try {
      if (window.AndroidBridge && typeof window.AndroidBridge.saveFile === 'function') {
        window.AndroidBridge.saveFile(dataUri, fileName)
        return true
      }
    } catch (err) {
      console.error('Native file save failed, falling back to browser download:', err)
    }
    return false
  }

  sharePartOfScreen (pdf, currentPage, totalPages, signature, isRetransmit, scale, width, offsetX, offsetY, drawings, presentationType) {
    let data = {
      'host_stream_id': this.localStream.getID(),
      'sender': typeof this.localStream.getAttributes() !== 'undefined' && typeof this.localStream.getAttributes().real_name !== 'undefined' ? this.localStream.getAttributes().real_name : 'Agent',
      'type': 'pdf',
      'content': pdf,
      'current_page': currentPage,
      'total_pages': totalPages,
      'is_retransmit': typeof isRetransmit !== 'undefined' ? isRetransmit : false,
      'signature': signature,
      'presentation_type': presentationType
    }


    if (isRetransmit) {
      data.scale = scale
      data.offsetX = offsetX
      data.offsetY = offsetY
      data.width = width
      data.drawings = drawings
    }

    // Erizo's sendData went over the signaling socket, so a whole document fitted in one
    // message. LiveKit's data channel caps a message at 64KB, so split the content the way
    // the file-share path already does. Licode keeps the single-message path.
    if (this.mediaEngine === 'livekit' && typeof pdf === 'string' && pdf.length > PRESENTATION_CHUNK_LEN) {
      this.sendPresentationInChunks(data)
        .catch(err => console.error('[WebRTC] presentation send failed:', err))
      return
    }

    this.localStream.sendData(data)
  }

  // Only the first message carries the envelope; the rest are content-only so the JSON
  // overhead per chunk stays small. Paced with the file-share backpressure helper.
  async sendPresentationInChunks (data) {
    const content = data.content
    // onAddVideo fires on every TrackSubscribed, and publishData broadcasts, so a transfer
    // already running covers whoever just joined. Without this each event starts another copy.
    const transferKey = content.length + ':' + content.slice(-64)
    if (this.presentationSendKey === transferKey) {
      return
    }
    this.presentationSendKey = transferKey

    const chunkId = 'pdf-' + Date.now() + '-' + Math.random().toString(36).slice(2)
    this.presentationSendId = chunkId
    const inFlight = []

    // Send the decoded bytes: a data URI would travel ~33% larger and escape on every chunk.
    let bytes
    try {
      bytes = await this.dataUriToBytes(content)
    } catch (err) {
      console.error('[WebRTC] presentation decode failed:', err)
      this.presentationSendKey = null
      return
    }

    const mimeType = (content.slice(5).split(';')[0]) || 'application/pdf'
    const total = Math.ceil(bytes.length / PRESENTATION_BIN_CHUNK)

    // Envelope first, on its own and awaited: it opens the transfer and must not be overtaken.
    try {
      await this.localStream.sendData(Object.assign({}, data, {
        content: null,
        pdf_chunk_id: chunkId,
        pdf_bin_total: total,
        pdf_bin_mime: mimeType
      }))
    } catch (err) {
      console.error('[WebRTC] presentation header send failed:', err)
      this.presentationSendKey = null
      return
    }

    for (let i = 0; i < total; i++) {
      // Cancelled or superseded.
      if (this.presentationSendId !== chunkId) {
        this.presentationSendKey = null
        return
      }

      const part = bytes.subarray(i * PRESENTATION_BIN_CHUNK, (i + 1) * PRESENTATION_BIN_CHUNK)

      inFlight.push(this.livekitAdapter.sendBinary({
        type: 'pdf_bin',
        sender: data.sender,
        pdf_chunk_id: chunkId,
        pdf_bin_index: i,
        pdf_bin_total: total
      }, part))
      if (inFlight.length >= PRESENTATION_MAX_IN_FLIGHT) {
        try {
          await inFlight.shift()
        } catch (err) {
          console.error('[WebRTC] presentation chunk send failed:', err)
          this.presentationSendKey = null
          return
        }
      }
    }

    try {
      await Promise.all(inFlight)
    } catch (err) {
      console.error('[WebRTC] presentation chunk send failed:', err)
    } finally {
      this.presentationSendKey = null
    }
  }


  collectPresentationChunk (data) {
    if (this.presentationCompleted && this.presentationCompleted[data.pdf_chunk_id]) {
      return
    }
    if (!data.bytes || !data.bytes.length) {
      console.error('[WebRTC] presentation chunk arrived with no payload', data.pdf_bin_index)
      return
    }
    const buffer = this.presentationBuffer(data.pdf_chunk_id, data.pdf_bin_total)
    this.storePresentationPart(data.pdf_chunk_id, buffer, data.pdf_bin_index, data.bytes)
      .catch(err => console.error('[WebRTC] presentation assembly failed:', err))
  }

  // Opened by whichever message arrives first: the envelope can be overtaken by later chunks.
  // Presenter stopped: drop partial buffers so the spinner does not keep counting.
  abandonPresentationTransfers () {
    if (!this.presentationCompleted) {
      this.presentationCompleted = {}
    }
    if (this.presentationChunks) {
      for (const id of Object.keys(this.presentationChunks)) {
        clearTimeout(this.presentationChunks[id].stallTimer)
        // Chunks already in flight keep arriving after the stop; mark the id so they are
        // dropped rather than opening a fresh buffer and restarting the spinner.
        this.presentationCompleted[id] = true
      }
    }
    this.presentationChunks = {}
    this.EventBus.$emit('onPresentationLoading', { active: false, progress: 0 })
  }

  // A transfer that just stops has no error to report; surface the silence instead.
  armPresentationStallWatch (id) {
    const buffer = this.presentationChunks && this.presentationChunks[id]
    if (!buffer) {
      return
    }
    clearTimeout(buffer.stallTimer)
    buffer.stallTimer = setTimeout(() => {
      const current = this.presentationChunks && this.presentationChunks[id]
      if (!current) {
        return
      }
      console.error('[WebRTC] presentation STALLED at ' + current.received + '/' + current.total +
        ' parts after 15s of silence (envelope ' + (current.envelope !== null) + ')')
      this.EventBus.$emit('onPresentationLoading', { active: false, progress: 0 })
    }, 15000)
  }

  presentationBuffer (id, total) {
    if (!this.presentationChunks) {
      this.presentationChunks = {}
    }
    if (!this.presentationChunks[id]) {
      // Nothing is shown until the whole document arrives, which is tens of seconds on a
      // large file, so tell the UI a presentation is on its way as soon as we know.
      this.EventBus.$emit('onPresentationLoading', { active: true, progress: 0 })
      const ids = Object.keys(this.presentationChunks)
      if (ids.length >= 2) {
        delete this.presentationChunks[ids[0]]
      }
      this.presentationChunks[id] = {
        total: total,
        received: 0,
        parts: [],
        envelope: null,
        sender: null
      }
    }
    return this.presentationChunks[id]
  }

  // Completes only once every part AND the envelope have arrived, in any order. index -1
  // means "envelope only", which carries no part of its own.
  async storePresentationPart (id, buffer, index, bytes) {
    if (index >= 0 && typeof buffer.parts[index] === 'undefined') {
      buffer.parts[index] = bytes
      buffer.received++

      // Only on a whole-percent change: 467 chunks would otherwise be 467 re-renders.
      const percent = Math.floor((buffer.received / buffer.total) * 100)
      if (percent !== buffer.percent) {
        buffer.percent = percent
        this.EventBus.$emit('onPresentationLoading', { active: true, progress: percent })
      }
    }

    if (buffer.received !== buffer.total || buffer.envelope === null) {
      this.armPresentationStallWatch(id)
      return
    }

    clearTimeout(buffer.stallTimer)

    delete this.presentationChunks[id]
    if (!this.presentationCompleted) {
      this.presentationCompleted = {}
    }
    this.presentationCompleted[id] = true

    let total = 0
    for (const part of buffer.parts) {
      total += part.length
    }
    const joined = new Uint8Array(total)
    let offset = 0
    for (const part of buffer.parts) {
      joined.set(part, offset)
      offset += part.length
    }


    // The rest of the app treats a presentation as a data URI (pdfPresentation.src drives the
    // whole presentation UI), so rebuild one rather than changing every consumer.
    let dataUri
    try {
      dataUri = await this.bytesToDataUri(joined, buffer.mime)
    } catch (err) {
      console.error('[WebRTC] presentation encode failed:', err)
      this.EventBus.$emit('onPresentationLoading', { active: false, progress: 0 })
      return
    }
    this.EventBus.$emit('onPresentationLoading', { active: false, progress: 100 })
    this.emitPresentationReceived(buffer.envelope, dataUri, buffer.sender)
  }

  // Native conversions: far faster than per-byte atob/btoa loops over tens of megabytes.
  async dataUriToBytes (dataUri) {
    const response = await fetch(dataUri)
    return new Uint8Array(await response.arrayBuffer())
  }

  bytesToDataUri (bytes, mimeType) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(new Blob([bytes], { type: mimeType || 'application/pdf' }))
    })
  }

  emitPresentationReceived (data, content, sender) {
    // Presentations arrive as a data: URI, or null on a page change. Anything else would
    // make pdf.js fetch a URL chosen by the sender.
    if (content !== null && typeof content !== 'undefined' && !(typeof content === 'string' && /^data:/i.test(content))) {
      console.warn('[WebRTC] ignored presentation with non-data content')
      return
    }
    this.EventBus.$emit('onPresentationScreenReceived', {
      'sender': sender,
      'pdf': content,
      'current_page': data.current_page,
      'total_pages': data.total_pages,
      'signature': data.signature,
      'is_retransmit': data.is_retransmit,
      'host_stream_id': data.host_stream_id,
      'offsetX': data.offsetX,
      'offsetY': data.offsetY,
      'scale': data.scale,
      'drawings': data.drawings,
      'width': data.width,
      'presentation_type': data.presentation_type
    })
  }

  stopPresentation () {
    // Abandon any transfer in flight; the receivers are about to drop their buffers.
    this.presentationSendId = null
    this.presentationSendKey = null

    if (this.localStream) {
      this.localStream.sendData({
        'sender': typeof this.localStream.getAttributes() !== 'undefined' && typeof this.localStream.getAttributes().real_name !== 'undefined' ? this.localStream.getAttributes().real_name : 'Agent',
        'type': 'end_presentation'
      })
    }
  }

  triggerIdentification () {
    console.log('trigger ID process')
    this.EventBus.$emit('onIdentificationTriggered')
  }

  generateIdentificationProcess () {
    console.log('identification process generated')
    if (this.localStream) {
      let sender = this.getRealNameFromStream(this.localStream)
      this.localStream.sendData({
        'sender': sender,
        'type': 'digid_process'
      })
      this.EventBus.$emit('ongenerateIdentificationProcess', {
        'sender': sender,
        'type': 'digid_process'
      })
    }
  }

  generateRandomString (length) {
    const characters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let randomString = ''
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length)
      randomString += characters[randomIndex]
    }
    return randomString
  }

  async startIdentificationProcess (currentUrl) {
    let meeting = store.getters.getSignicatConfig
    const { signicatClientID, signicatClientURL } = meeting
    if (!signicatClientID || !signicatClientURL) {
      return
    }
    await Signicat.redirectToAuthorizationEndpoint(currentUrl, signicatClientID, signicatClientURL)
  }

  async exchangeCodeForToken (currentUrl) {
    return Signicat.exchangeCodeForToken(currentUrl)
  }

  toggleDeviceSettingsPane () {
    this.showDeviceSettingsPane = !this.showDeviceSettingsPane
    this.EventBus.$emit('onToggleDeviceSettingsPane', {
      'show': this.showDeviceSettingsPane
    })
  }

  setUploadBumicomRecording (shouldUploadRecording, axiosInstance) {
    this.uploadBumicomRecording = shouldUploadRecording
    this.axiosInstance = axiosInstance
  }

  startMeetingCounter (roomId) {
    this.connection.emit('start-meeting-counter', { roomId })
  }
  broadcastMuteUserAudio (data) {
    this.connection.emit('mute user', { ...data, userId: data.userId, roomId: this.roomId, description: `Remote Mute Audio ${data.userId}`, respondEventName: 'onMuteUserAudio' })
  }

  emitMoveParticipantToWaitingRoom (roomId, participantId) {
    if (!this.connection) return
    this.connection.emit('move participant to waiting room', { roomId, participantId })
  }

  emitReturnParticipantFromWaitingRoom (roomId, participantId) {
    if (!this.connection) return
    this.connection.emit('return participant from waiting room', { roomId, participantId })
  }

  emitParticipantWaitingRoomReturnComplete (roomId) {
    if (!this.connection) return
    this.connection.emit('participant-waiting-room-return-complete', { roomId })
  }

  _notifyMuteStatus (data) {
    if (!this.localStream || this.localStream === null || !this.connection) { return }
    const userId = this.getUserIdFromStream(this.localStream)
    this.connection.emit('muteStatusChanged', {
      ...data,
      roomId: this.roomId,
      userId
    })
    window.LoggerAPI.addLog('client', new Date(), 'log', 'BROADCAST_MUTE_STATUS', JSON.stringify({
      'sender': this.getRealNameFromStream(this.localStream),
      'type': 'muteStatus',
      'content': { ...data, userId: this.getUserIdFromStream(this.localStream) }
    }))
  }

  notifyMuteStatusIfChanged (data) {
    if (!this.localStream || this.localStream === null) {
      return
    }
    const userId = this.getUserIdFromStream(this.localStream)
    const prev = this.muteStatusMap[userId]

    if (prev && prev.audioMuted === data.audioMuted && prev.videoMuted === data.videoMuted && !data.force) {
      return
    }

    // Cache latest state
    const userIdentifier = store.getters.getUser?.name || 'visitor'
    this.muteStatusMap[userId] = { ...data, isAgent: userIdentifier !== 'visitor', userId }

    this._debouncedNotify(data)
  }

  // Video Zoom Methods

  /**
   * Initialize video zoom with session-based isolation
   * @param {HTMLVideoElement} videoElement - The video element to zoom
   * @param {HTMLElement} containerElement - The container element for pinch gestures
   */
  initializeVideoZoom (videoElement, containerElement) {
    if (!videoElement || !this.localStream) {
      return false
    }

    try {
      // Destroy any existing zoom instance
      if (this.videoZoom) {
        this.destroyVideoZoom()
      }

      // Create video zoom instance
      this.videoZoom = new VideoZoom()
      this.videoZoom.initialize(videoElement, this.localStream.stream)

      // Set up zoom change callback
      this.videoZoom.onZoomChange = (zoomLevel, isZoomed) => {
        this.currentZoomLevel = zoomLevel
        this.isZoomEnabled = isZoomed

        // Emit zoom change event
        this.EventBus.$emit('onVideoZoomChanged', { zoomLevel, isZoomed })

        // Update the stream being sent to remote participants with delay
        setTimeout(() => {
          this.updateZoomedStream()
        }, 200)
      }

      // Set up device not supported callback
      this.videoZoom.onDeviceNotSupported = () => {
        this.EventBus.$emit('onCameraZoomNotSupported')
      }

      // Set up fallback to digital zoom callback
      this.videoZoom.onFallbackToDigital = () => {
        this.EventBus.$emit('onFallbackToDigitalZoom')
      }

      // Mark zoom as active for this call so camera switches re-init it.
      this.zoomFeatureActive = true

      // Initialize pinch-to-zoom if container is provided
      if (containerElement && 'ontouchstart' in window) {
        this.pinchZoomHandler = new PinchZoomHandler(containerElement, (scale, centerX, centerY) => {
          if (this.videoZoom) {
            this.videoZoom.handlePinchZoom(scale, centerX, centerY)
          }
        }, () => {
          // Handle pinch gesture end
          if (this.videoZoom) {
            this.videoZoom.resetPinchZoomState()
          }
        }, () => {
          // Get current zoom level
          if (this.videoZoom) {
            return this.videoZoom.getCurrentZoomLevel()
          }
          return 1.0
        })
      }

      return true
    } catch (error) {
      return false
    }
  }

  /**
   * Check if zoom should be hidden (front camera on mobile)
   * @returns {boolean} True if zoom should be hidden
   */
  shouldHideZoomForMobile () {
    // Check if we're on a mobile device (the UA regex misses iPad's desktop UA)
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || this.isIOSDevice()

    if (!isMobile) {
      return false
    }

    // Check if we're using front camera (facingMode === 'user')
    if (this.facingMode === 'user') {
      return true
    }

    // Also check the current stream's facing mode as backup
    if (this.localStream && this.localStream.stream) {
      const videoTracks = this.localStream.stream.getVideoTracks()
      if (videoTracks.length > 0) {
        try {
          const settings = videoTracks[0].getSettings()
          if (settings.facingMode === 'user') {
            return true
          }
        } catch (error) {
          console.warn('Error getting video track settings:', error)
        }
      }
    }

    return false
  }

  /**
   * Reset and re-establish zoom when the camera is switched.
   *
   * A switch creates a new stream + video element and removes the old element/canvas
   * from the DOM, but the outgoing sender may still hold the old zoom canvas track
   * (so the remote stays stuck on a frozen, zoomed image). We therefore: tear down the
   * stale zoom, force the outgoing track back to the new camera's RAW track, then
   * re-init zoom fresh at 1x — but only for a zoomable (non front-cam-mobile) camera.
   */
  async updateZoomForNewCamera () {
    // Gate on the session flag, not `videoZoom`: a previous switch may have torn zoom
    // down (e.g. switching to the front camera), and we still need to handle the switch.
    if (!this.zoomFeatureActive || !this.localStream || !this.localStream.stream) {
      return
    }

    if (this.mediaEngine === 'livekit') {
      // Await the swap back to the raw track before destroy stops the zoom canvas.
      const currentRawTrack = this.localStream.stream.getVideoTracks()[0]
      if (currentRawTrack) {
        if (this.livekitAdapter && this.livekitAdapter.localVideoTrack) {
          await this.livekitAdapter.replaceVideoTrack(currentRawTrack)
        } else {
          this.replaceTracksInPeerConnection(currentRawTrack, null)
        }
      }
    }

    // Stop the zoom bound to the previous camera (stops its canvas processing/stream
    // and resets the old, now-detached video element).
    this.destroyVideoZoom()

    setTimeout(() => {
      // Force the outgoing stream back to the new camera's raw video track so the remote
      // side isn't left on the stale zoomed canvas. Audio is left untouched.
      const rawVideoTrack = this.localStream?.stream?.getVideoTracks?.()[0]
      if (rawVideoTrack) {
        this.replaceTracksInPeerConnection(rawVideoTrack, null)
      }

      // Re-init zoom fresh (at 1x) on the new video element, unless hidden (front cam mobile).
      const localVideoElement = document.querySelector('#local') ||
        document.querySelector(`#${CSS.escape(this.localStream?.getID())}`)
      if (localVideoElement && localVideoElement.srcObject && !this.shouldHideZoomForMobile()) {
        this.initializeVideoZoom(localVideoElement, localVideoElement.parentNode)
      }

      // Resync the UI zoom controls/level for the new camera.
      this.EventBus.$emit('onCameraSwitched')
    }, 500)
  }

  /**
   * Update the stream being sent to remote participants with zoomed video.
   *
   * For digital (canvas) zoom the agent only sees the zoom if the canvas
   * captureStream track is actually swapped into the publish peer connection.
   * That swap can intermittently fail when this runs before the sender exists
   * or before the canvas has started rendering (captureStream of an undrawn
   * canvas emits a frozen/black frame). We therefore retry a bounded number of
   * times until the swap lands, and make sure the canvas processing loop is
   * running first so the agent receives live zoomed frames rather than a stale
   * one. The participant's local preview is unaffected (it uses a local overlay).
   *
   * @param {number} attempt - Internal retry counter.
   */
  updateZoomedStream (attempt = 0) {
    // `room` is Erizo-only; on LiveKit the equivalent is having a published track.
    const canPublish = this.mediaEngine === 'livekit'
      ? !!(this.livekitAdapter && this.livekitAdapter.localVideoTrack)
      : !!this.room
    if (!this.videoZoom || !this.localStream || !canPublish) {
      return
    }

    const MAX_ATTEMPTS = 10
    const RETRY_DELAY = 200

    const retry = () => {
      if (attempt < MAX_ATTEMPTS) {
        clearTimeout(this.updateZoomedStreamTimeout)
        this.updateZoomedStreamTimeout = setTimeout(() => {
          this.updateZoomedStream(attempt + 1)
        }, RETRY_DELAY)
      } else {
        console.warn('[WebRTC] Gave up updating zoomed stream for remote after retries')
      }
    }

    try {
      // For digital zoom the canvas must be actively rendering, otherwise the
      // captureStream track sent to the agent is frozen/black.
      if (this.videoZoom.zoomMode === 'digital' && !this.videoZoom.isProcessing) {
        this.videoZoom.startProcessing()
      }

      if (this.mediaEngine === 'livekit' && this.videoZoom.zoomMode === 'digital' && !this.videoZoom.isZoomed) {
        const rawVideoTrack = this.localStream.stream.getVideoTracks()[0]
        if (rawVideoTrack && rawVideoTrack.readyState === 'live') {
          this.replaceTracksInPeerConnection(rawVideoTrack, null)
          return
        }
      }

      const zoomedStream = this.videoZoom.getZoomedStream()
      if (!zoomedStream) {
        console.warn('No zoomed stream available, retrying')
        retry()
        return
      }

      // Get the video track from the zoomed stream
      const videoTrack = zoomedStream.getVideoTracks()[0]
      const audioTrack = this.localStream.stream.getAudioTracks()[0]

      // Audio track is optional - if not available, we'll only replace video
      if (!audioTrack) {
        console.warn('No audio track in local stream, will only replace video track')
      }

      // Validate tracks before attempting replacement
      if (!videoTrack || videoTrack.readyState !== 'live') {
        console.warn('Zoomed video track not ready, retrying stream replacement')
        retry()
        return
      }

      const senderFound = this.replaceTracksInPeerConnection(videoTrack, audioTrack)
      if (!senderFound) {
        // Peer connection / sender not ready yet - try again shortly so the
        // agent reliably ends up on the zoomed track.
        console.warn('Peer connection sender not ready for zoomed track, retrying')
        retry()
      }
    } catch (error) {
      console.error('Failed to update zoomed stream:', error)
      // Don't break the existing stream on error
    }
  }

  /**
   * Set zoom level with safe stream handling - session-based isolation
   * @param {number} level - Zoom level (1.0 = no zoom, 2.0 = 2x zoom, etc.)
   */
  setZoomLevel (level) {
    if (this.videoZoom) {
      // Clear any pending zoom operations
      clearTimeout(this.zoomTimeout)

      // Use a timeout to prevent rapid zoom changes that might break the stream
      this.zoomTimeout = setTimeout(() => {
        this.videoZoom.setZoomLevel(level)
      }, 100)
    }
  }

  /**
   * Safe zoom in with delay - session-based isolation
   */
  zoomIn () {
    if (this.videoZoom) {
      // Clear any pending zoom operations
      clearTimeout(this.zoomTimeout)

      this.zoomTimeout = setTimeout(() => {
        this.videoZoom.zoomIn()
      }, 100)
    }
  }

  /**
   * Safe zoom out with delay - session-based isolation
   */
  zoomOut () {
    if (this.videoZoom) {
      // Clear any pending zoom operations
      clearTimeout(this.zoomTimeout)

      this.zoomTimeout = setTimeout(() => {
        this.videoZoom.zoomOut()
      }, 100)
    }
  }

  /**
   * Clean up zoom functionality
   */
  destroyVideoZoom () {
    // Stop any pending remote zoomed-stream retry so it can't re-arm after teardown.
    clearTimeout(this.updateZoomedStreamTimeout)

    if (this.videoZoom) {
      this.videoZoom.destroy()
      this.videoZoom = null
    }

    if (this.pinchZoomHandler) {
      this.pinchZoomHandler.destroy()
      this.pinchZoomHandler = null
    }

    this.isZoomEnabled = false
    this.currentZoomLevel = 1.0

    console.log('Video zoom unmounted')
  }

  /**
   * Check if camera zoom is supported
   * @returns {boolean} True if camera zoom is supported
   */
  isCameraZoomSupported () {
    if (this.videoZoom) {
      return this.videoZoom.isCameraZoomSupported()
    }
    return false
  }

  /**
   * Get zoom status information
   * @returns {Object} Zoom status information
   */
  getZoomStatus () {
    if (this.videoZoom) {
      return this.videoZoom.getZoomStatus()
    }
    return {
      mode: 'camera',
      level: 1.0,
      isZoomed: false,
      isWorking: false,
      supportsCameraZoom: false,
      isProcessing: false
    }
  }

  /**
   * Check if zoom is working properly
   * @returns {boolean} True if zoom is working
   */
  isZoomWorking () {
    if (this.videoZoom) {
      return this.videoZoom.isZoomWorking()
    }
    return false
  }

  /**
   * Get zoom session ID for debugging
   * @returns {string} Zoom session ID
   */
  getZoomSessionId () {
    if (this.videoZoom) {
      return this.videoZoom.sessionId
    }
    return null
  }

  /**
   * Check if zoom operation is allowed for current session
   * @returns {boolean} True if operation is allowed
   */
  isZoomOperationAllowed () {
    if (!this.videoZoom) {
      return false
    }
    return this.videoZoom.isOperationAllowed()
  }
}

export { Webrtc }
